/**
 * Workspace store — wraps the runtime-agnostic WorkspaceStore from @ae/domain
 * and exposes a Zustand-friendly subscribable API for React.
 *
 * Mutations are optimistic: the local store updates immediately, and the API
 * call is debounced. On API error the change is rolled back via the undo stack.
 *
 * M2.3 ships create + move + connect (relation). PATCH attribute updates and
 * delete come in M2.4.
 */
import {
  type BilingualText,
  type Concept,
  computeHiddenConcepts,
  createConcept,
  createRelation,
  DIAGRAM_BOX_STYLES_KEY,
  type Diagram,
  fillMissingRequiredAttributes,
  HEAT_MAP_CONFIG_KEY,
  layerCollapseMembers,
  normalizeViewState,
  OVERLAY_RULES_KEY,
  type Relation,
  ROADMAP_MATRIX_CONFIG_KEY,
  removeConceptCommand,
  removeRelationCommand,
  type SimplifyOptions,
  type SimplifyRelationRef,
  setResolver,
  UndoStack,
  upsertConceptCommand,
  upsertDiagramCommand,
  upsertRelationCommand,
  type ViewElementState,
  type ViewState,
  WorkspaceStore,
} from "@ae/domain";
import i18n from "i18next";
import { create } from "zustand";
import {
  ApiError,
  api,
  type CreateCustomViewKindBody,
  type CustomViewKindDTO,
  type PatchCustomViewKindBody,
} from "../api/client";
import { conceptHasName, findDuplicateConcepts } from "../editor/canvas/conceptIdentity";
import { writeBoxStyle } from "../editor/canvas/diagramBoxStyles";
import { writeOverlayRules } from "../editor/canvas/overlayConfig";
import { findDuplicateRelations, isSameRelationIdentity } from "../editor/canvas/relationIdentity";
import { layoutSimplifiedViewState } from "../editor/canvas/simplifyLayout";
import { heeftStandaardnaam } from "../editor/canvas/standaardNaam";
import { getRegistry } from "../registry";
import { useSession } from "./session";
import { useUi } from "./ui";
import { findEntryKeyForEntity, vsGet, vsPatchEntry, vsPatchEntryWithRef } from "./viewStateAccess";
import { bepaalWatErHeenMoet, classificeerWeigering } from "./viewStateRejection";

const PERSIST_DEBOUNCE_MS = 300;

/** Pseudo-type voor promote-to-abstraction-super-nodes (view-only). */
const PROMOTE_ABSTRACTION_TYPE_ID = "archimate.ApplicationCollaboration";

/**
 * Eén te promoveren cluster: detail-elementen die samen tot één abstractie-
 * blok worden gevouwen, met een optioneel custom label (default = het
 * service-anker via de UI). View-only — géén echt concept.
 */
export interface PromoteCluster {
  memberConceptIds: string[];
  label?: BilingualText;
}

/** Client-opties voor `createSimplifiedCopy` = domain-transforms + view-only promote. */
export type SimplifyClientOptions = SimplifyOptions & {
  promoteClusters?: PromoteCluster[];
};

interface PendingPatch {
  diagramId: string;
  timer: ReturnType<typeof setTimeout> | null;
  /** ViewState zoals 'ie was vóór onze eigen wijziging. Levert bij het
   *  flushen de lijst met sleutels die wij hebben weggehaald. */
  baseline: Record<string, unknown>;
}

/**
 * De viewState zoals de server 'm bij ons weten heeft, per diagram.
 *
 * **Waarom dit bestaat.** Een patch stuurt alleen wat er veranderd is, gemeten
 * tegen een uitgangspunt. Dat uitgangspunt werd tot 2026-09-01 vastgelegd
 * binnen `schedulePersistDiagram` — en die wordt door élke actie aangeroepen
 * *nadat* 'ie de store al heeft bijgewerkt. Het uitgangspunt was dus de stand
 * ná de wijziging, waardoor de wijziging zichzelf wegvergeleek en er niets
 * werd meegestuurd.
 *
 * Zichtbaar werd dat alleen bij een bewerking van een bestaand voorkomen: een
 * nieuw geplaatst object heeft nog geen sleutel in het uitgangspunt en ging
 * dus wél mee. Vandaar het beeld dat tekenen werkt maar verplaatsen,
 * verschalen, nesten en opmaak "niet bewaard blijven" — ze bereikten de server
 * nooit.
 *
 * Deze kaart houdt vast wat de server heeft: gevuld bij het ophalen en na elke
 * geslaagde schrijfactie. Het verschil daarmee is per definitie wat er nog
 * heen moet.
 */
const serverViewStates = new Map<string, Record<string, ViewElementState>>();

const pendingDiagramPatches = new Map<string, PendingPatch>();
const pendingConceptPatches = new Map<string, ReturnType<typeof setTimeout>>();
/**
 * Per-concept accumulator of all field-level changes within a single edit
 * session. Built up across rapid keystrokes so the eventual debounced
 * `api.patchConcept` call carries every changed field — earlier versions
 * only sent the LAST patch object, which silently dropped intermediate
 * field changes when the user tabbed across fields mid-session.
 */
const conceptPatchAccumulators = new Map<string, ConceptPatch>();
/**
 * Snapshot of a concept at the start of an edit session. Cleared when the
 * debounce flushes; used to build a single user-undo entry covering the
 * whole session.
 */
const conceptPatchBaselines = new Map<string, Concept>();

export interface ConceptPatch {
  name?: Record<string, string>;
  description?: Record<string, string>;
  attributes?: Record<string, unknown>;
  tags?: string[];
  parent_id?: string | null;
  type_id?: string;
  stereotypes?: string[];
  /** Per-view-resolver alt-labels (M8 §5). Sleutel = resolver-id (`sharp`,
   *  `c4`, `bmc`), waarde = volledige `BilingualText` voor die resolver.
   *  Meegeven vervangt de resolver-entry als geheel (component stuurt de
   *  gemergede bag mee), zodat een NL-edit de EN-waarde niet wist. */
  alt_labels?: Record<string, Record<string, string>>;
}

export interface RelationPatch {
  type_id?: string;
  source_concept_id?: string;
  target_concept_id?: string;
  label?: Record<string, string>;
  attributes?: Record<string, unknown>;
  /** Full replacement (analog to ConceptPatch.stereotypes) — meegeven
   *  betekent "vervang de hele lijst". Server-side wordt gevalideerd
   *  dat elk id bestaat én op het relatie-type mag landen. */
  stereotypes?: string[];
  /** Per-view-resolver alt-labels (M8 §5). Zelfde semantiek als
   *  ConceptPatch.alt_labels — resolver-entry wordt als geheel vervangen. */
  alt_labels?: Record<string, Record<string, string>>;
  /** Waarom deze wijziging gebeurde; belandt in de versiehistorie. Gaat als
   *  enige veld niet over de inhoud van de relatie — `applyRelationPatch`
   *  laat 'm dan ook links liggen. */
  change_reason?: string;
}

const pendingRelationPatches = new Map<string, ReturnType<typeof setTimeout>>();
const relationPatchAccumulators = new Map<string, RelationPatch>();
const relationPatchBaselines = new Map<string, Relation>();

/**
 * Single user-level undo entry. Both `undo` and `redo` re-issue the same
 * workspace-store actions the user originally invoked, which means the API
 * stays in sync — no special server-side undo handling needed.
 */
export interface UserUndoEntry {
  label: string;
  undo: () => void | Promise<void>;
  redo: () => void | Promise<void>;
  /**
   * De viewState-sleutels die deze stap aanraakt. Nodig om een geweigerd
   * voorkomen eruit te kunnen halen: schreef de server dat vakje niet, dan
   * hoort Cmd+Z het ook niet terug te zetten — anders botst 'ie opnieuw en
   * krijgt de gebruiker dezelfde melding nog eens.
   */
  viewStateKeys?: string[];
  /** Bij een samengestelde stap (een versleping van meerdere objecten) de
   *  losse stappen, zodat er eentje uit te halen is. */
  children?: UserUndoEntry[];
}

const USER_UNDO_LIMIT = 100;

interface InternalState {
  store: WorkspaceStore;
  undo: UndoStack;
  /** User-facing Cmd+Z / Ctrl+Z stack. Distinct from `undo` above, which
   * is only used for optimistic-rollback on API errors. */
  userUndoStack: UserUndoEntry[];
  userRedoStack: UserUndoEntry[];
  /** Bumped na een user-undo/redo zodat de Canvas React-Flow's node-internals
   *  forceert te her-meten — een store-gedreven positie-revert her-tekent de
   *  edge-aanhechting anders pas bij de volgende render (endpoint-lag). */
  canvasGeometryNonce: number;
  /** Bumped on every store change so React re-renders consumers. */
  revision: number;
  /** Currently active diagram id for canvas viewing. */
  activeDiagramId: string | null;
  /**
   * Diagram ids that should appear as a tab above the canvas. Closing a
   * tab removes the id from this list; the diagram itself stays in the
   * workspace store and remains reachable via the Diagrams panel.
   * Persisted to localStorage per workspace so the open-tab set survives
   * a browser refresh.
   */
  openDiagramIds: string[];
  /** Last error from a server mutation (toast-worthy). */
  lastError: string | null;
  /**
   * Workspace-scoped custom diagram type definitions, mirrored as DTOs
   * so the Settings UI can render them with full metadata (creator,
   * centrally_managed flag, timestamps). The runtime registry holds
   * the same data in `ViewKind` shape for the rest of the app.
   */
  customViewKinds: CustomViewKindDTO[];
  /**
   * Workspace-scoped concept subtypes (Specialization). Mirrored as
   * DTOs voor de Settings UI; tegelijk geregistreerd op de runtime
   * registry zodat TypePalette + AttributePanel ze als eersteklas
   * types behandelen via mergeExtension.
   */
}

interface Actions {
  /** Load all concepts, relations, diagrams for a (workspace, branch) into the store. */
  hydrate: (workspaceId: string, branchId: string) => Promise<void>;
  setActiveDiagram: (diagramId: string | null) => void;
  ensureDefaultDiagram: (workspaceId: string, branchId: string) => Promise<Diagram>;

  /** Create a new diagram, store it, switch to it, and return it. */
  createDiagram: (
    workspaceId: string,
    branchId: string,
    name: BilingualText,
    viewKind: string,
  ) => Promise<Diagram>;

  /**
   * Genereer een vereenvoudigde KOPIE van een diagram (deterministische
   * reductie-transforms). Kopieert alleen de view (diagram + viewState); de
   * gedeelde concepten/relaties blijven ongedeeld. De gekozen transforms
   * (`computeHiddenConcepts`) zetten `hidden` op de kopie z'n viewState; het
   * origineel blijft intact. Opent de kopie. Returnt id + aantal verborgen.
   */
  createSimplifiedCopy: (
    workspaceId: string,
    branchId: string,
    sourceDiagramId: string,
    options: SimplifyClientOptions,
    autoLayout: boolean,
  ) => Promise<{ id: string; hiddenCount: number } | null>;

  /**
   * Maak een nieuw diagram aan vanuit een DiagramTemplate. Creëert
   * eerst het diagram (met de template's defaultDiagramName of een
   * meegegeven nameOverride), daarna voor elk element in de template
   * een concept + viewState-entry (position/size/nodeStyle). De
   * gebruiker kan na creatie alles bewerken zoals een normaal diagram.
   */
  createDiagramFromTemplate: (
    workspaceId: string,
    branchId: string,
    template: import("../editor/templates/riskSecurityAssessment").DiagramTemplate,
    nameOverride?: BilingualText,
  ) => Promise<Diagram>;

  /** Update a diagram's bilingual name. Optimistic local update + PATCH. */
  renameDiagram: (
    workspaceId: string,
    branchId: string,
    diagramId: string,
    name: BilingualText,
  ) => Promise<void>;

  /**
   * Update a diagram's bilingual description. Empty / all-blank
   * description maps to "clear field" on the server. Used by the
   * diagram-properties dialog and is included as a `title` statement
   * in the PlantUML emit when non-empty.
   */
  setDiagramDescription: (
    workspaceId: string,
    branchId: string,
    diagramId: string,
    description: BilingualText,
  ) => Promise<void>;

  /** Change the diagram's view-kind (which viewpoint it represents). */
  changeDiagramViewKind: (
    workspaceId: string,
    branchId: string,
    diagramId: string,
    viewKind: string,
  ) => Promise<void>;

  /**
   * Schrijf de heat-map-configuratie van een diagram (analytics-pack).
   * **Deprecated v1** — bewaard zolang v1-renderer in actieve code staat.
   * Schrijft naar viewState[HEAT_MAP_CONFIG_KEY]; v2 gebruikt
   * `setDiagramAttribute` op `Diagram.attributes`.
   */
  setHeatMapConfig: (
    workspaceId: string,
    branchId: string,
    diagramId: string,
    config: import("@ae/domain").HeatMapConfig,
  ) => void;

  /**
   * Schrijf de deterministische kleur/dim-overlay-regelset van een diagram
   * (conditional formatting). Zelfde sentinel-patroon als `setHeatMapConfig`:
   * schrijft naar `viewState[OVERLAY_RULES_KEY]` via de `writeOverlayRules`-
   * helper en schedule een debounced PATCH. Niet-destructief; overlay uit
   * (`enabled: false`) of lege regelset → concepten renderen standaardkleur.
   */
  setOverlayRules: (
    workspaceId: string,
    branchId: string,
    diagramId: string,
    ruleset: import("@ae/domain").OverlayRuleSet,
  ) => void;

  /**
   * Schrijf de Roadmap-matrix-configuratie van een diagram (roadmap-pack,
   * view-kind `roadmap.roadmap-matrix`). Zelfde sentinel-patroon als
   * `setHeatMapConfig`: schrijft naar `viewState[ROADMAP_MATRIX_CONFIG_KEY]`
   * en schedule een debounced PATCH.
   */
  setRoadmapMatrixConfig: (
    workspaceId: string,
    branchId: string,
    diagramId: string,
    config: import("@ae/domain").RoadmapMatrixConfig,
  ) => void;

  /** Merge een per-box opmaak-patch (font/kleur/uitlijning) in de viewState-
   *  stash van een custom-render-diagram (Roadmap + matrix). */
  setDiagramBoxStyle: (
    workspaceId: string,
    branchId: string,
    diagramId: string,
    boxKey: string,
    patch: import("@ae/domain").BoxStyle,
  ) => void;

  /**
   * Schrijf één diagram-niveau attribuut (M6.5+, gebruikt door
   * view-kinds met eigen `attributes`-schema's — eerste use-case
   * analytics.heat-map). Update `Diagram.attributes[name]` optimistisch
   * in de lokale store en schedule een debounced PATCH naar de server.
   *
   * Bij value === null of undefined: het attribuut wordt uit de bag
   * verwijderd (clear). Bij andere waarden: geschreven zoals doorgegeven.
   */
  setDiagramAttribute: (
    workspaceId: string,
    branchId: string,
    diagramId: string,
    name: string,
    value: unknown,
  ) => void;

  // ---- Custom view-kinds ------------------------------------------------
  createCustomViewKind: (
    workspaceId: string,
    body: CreateCustomViewKindBody,
  ) => Promise<CustomViewKindDTO>;
  updateCustomViewKind: (
    workspaceId: string,
    id: string,
    body: PatchCustomViewKindBody,
  ) => Promise<CustomViewKindDTO>;
  deleteCustomViewKind: (workspaceId: string, id: string) => Promise<void>;

  // ---- Concept subtypes (Specialization) -------------------------------

  /**
   * §12.3 — maak een "kaal" businesslaag-object (plain, geen «Concept») met
   * naam + definitie overgenomen van een bron-Concept, voor de facet-referentie-
   * picker (greenfield: het facet-element bestaat nog niet). Upsert in de store
   * en geeft het aangemaakte concept terug zodat de caller z'n id kan koppelen.
   */
  createFacetConcept: (
    workspaceId: string,
    branchId: string,
    input: { typeId: string; name: BilingualText; description?: BilingualText },
  ) => Promise<Concept>;

  /**
   * Hard-delete a diagram. The active selection moves to whichever diagram
   * remains (or null if it was the last). Refuses if there's only one
   * diagram left.
   */
  deleteDiagram: (workspaceId: string, branchId: string, diagramId: string) => Promise<void>;

  /**
   * Open a diagram as a tab and make it active. Idempotent — if the
   * diagram is already open, just sets it as active.
   */
  openDiagram: (workspaceId: string, diagramId: string) => void;
  /**
   * Close a tab without deleting the diagram. The diagram stays in the
   * workspace and can be re-opened via the Diagrams panel. If the closed
   * tab was active, switches to the next remaining open tab (or null
   * when none remain).
   */
  closeDiagramTab: (workspaceId: string, diagramId: string) => void;

  addConceptOnCanvas: (
    workspaceId: string,
    branchId: string,
    typeId: string,
    name: BilingualText,
    position: { x: number; y: number },
    /**
     * Optional attribute defaults applied at create-time. Used by the
     * virtual palette groups (e.g. C4) so dropping a "Software System"
     * entry creates an ApplicationComponent with `c4_level=system`
     * already set. The server-side validator runs on these too.
     */
    attributes?: Record<string, unknown>,
    /**
     * Optional stereotype-ids applied at create-time (M5+). Used by
     * palette-groups als Sharp's "DATA MODELING" zodat een drop van
     * "Concept" direct een BusinessObject + `["sharp.Concept"]` als
     * stereotype-array oplevert. Server-side validator (per
     * stereotype-required-attribute) runt hier ook op.
     */
    stereotypes?: string[],
  ) => Promise<Concept>;

  moveConceptOnCanvas: (
    workspaceId: string,
    branchId: string,
    conceptId: string,
    position: { x: number; y: number },
  ) => void;

  /**
   * Zet de `tableOrder` op een viewState-entry. Gebruikt door GridPane bij
   * "Regel invoegen" zodat de nieuwe rij in de tabel direct ná de
   * cursor-rij verschijnt — onafhankelijk van de natuurlijke listConcepts/
   * listRelations-volgorde. Sluit aan op het bestaande
   * schedulePersistDiagram-debounce-pad, dus geen extra API-round-trip
   * bovenop wat addConceptOnCanvas/connectConcepts al doet.
   */
  setViewElementTableOrder: (
    workspaceId: string,
    branchId: string,
    elementKey: string,
    tableOrder: number,
  ) => void;

  /**
   * Persist explicit width/height on the diagram view-state for a node.
   * Set by the React Flow `<NodeResizer />` handles; lets the user grow
   * a vakje to act as a container for nested children.
   */
  resizeNodeOnCanvas: (
    workspaceId: string,
    branchId: string,
    nodeId: string,
    size: { width: number; height: number },
  ) => void;

  /**
   * Drop an EXISTING concept (already in the workspace) onto the active
   * diagram. No new concept is created — only the diagram's view_state
   * gets a position entry, so the concept appears on the canvas. Idempotent
   * if the concept is already on the diagram (just updates the position).
   */
  placeExistingConceptOnDiagram: (
    workspaceId: string,
    branchId: string,
    conceptId: string,
    position: { x: number; y: number },
  ) => string | null;

  /**
   * Bulk-variant van `placeExistingConceptOnDiagram` (multi-select drop):
   * plaatst alle concepten in ÉÉN gecombineerde undo-entry zodat Cmd+Z de
   * hele drop in één keer terugdraait (i.p.v. één entry per object).
   * Returnt de aangemaakte instance-ids in dezelfde volgorde.
   */
  placeExistingConceptsOnDiagram: (
    workspaceId: string,
    branchId: string,
    items: { conceptId: string; position: { x: number; y: number } }[],
  ) => string[];

  /**
   * Canvas-mode placement: zet een instance in een vast template-slot in
   * plaats van op een x/y-positie. Schrijft viewState `{ slotId,
   * orderInSlot, conceptRef? }` en stript `position`/`width`/`height`
   * (per ViewElementState-spec mutually exclusive met slotId).
   *
   * Wordt aangeroepen door de canvas-grid-renderer (BMC, later Lean
   * Canvas / SWOT / etc.) bij drop-op-slot of stack-reorder. Als de
   * instance nog niet op dit diagram staat krijgt 'ie conceptId als
   * instanceId; al-bestaande instances worden ge-update.
   */
  placeConceptInSlot: (
    workspaceId: string,
    branchId: string,
    conceptId: string,
    slotId: string,
    orderInSlot: number,
  ) => string | null;

  /**
   * Plaatst een bestaande relatie op het actieve diagram door alleen een
   * (lege) viewState-entry voor `relationId` aan te leggen — geen nieuwe
   * relatie aanmaken. Zo komt de pijl tevoorschijn op deze view zónder
   * dat er een tweede relatie in de workspace wordt geboren.
   *
   * Returnt:
   * - `"placed"` als de visibility-marker net is gezet,
   * - `"already"` als de relatie hier al stond,
   * - `"missing-endpoint"` als bron- of doel-concept niet op dit
   *   diagram aanwezig is (dan stelt de UI zelf een toast in).
   */
  placeExistingRelationOnDiagram: (
    workspaceId: string,
    branchId: string,
    relationId: string,
    /** Optionele per-instance-pinning (Feature A — drop-target instance-
     *  aware). Wanneer gezet, krijgt de relatie viewState met expliciete
     *  sourceInstanceId / targetInstanceId zodat 't endpoint pakt op de
     *  gewenste instance i.p.v. de canonical-eerste. `entryKey` laat de
     *  caller de nieuwe viewState-key vooraf bepalen (i.p.v. een interne
     *  `crypto.randomUUID()`) — nodig wanneer de caller na afloop nog
     *  extra viewState-velden op precies díe entry wil zetten (paste). */
    options?: { sourceInstanceId?: string; targetInstanceId?: string; entryKey?: string },
  ) => "placed" | "already" | "missing-endpoint";

  /**
   * Visually nest one canvas instance inside another. Sets
   * `viewState[childInstanceId].containerId = parentInstanceId` and
   * stores the child's position relative to the parent. Hides any
   * previously-visible relations between the two underlying concepts on
   * this diagram (per the user-defined nesting rules).
   *
   * Returns the count of relations that existed and were hidden so the
   * caller can decide whether to prompt the user for a new relation
   * (no-existing case).
   */
  nestOnDiagram: (
    workspaceId: string,
    branchId: string,
    childInstanceId: string,
    parentInstanceId: string,
    relativePosition: { x: number; y: number },
  ) => { hiddenCount: number };

  /**
   * Drag the child out of its parent — clears `containerId` and stores
   * the child's new ABSOLUTE position. Returns the list of relations
   * between the two concepts that were hidden on this diagram so the
   * caller can decide which (if any) to unhide.
   */
  unnestOnDiagram: (
    workspaceId: string,
    branchId: string,
    childInstanceId: string,
    absolutePosition: { x: number; y: number },
  ) => { previouslyHiddenRelationIds: string[] };

  /** Clear `viewState[relationId].hidden`, making the edge visible again. */
  unhideRelationOnDiagram: (workspaceId: string, branchId: string, relationId: string) => void;

  /**
   * Scan één of alle diagrammen op de actieve branch en zet
   * `containerId` voor concepten die geometrisch BINNEN een (grotere)
   * container vallen maar nog geen parent-link hebben. Position wordt
   * mee-geconverteerd van absoluut naar parent-relatief.
   *
   * Scope:
   *   - "active" → alleen het actief geopende diagram (Cmd+Shift+F-shortcut)
   *   - "all"    → alle diagrammen op de actieve branch (Repository-menu)
   *
   * Returnt totaal-gekoppeld + aantal diagrammen waar iets gebeurde,
   * zodat de UI een korte bevestiging kan tonen.
   */
  autoDetectNestingOnDiagram: (
    workspaceId: string,
    branchId: string,
    scope?: "active" | "all",
  ) => { nestedCount: number; diagramsAffected: number };

  /**
   * Persist which side of each endpoint node a relation is attached to.
   * Stored in the active diagram's view_state and debounced through
   * patchDiagram, so the choice survives a reload.
   */
  setRelationHandlesOnCanvas: (
    workspaceId: string,
    branchId: string,
    relationId: string,
    sourceHandle: string | null,
    targetHandle: string | null,
  ) => void;

  /**
   * Persist the source/target attachment angles (degrees, point_from_angle
   * convention: 0=right, 90=down, 180=left, 270=up) for a relation on the
   * active diagram. Pass `undefined` to leave an angle unchanged, `null` to
   * explicitly clear it.
   */
  setRelationAnglesOnCanvas: (
    workspaceId: string,
    branchId: string,
    relationId: string,
    sourceAngle: number | null | undefined,
    targetAngle: number | null | undefined,
  ) => void;

  /**
   * Replace the waypoint list for a relation on the active diagram. Used
   * by the canvas's RoutedEdge when the user drags a bend-point. An
   * empty array (or `null`) clears all waypoints, falling back to the
   * default smoothstep routing.
   */
  setRelationWaypointsOnCanvas: (
    workspaceId: string,
    branchId: string,
    relationId: string,
    waypoints: Array<{ x: number; y: number }> | null,
  ) => void;

  /**
   * Atomisch zetten van waypoints + source/target-angles voor één
   * relation, in één undo-entry. Gebruikt door de "Recht maken"-actie
   * zodat de afgeleide endpoint-angles na het orthogonaliseren in
   * hetzelfde write-pad worden gepersisteerd. Voorkomt dat een 2e
   * klik op Recht het edge-gedrag weer laat verschuiven omdat de
   * angles nog undefined waren en de render een andere fallback gaf
   * dan `edgeEndpointPos` in CanvasToolbar.
   */
  setRelationGeometryOnCanvas: (
    workspaceId: string,
    branchId: string,
    relationId: string,
    waypoints: Array<{ x: number; y: number }> | null,
    sourceAngle: number | null | undefined,
    targetAngle: number | null | undefined,
  ) => void;

  /**
   * Set the line stroke colour for a relation on the active diagram. Pass
   * `null` (or `undefined`) to clear the override and fall back to the
   * relation type's default colour. Pushes a single user-undo entry.
   */
  setRelationLineColorOnCanvas: (
    workspaceId: string,
    branchId: string,
    relationId: string,
    color: string | null,
  ) => void;

  /**
   * Vervang de items-lijst van een drawing.ListBox-instance. Per-
   * instance opgeslagen in viewState.listboxItems. Pushes één
   * user-undo entry zodat Cmd+Z de hele wijziging terugrolt.
   */
  setListboxItemsOnCanvas: (
    workspaceId: string,
    branchId: string,
    instanceId: string,
    items: Array<{ id: string; kind: "concept" | "diagram" }>,
  ) => void;

  /**
   * Set the stroke pattern (solid / dashed / dotted) for a relation.
   * Pass `null` to clear back to the relation-type default. Pushes a
   * user-undo entry per call so Cmd+Z reverts the change cleanly.
   */
  setRelationLineStyleOnCanvas: (
    workspaceId: string,
    branchId: string,
    relationId: string,
    style: "solid" | "dashed" | "dotted" | "none" | null,
  ) => void;

  /**
   * Set the per-relation label-background colour on the active diagram.
   * Default rendering is transparent (text-only with white text-shadow);
   * setting an explicit colour gives the label a solid background so
   * it's legible over busy areas. Pass `null` to clear back to
   * transparent.
   */
  setRelationLabelBgColorOnCanvas: (
    workspaceId: string,
    branchId: string,
    relationId: string,
    color: string | null,
  ) => void;

  /**
   * Set the per-relation label-text colour on the active diagram. When
   * `null`, the relation falls back to its type-default text colour
   * (slate-600 voor gewone, donkergrijs voor Influence-weight).
   */
  setRelationLabelTextColorOnCanvas: (
    workspaceId: string,
    branchId: string,
    relationId: string,
    color: string | null,
  ) => void;

  /**
   * De opmaak van het edge-label: lettergrootte, vet, cursief, onderstreept,
   * lettertype. Eén actie voor het hele stel, want de werkbalk zet ze in
   * dezelfde handeling; `undefined` in de patch laat een veld ongemoeid,
   * `null` wist 'm terug naar de standaard van het relatietype.
   */
  setRelationLabelStyleOnCanvas: (
    workspaceId: string,
    branchId: string,
    relationId: string,
    patch: {
      labelFontSize?: number | null;
      labelBold?: boolean | null;
      labelItalic?: boolean | null;
      labelUnderline?: boolean | null;
      labelFontFamily?: string | null;
    },
  ) => void;

  /** Per-relation text-align (left/center/right) van de tekst binnen
   *  het edge-label-blok. `null` = clear (label valt terug op
   *  default-centered). */
  setRelationLabelTextAlignOnCanvas: (
    workspaceId: string,
    branchId: string,
    relationId: string,
    align: "left" | "center" | "right" | null,
  ) => void;

  /** Per-relation word-wrap-strategie voor lange labels. `null` = clear. */
  setRelationLabelWrapOnCanvas: (
    workspaceId: string,
    branchId: string,
    relationId: string,
    wrap: "hyphenate" | "break-anywhere" | null,
  ) => void;

  /**
   * Set the per-relation max-width (in pixels) of the edge-label
   * container, forcing the label to wrap. Set via Cmd/Ctrl-drag on
   * the label. Pass `null` to clear back to intrinsic content width.
   */
  setRelationLabelMaxWidthOnCanvas: (
    workspaceId: string,
    branchId: string,
    relationId: string,
    width: number | null,
  ) => void;

  /**
   * Set per-side end-label-inset (pixels langs de lijn vanaf het
   * respectieve uiteinde) voor Sharp ConceptAssociation forward/
   * reverse-naam-rendering. `null` reset naar default. Side bepaalt
   * welke van de twee labels.
   */
  setRelationEndLabelInsetOnCanvas: (
    workspaceId: string,
    branchId: string,
    relationId: string,
    side: "source" | "target",
    inset: number | null,
  ) => void;

  /**
   * Set per-side max-breedte (px) op forward/reverse-labels van een
   * Sharp ConceptAssociation. `null` reset naar intrinsiek (één regel).
   * Tekst wraps boven die breedte over meerdere regels.
   */
  setRelationEndLabelWidthOnCanvas: (
    workspaceId: string,
    branchId: string,
    relationId: string,
    side: "source" | "target",
    width: number | null,
  ) => void;

  /**
   * Vrije 2D-offset (px) van het forward/reverse-label t.o.v. de
   * default-positie nabij het uiteinde — analoog aan labelOffset op
   * het standaard relatie-label.
   */
  setRelationEndLabelOffsetOnCanvas: (
    workspaceId: string,
    branchId: string,
    relationId: string,
    side: "source" | "target",
    offset: { x: number; y: number } | null,
  ) => void;

  /**
   * Set the per-relation routing strategy on the active diagram. Pass
   * `null` to clear the override and fall back to the default routing.
   */
  /**
   * De volgorde van de banen van boven naar beneden, als lijst van
   * bewoner-ids. Wie er niet in staat valt terug op de afgeleide volgorde,
   * achter de rest. Per diagram, en in één ongedaan-stap.
   */
  setSwimlaneRowOrderOnCanvas: (workspaceId: string, branchId: string, volgorde: string[]) => void;
  /**
   * In welke baan een voorkomen op het banen-canvas ligt — het concept-id van
   * de bewoner, of `null` voor de baan zonder uitvoerder.
   *
   * Legt de koppeling vast die anders alleen uit de y-positie volgde. Geen
   * eigen ongedaan-stap: dit reist mee met de verplaatsing die 'm veroorzaakt
   * (`moveConceptOnCanvas` binnen dezelfde batch).
   */
  setSwimlaneLaneOnCanvas: (
    workspaceId: string,
    branchId: string,
    sleutel: string,
    baanId: string | null,
  ) => void;
  /**
   * De hoogte van één baan op het banen-canvas, in punten. `null` zet 'm
   * terug op de standaardhoogte.
   */
  setSwimlaneLaneHeightOnCanvas: (
    workspaceId: string,
    branchId: string,
    bewonerId: string,
    hoogte: number | null,
  ) => void;
  /**
   * De meebewegende knik van een stroom op het banen-canvas, of `null` om 'm
   * weer recht te trekken. Zie `SwimlaneKnik` — geen absolute punten.
   */
  setSwimlaneKnikOnCanvas: (
    workspaceId: string,
    branchId: string,
    relationId: string,
    knik: { langs: number; opzij: number } | null,
  ) => void;
  setRelationRoutingOnCanvas: (
    workspaceId: string,
    branchId: string,
    relationId: string,
    routing: "smoothstep" | "straight" | "bezier" | "step" | "manual" | null,
  ) => void;

  /**
   * Set the pixel offset of a relation's label from the route's natural
   * midpoint. `null` clears the offset.
   */
  setRelationLabelOffsetOnCanvas: (
    workspaceId: string,
    branchId: string,
    relationId: string,
    offset: { x: number; y: number } | null,
  ) => void;

  /**
   * Pixel offset van het concept-label t.o.v. z'n natural-positie
   * (bepaald door labelAlignX/Y + labelOutside). Gebruikt om labels
   * vrij te verslepen — analoog aan setRelationLabelOffsetOnCanvas.
   * `null` clears the offset.
   */
  setConceptLabelOffsetOnCanvas: (
    workspaceId: string,
    branchId: string,
    instanceId: string,
    offset: { x: number; y: number } | null,
  ) => void;

  /**
   * Merge a partial NodeStyle patch into the concept-instance's view_state.
   * Null values in the patch clear individual fields; passing an empty object
   * leaves the style unchanged. Pushes a single undo entry.
   */
  setNodeStyleOnCanvas: (
    workspaceId: string,
    branchId: string,
    instanceId: string,
    patch: Partial<import("@ae/domain").NodeStyle>,
  ) => void;

  /**
   * Z-stack manipulatie op het actieve diagram. Werkt op een set
   * instance-ids tegelijk (multi-select friendly). Eén undo-entry per
   * call zodat een toolbar-klik atomair undoable is.
   *
   *   - "forward"  : zOrder += 1 voor elke selectie
   *   - "backward" : zOrder -= 1
   *   - "toFront"  : zOrder = max(alle zOrders op diagram) + 1
   *   - "toBack"   : zOrder = min(alle zOrders op diagram) - 1
   */
  changeZOrderOnCanvas: (
    workspaceId: string,
    branchId: string,
    instanceIds: string[],
    action: "forward" | "backward" | "toFront" | "toBack",
  ) => void;

  connectConcepts: (
    workspaceId: string,
    branchId: string,
    sourceId: string,
    targetId: string,
    relationTypeId: string,
    /**
     * Optionele stereotypes om bij creatie meteen toe te passen
     * (analoog aan `addConceptOnCanvas`). Required-attrs van die
     * stereotypes worden gevuld met NTB/TBD-defaults zodat de
     * worker-validatie passeert.
     */
    stereotypes?: string[],
    /**
     * Optionele extra attributen die bovenop de stereotype-defaults
     * worden gezet (bv. `directed: true` op een Association zodat 'ie
     * met een halve pijl rendert). Wint van de NTB/TBD-defaults bij
     * conflict op dezelfde key.
     */
    extraAttributes?: Record<string, unknown>,
  ) => Promise<Relation>;

  patchConcept: (
    workspaceId: string,
    branchId: string,
    conceptId: string,
    patch: ConceptPatch,
  ) => Promise<void>;

  patchRelation: (
    workspaceId: string,
    branchId: string,
    relationId: string,
    patch: RelationPatch,
  ) => Promise<void>;

  /** `actionId` koppelt deze delete aan een bulk-delete-actie zodat
   *  de Prullenbak alle records bij die actie als één entry kan
   *  tonen. Onafgemaakte / single-delete-calls passen 'm gewoon weg. */
  removeConcept: (
    workspaceId: string,
    branchId: string,
    conceptId: string,
    actionId?: string,
  ) => Promise<void>;
  removeRelation: (
    workspaceId: string,
    branchId: string,
    relationId: string,
    actionId?: string,
  ) => Promise<void>;

  /**
   * Batch-delete van concepten + losse relaties met **één** gecombineerde
   * undo-entry. Verschil met loop van `removeConcept` + `removeRelation`:
   *  - Per-concept-undo's vangen incident-relations met de IDs zoals
   *    ze waren VÓÓR de batch. Wanneer concepten in volgorde worden
   *    gerestored krijgt elk een nieuwe UUID → de op-id-gebaseerde
   *    cross-references tussen relaties en het andere concept lopen
   *    stuk (target verwijst naar oude id, die niet meer bestaat).
   *  - Combined undo verzamelt alle concepten + alle (incident +
   *    expliciete) relaties in één snapshot; restoret eerst alle
   *    concepten (oude-id → nieuwe-id map opbouwend), dan alle
   *    relaties met endpoints vertaald via die map.
   */
  removeBatch: (
    workspaceId: string,
    branchId: string,
    conceptIds: string[],
    relationIds: string[],
  ) => Promise<void>;

  /**
   * Soft-hide on the active diagram only — sets `view_state[id].hidden`
   * so the canvas filters it out, but the concept/relation remains in the
   * workspace store and other diagrams. Used by the plain Delete keystroke.
   * Cmd/Ctrl+Delete still calls `removeConcept` / `removeRelation` for a
   * full hard-delete.
   */
  hideConceptOnDiagram: (workspaceId: string, branchId: string, conceptId: string) => void;
  hideRelationOnDiagram: (workspaceId: string, branchId: string, relationId: string) => void;

  /**
   * Set view-element-state on the active diagram and schedule a persist.
   * Used by paste-undo om de exacte (instanceId, viewState)-paren terug
   * te zetten zonder via een specifiek-gegeneriek pad te lopen.
   */
  /**
   * Voeg ≥1 loser-concepten samen in de winner. Server-side: relaties,
   * viewState-references en folder-koppelingen verlegd naar winner;
   * losers hard verwijderd. Lokaal: store-mutaties zodat de UI direct
   * actueel is zonder reload.
   */
  mergeConcepts: (
    workspaceId: string,
    branchId: string,
    winnerId: string,
    loserIds: string[],
  ) => Promise<void>;

  mergeRelations: (
    workspaceId: string,
    branchId: string,
    winnerId: string,
    loserIds: string[],
  ) => Promise<void>;

  /** Check of `relationId` nu identiek is aan een bestaande relatie en zo ja
   *  de samenvoeg-vraag tonen. Aangeroepen op COMMIT van een rename
   *  (blur/Enter) en door de patch-hook bij type/richting/directed-wijziging. */
  checkRelationDuplicate: (
    workspaceId: string,
    branchId: string,
    relationId: string,
  ) => Promise<void>;

  /** Check of `conceptId` (named) nu identiek is aan een bestaand concept
   *  (type + naam + stereotypes) en zo ja de samenvoeg-vraag tonen. Aangeroepen
   *  bij aanmaken (drag) én op COMMIT van een rename. Named-only. */
  checkConceptDuplicate: (
    workspaceId: string,
    branchId: string,
    conceptId: string,
  ) => Promise<void>;

  applyViewElementOnCanvas: (
    workspaceId: string,
    branchId: string,
    diagramId: string,
    instanceId: string,
    viewState: ViewElementState,
  ) => void;
  /** Remove a single viewState-entry; gebruikt door paste-undo om
   *  geplaatste items volledig op te ruimen (geen hidden-flag). */
  removeViewElementOnCanvas: (
    workspaceId: string,
    branchId: string,
    diagramId: string,
    instanceId: string,
  ) => void;

  /**
   * Toggle de collapse-state van een container-instance op het actieve
   * diagram. Collapse → alle directe + transitive descendants krijgen
   * `hidden: true` met behouden position, edges naar hidden endpoints
   * worden door de Canvas-edge-re-anchor automatisch geprojecteerd op
   * de container. Expand → clear hidden recursief. Undo via Cmd+Z.
   */
  toggleCollapseContainer: (
    workspaceId: string,
    branchId: string,
    containerInstanceId: string,
  ) => void;

  /** Push an entry onto the user-undo stack. Internal — call from actions. */
  pushUserUndo: (entry: UserUndoEntry) => void;
  /**
   * Start een user-undo-batch: alle `pushUserUndo`-entries tot `endUserUndoBatch`
   * worden gebundeld in één undo-entry. Gebruikt door de canvas-drag zodat een
   * multi-node-move (+ waypoint-shifts) met één Cmd+Z terugdraait.
   */
  beginUserUndoBatch: () => void;
  /** Sluit de batch: bundelt de verzamelde entries in één composite-undo-entry. */
  endUserUndoBatch: (label: string) => void;
  /**
   * Voeg achteraf samen: `marker` en alle undo-entries die daarna op de stack
   * kwamen worden één stap. Voor flows waar tussen de eerste stap en de rest
   * een (async) vraag aan de gebruiker zit — bv. herschikken naar een andere
   * container + de oude relatie verwijderen. No-op als `marker` niet (meer)
   * op de stack staat.
   */
  mergeUserUndoSince: (marker: UserUndoEntry, label: string) => void;
  /** Cmd+Z. Pops the top of the undo stack, runs `undo`, pushes onto redo. */
  userUndo: () => Promise<void>;
  /** Cmd+Shift+Z / Cmd+Y. Pops the top of the redo stack, runs `redo`. */
  userRedo: () => Promise<void>;

  clearError: () => void;
}

export type WorkspaceStoreState = InternalState & Actions;

const sharedStore = new WorkspaceStore();
const sharedUndo = new UndoStack();

// User-undo-batch-buffer (module-level, geen Zustand-state → geen re-render-
// churn tijdens een drag). Wanneer non-null verzamelen pushUserUndo-entries
// hierin i.p.v. direct op de stack; endUserUndoBatch bundelt ze in één
// composite-entry zodat één Cmd+Z de hele batch (bv. multi-node-move +
// waypoint-shifts) terugdraait.
let userUndoBatch: UserUndoEntry[] | null = null;

// Sync de WorkspaceStore-revision naar useWorkspaceData zodat React-
// components via Zustand re-renderen op store-mutaties. Equality-check
// voorkomt overbodige setState-calls als de store notify't met dezelfde
// revision (defensive — Zustand bail't normaal op shallow-equal, maar
// een no-op call genereert nog steeds een subscribe-trigger in dev/
// StrictMode wat zichtbaar is als "Maximum update depth exceeded"-
// warnings bij high-frequency canvas-mutaties zoals drag).
sharedStore.subscribe((s) => {
  if (useWorkspaceData.getState().revision !== s.revision) {
    useWorkspaceData.setState({ revision: s.revision });
  }
});

export const useWorkspaceData = create<WorkspaceStoreState>((set, get) => ({
  store: sharedStore,
  undo: sharedUndo,
  userUndoStack: [],
  userRedoStack: [],
  canvasGeometryNonce: 0,
  revision: 0,
  activeDiagramId: null,
  openDiagramIds: [],
  lastError: null,
  customViewKinds: [],

  hydrate: async (workspaceId, branchId) => {
    // Fase-1 branch-diagram-nasynchronisatie (ontwerp 2026-06-07): trek vóór
    // het laden de plaatsingen over die de parent ná de fork aan een diagram
    // toevoegde, zodat nieuwe main-objecten op de branch-kopie verschijnen
    // zonder branch-eigen wijzigingen plat te slaan. Server-side idempotent +
    // timestamp-gated. Faalt stil — een reconcile-hapering mag het laden nooit
    // blokkeren; de branch laadt dan gewoon zonder de nieuwe merge. Een lezer
    // synchroniseert niet na: dat is schrijven (de worker weigert), en de
    // prompts die eruit komen kan 'ie toch niet afhandelen.
    try {
      const rec = useSession.getState().readOnly
        ? { parentDeletedPrompts: [], homonymPrompts: [] }
        : await api.reconcileBranch(workspaceId, branchId);
      // Fase-2: parent-delete-prompts surfacen naar de modal (leeg = sluit een
      // eventueel openstaande modal van een vorige branch).
      useUi.getState().setParentDeletePrompts(rec.parentDeletedPrompts ?? []);
      // Fase-3: homoniem-link-suggesties surfacen (leeg = sluit een eventueel
      // openstaande modal van een vorige branch).
      useUi.getState().setHomonymPrompts(rec.homonymPrompts ?? []);
    } catch {
      // negeren
    }
    const [conceptsRes, relationsRes, diagrams, customViewKinds, profilesRes] = await Promise.all([
      api.listConcepts(workspaceId, branchId),
      api.listRelations(workspaceId, branchId),
      api.listDiagrams(workspaceId, branchId),
      api.listCustomViewKinds(workspaceId).catch(() => [] as CustomViewKindDTO[]),
      // Faalt dit, dan draait de registry op de pack-definities — dezelfde
      // terugval als aan de worker-kant. Een hapering in de profiel-tabel
      // mag geen lege typelijst opleveren.
      api.listProfiles(workspaceId, branchId).catch(() => null),
    ]);

    // Profielen vervángen (niet stapelen): bij een branch-wissel naar een
    // ander workpackage moeten de profielen van het vorige verdwijnen, anders
    // staan ze in de keuzelijst terwijl ze daar niet gelden.
    if (profilesRes) {
      getRegistry().setDynamicProfiles(
        profilesRes.profiles.map((p) => ({
          id: p.name,
          label: p.label ?? { nl: p.name, en: p.name },
          ...(p.description ? { description: p.description } : {}),
          applies_to: p.applies_to,
          ...(p.attributes ? { attributes: p.attributes as never } : {}),
          ...(p.icon_id ? { icon_id: p.icon_id } : {}),
        })),
      );
    }

    // Fase-1 multi-instance-refactor: backfill `type` + `ref` op alle
    // diagram-viewStates uit deze workspace voordat ze de store in gaan.
    // Idempotent — pre-migratie entries krijgen de aanvulling, al-gemigreerde
    // entries blijven referentieel-identiek (geen onnodig re-render).
    // Persistence-strategie: in-memory NU, terugschrijven naar D1 gebeurt
    // bij de eerstvolgende viewState-patch op een diagram (PendingPatch-
    // debounce stuurt het volledige viewState-blob, dus het gemigreerde
    // veld lift automatisch mee). Diagrammen waar niemand op werkt blijven
    // Fase-0 op de server — Fase-2-cleanup-pass migreert die ooit
    // server-side offline.
    const conceptIdSet = new Set(conceptsRes.items.map((c) => c.id));
    const relationIdSet = new Set(relationsRes.items.map((r) => r.id));
    const resolver = setResolver(conceptIdSet, relationIdSet);
    const preStore = get().store;
    const migratedDiagrams = diagrams.map((d) => {
      // Behoud lokale, nog-niet-gepersiste viewState voor diagrammen met een
      // in-flight (gedebouncede) patch. Zonder dit overschrijft een self-
      // getriggerde rehydrate (CRDT-tick ná onze eigen mutatie) een node-drag
      // die de server nog niet heeft bereikt → het object springt terug naar
      // z'n oude positie (zichtbaar bij een verse vereenvoudigde kopie: eerste
      // drag reverte, tweede bleef staan). De debounce-flush stuurt straks
      // alsnog de volledige blob, dus we verliezen niets.
      const localCurrent = pendingDiagramPatches.has(d.id) ? preStore.getDiagram(d.id) : null;
      const source = localCurrent ? { ...d, viewState: localCurrent.viewState } : d;
      const result = normalizeViewState(source.viewState ?? {}, resolver);
      if (result.migrated === 0) return source;
      return { ...source, viewState: result.viewState };
    });

    const store = get().store;
    store.transaction(() => {
      store.replace(conceptsRes.items, relationsRes.items, migratedDiagrams);
    });
    // De stand van de server vastleggen als uitgangspunt voor het verschil dat
    // een patch meestuurt. Zonder dit vergelijkt de eerstvolgende schrijfactie
    // met zichzelf en stuurt 'ie niets mee.
    for (const d of migratedDiagrams) {
      if (!pendingDiagramPatches.has(d.id)) {
        serverViewStates.set(d.id, { ...(d.viewState ?? {}) });
      }
    }
    // Mirror custom view-kinds into the runtime registry so the rest of
    // the app (TypePalette, Diagrams panel, drop-validation) treats them
    // identically to the bundled Open Group viewpoints.
    const reg = getRegistry();
    reg.replaceCustomViewKinds(customViewKinds.map((c) => customViewKindToViewKind(c)));
    // Replace any previously-registered workspace subtypes met de nieuwe
    // set. We strippen alleen extensions met de "subtype.<workspace>." prefix
    // zodat pack-shipped extensions (zoals c4.* of drawing.*) intact blijven.
    set({ customViewKinds });

    // Per-branch keys VÓÓR elke read/write zodat zowel de actieve diagram als
    // de open-tab-set PER BRANCH worden onthouden. Bij een switch naar een nog
    // niet bezochte branch opent alleen het actieve diagram (geen flood); een
    // eerder bezochte branch herstelt z'n eigen active + tabs.
    currentWorkspaceId = workspaceId;
    openTabsBranchKey = branchId;
    let activeId = get().activeDiagramId;
    if (!activeId || !store.getDiagram(activeId)) {
      // Na een hard refresh is de in-memory active-id null → herstel de
      // gepersisteerde actieve diagram (per branch) als die nog bestaat;
      // anders het eerste diagram.
      const persistedActive = readActiveDiagramId(workspaceId);
      activeId =
        persistedActive && store.getDiagram(persistedActive)
          ? persistedActive
          : (diagrams[0]?.id ?? null);
    }
    // Restore the previously-open tab set from localStorage if available;
    // otherwise open ONLY the active diagram (de gebruiker bepaalt zelf wat
    // er verder opengaat — geen auto-open-alles bij branch/WP-switch).
    const persisted = readOpenDiagramIds(workspaceId);
    const allDiagramIds = diagrams.map((d) => d.id);
    const validIds = new Set(allDiagramIds);
    // Diagrammen die deze branch NOOIT eerder heeft gezien zijn écht nieuw
    // (server-side binnengekomen via AMEFF/JSON-import, snapshot-restore of
    // realtime-collab) en defaulten naar OPEN. Een diagram dat de gebruiker
    // bewust heeft gesloten zit wél in `known` en komt dus NIET terug. Bij de
    // allereerste hydrate van een branch (known == null) beschouwen we niets
    // als "nieuw" — dan opent alleen het actieve diagram, geen flood.
    const known = readKnownDiagramIds(workspaceId);
    const knownSet = new Set(known ?? allDiagramIds);
    const newlyArrived = known ? allDiagramIds.filter((id) => !knownSet.has(id)) : [];
    let openIds: string[];
    if (known && persisted) {
      // Normaal pad: branch eerder gezien onder dit schema. Herstel de
      // gepersisteerde tabs + voeg écht-nieuwe diagrammen toe.
      openIds = [...persisted.filter((id) => validIds.has(id)), ...newlyArrived];
    } else {
      // Eerste hydrate van deze branch onder het known-schema (known == null):
      // de oude flood-logica kan de gepersisteerde set hebben volgepompt met
      // álle diagrammen, dus die is niet te vertrouwen. Schone lei: alleen het
      // actieve diagram opent (zie de activeId-borging hieronder). Geldt ook
      // voor een echt eerste branch-bezoek.
      openIds = [];
    }
    if (activeId && !openIds.includes(activeId)) {
      openIds = [...openIds, activeId];
    }
    // Dedup (active kan al in newlyArrived zitten).
    openIds = [...new Set(openIds)];
    writeOpenDiagramIds(workspaceId, openIds);
    // Markeer alle huidige diagrammen als "gezien" zodat ze volgende keer niet
    // opnieuw als nieuw-binnengekomen tellen.
    writeKnownDiagramIds(workspaceId, allDiagramIds);
    set({ activeDiagramId: activeId, openDiagramIds: openIds });
  },

  setActiveDiagram: (diagramId) => set({ activeDiagramId: diagramId }),

  openDiagram: (workspaceId, diagramId) => {
    // Een gewoon diagram openen haalt de focus van de Glossary af (de
    // Glossary-tab blijft staan zodat de modelleur terug kan klikken).
    useUi.getState().blurGlossary();
    set((s) => {
      const next = s.openDiagramIds.includes(diagramId)
        ? s.openDiagramIds
        : [...s.openDiagramIds, diagramId];
      writeOpenDiagramIds(workspaceId, next);
      return { openDiagramIds: next, activeDiagramId: diagramId };
    });
  },

  closeDiagramTab: (workspaceId, diagramId) => {
    set((s) => {
      const next = s.openDiagramIds.filter((id) => id !== diagramId);
      writeOpenDiagramIds(workspaceId, next);
      // If the closed tab was active, switch to the next surviving tab,
      // or null when none remain. The diagram itself stays in the
      // workspace store and Diagrams panel.
      const nextActive =
        s.activeDiagramId === diagramId ? (next[next.length - 1] ?? null) : s.activeDiagramId;
      return { openDiagramIds: next, activeDiagramId: nextActive };
    });
  },

  createDiagram: async (workspaceId, branchId, name, viewKind) => {
    const created = await api.createDiagram(workspaceId, branchId, {
      name,
      notation_pack: "archimate",
      view_kind: viewKind,
    });
    sharedUndo.apply(upsertDiagramCommand(get().store, created));
    set((s) => {
      const next = s.openDiagramIds.includes(created.id)
        ? s.openDiagramIds
        : [...s.openDiagramIds, created.id];
      writeOpenDiagramIds(workspaceId, next);
      return { activeDiagramId: created.id, openDiagramIds: next };
    });
    // Roadmap-diagram → server ensure-creëert Gap + Plateau + het Program-object
    // op de WP-main. Volledige hydrate zodat siblings én het Program-concept in
    // de store komen (de Roadmap-matrix bindt aan dat Program, §6.4).
    if (viewKind === "roadmap.roadmap-diagram") {
      await get().hydrate(workspaceId, branchId);
    }
    return created;
  },

  createSimplifiedCopy: async (workspaceId, branchId, sourceDiagramId, options, autoLayout) => {
    const store = get().store;
    const source = store.getDiagram(sourceDiagramId);
    if (!source) return null;
    const viewState = source.viewState;

    // Concepten die zichtbaar op het bron-diagram staan.
    const present = new Set<string>();
    for (const [k, ve] of Object.entries(viewState)) {
      if (ve.type === "object" && ve.position) present.add(ve.ref ?? k);
    }
    // Relaties op het diagram (beide eindpunten aanwezig).
    const relById = new Map(store.listRelations().map((r) => [r.id, r]));
    const relations: SimplifyRelationRef[] = [];
    for (const [k, ve] of Object.entries(viewState)) {
      if (ve.type !== "relation") continue;
      const rel = relById.get(ve.ref ?? k);
      if (rel && present.has(rel.sourceConceptId) && present.has(rel.targetConceptId)) {
        relations.push({
          id: rel.id,
          typeId: rel.typeId,
          sourceConceptId: rel.sourceConceptId,
          targetConceptId: rel.targetConceptId,
        });
      }
    }
    const hidden = computeHiddenConcepts({ conceptIds: [...present], relations }, options);

    // Fase 2 — layer-collapse: per gekozen laag de members bepalen en één
    // synthetische super-node-entry aanmaken. Members worden hidden +
    // containerId→super-node zodat hun externe relaties via het bestaande
    // edge-re-anchor-mechanisme op het super-blok landen (interne
    // member↔member-relaties collapsen vanzelf weg via de same-anchor-skip
    // in de Canvas-edge-loop).
    const memberToSupernode = new Map<string, string>();
    const supernodeEntries = new Map<string, ViewElementState>();

    // Bouw één super-node-entry uit een member-set (gedeeld door layer-collapse
    // en promote-to-abstraction). Positie = centroid van de members zodat 't
    // blok zonder auto-layout op een logische plek landt. Members worden hidden
    // + gekoppeld via memberToSupernode (containerId volgt in de kopieer-pass).
    const makeSuperNode = (
      members: string[],
      sn: NonNullable<ViewElementState["supernode"]>,
    ): void => {
      if (members.length === 0) return;
      const snKey = crypto.randomUUID();
      let sx = 0;
      let sy = 0;
      let n = 0;
      for (const [k, ve] of Object.entries(viewState)) {
        if (ve.type !== "object" || !ve.position) continue;
        if (!members.includes(ve.ref ?? k)) continue;
        sx += ve.position.x;
        sy += ve.position.y;
        n++;
      }
      supernodeEntries.set(snKey, {
        type: "supernode",
        ref: snKey,
        position: n > 0 ? { x: sx / n, y: sy / n } : { x: 80, y: 80 },
        width: 260,
        height: 120,
        supernode: sn,
      });
      for (const m of members) {
        memberToSupernode.set(m, snKey);
        hidden.add(m);
      }
    };

    // Fase 2 — layer-collapse: per gekozen laag de members bepalen en één
    // synthetische super-node-entry aanmaken. Members worden hidden +
    // containerId→super-node zodat hun externe relaties via het bestaande
    // edge-re-anchor-mechanisme op het super-blok landen (interne
    // member↔member-relaties collapsen vanzelf weg via de same-anchor-skip
    // in de Canvas-edge-loop).
    const collapseLayers = options.collapseLayers ?? [];
    if (collapseLayers.length > 0) {
      const reg = getRegistry();
      const conceptLayers: Record<string, string> = {};
      for (const cid of present) {
        const concept = store.getConcept(cid);
        const layer = concept ? reg.getElementType(concept.typeId)?.layer : undefined;
        if (layer) conceptLayers[cid] = layer;
      }
      const membersByLayer = layerCollapseMembers([...present], conceptLayers, collapseLayers);
      for (const [layer, members] of membersByLayer) {
        makeSuperNode(members, { layer, memberConceptIds: members });
      }
    }

    // Fase 2 — promote-to-abstraction: elke opgegeven cluster (detail-elementen
    // die samen één abstractie vormen) wordt gevouwen tot één
    // ApplicationCollaboration-gestileerd blok. Alleen members die (nog)
    // zichtbaar op 't diagram staan en niet al in een ander blok zitten.
    for (const cluster of options.promoteClusters ?? []) {
      const members = cluster.memberConceptIds.filter(
        (id) => present.has(id) && !memberToSupernode.has(id),
      );
      if (members.length < 2) continue;
      makeSuperNode(members, {
        layer: "application",
        typeId: PROMOTE_ABSTRACTION_TYPE_ID,
        memberConceptIds: members,
        ...(cluster.label ? { label: cluster.label } : {}),
      });
    }

    // Diepe kopie van de viewState + `hidden` op de gekozen concepten en op
    // relaties waarvan een eindpunt verborgen wordt (geen dangling edges).
    // Uitzondering: relaties van/naar super-node-members blijven zichtbaar —
    // die herankeren op het super-blok (tenzij beide eindpunten in dezelfde
    // super-node zitten, dan verdwijnen ze vanzelf via same-anchor-skip).
    const nextViewState: ViewState = structuredClone(viewState);
    for (const [k, ve] of Object.entries(nextViewState)) {
      if (ve.type === "object" && ve.position) {
        const cid = ve.ref ?? k;
        if (hidden.has(cid)) {
          ve.hidden = true;
          const snKey = memberToSupernode.get(cid);
          if (snKey) {
            // Koppel de member visueel aan z'n super-node. Position wordt
            // relatief-aan-parent geïnterpreteerd zodra containerId gezet is;
            // zet 'm op het blok-center zodat de re-anchor-hoek neutraal is.
            ve.containerId = snKey;
            ve.position = { x: 130, y: 60 };
          }
        }
      } else if (ve.type === "relation") {
        const rel = relById.get(ve.ref ?? k);
        if (!rel) continue;
        const srcHiddenHard =
          hidden.has(rel.sourceConceptId) && !memberToSupernode.has(rel.sourceConceptId);
        const tgtHiddenHard =
          hidden.has(rel.targetConceptId) && !memberToSupernode.has(rel.targetConceptId);
        if (srcHiddenHard || tgtHiddenHard) {
          ve.hidden = true;
        }
      }
    }
    for (const [snKey, entry] of supernodeEntries) {
      nextViewState[snKey] = entry;
    }

    // Herschik de zichtbare top-level nodes (dagre) zodat de kopie strak
    // uitgelijnd is i.p.v. gaten waar verborgen nodes stonden.
    if (autoLayout) {
      const positions = layoutSimplifiedViewState(
        nextViewState,
        hidden,
        relations,
        memberToSupernode,
      );
      for (const [key, pos] of positions) {
        const entry = nextViewState[key];
        if (entry && (entry.type === "object" || entry.type === "supernode")) {
          entry.position = pos;
        }
      }
      // Wis de vaste handle-/hoek-/knikpunt-info op relaties: die kwam uit de
      // oude handmatige layout en zou de edges nu aan de verkeerde kant laten
      // hechten. Zonder die velden routet RoutedEdge vanzelf naar de dichtstbij-
      // zijnde rand (nearestBorderAngle) op basis van de nieuwe posities.
      for (const ve of Object.values(nextViewState)) {
        if (ve.type !== "relation") continue;
        ve.sourceHandle = undefined;
        ve.targetHandle = undefined;
        ve.sourceAngle = undefined;
        ve.targetAngle = undefined;
        ve.waypoints = undefined;
        ve.routing = undefined;
      }
    }

    // Naam-suffix = de toegepaste transform(s) i.p.v. het generieke
    // "vereenvoudigd", zodat je in de tab-titel ziet wat er is toegepast.
    const suffixParts: { nl: string; en: string }[] = [];
    if (options.hideLeaves) suffixParts.push({ nl: "eindpunten verborgen", en: "leaves hidden" });
    if (options.clusterByComposition)
      suffixParts.push({ nl: "samenstellingen opgerold", en: "compositions rolled up" });
    if (options.criticalPath) suffixParts.push({ nl: "kritiek pad", en: "critical path" });
    if (collapseLayers.length > 0)
      suffixParts.push({ nl: "laag samengevouwen", en: "layer collapsed" });
    if ((options.promoteClusters ?? []).length > 0)
      suffixParts.push({ nl: "gepromoveerd tot abstractie", en: "promoted to abstraction" });
    const suffixNl = suffixParts.map((p) => p.nl).join(", ") || "vereenvoudigd";
    const suffixEn = suffixParts.map((p) => p.en).join(", ") || "simplified";
    const name: Record<string, string> = {};
    if (source.name.nl) name.nl = `${source.name.nl} (${suffixNl})`;
    if (source.name.en) name.en = `${source.name.en} (${suffixEn})`;
    if (!name.nl && !name.en) name.nl = `(${suffixNl})`;

    const created = await api.createDiagram(workspaceId, branchId, {
      name,
      notation_pack: source.notationPack,
      view_kind: source.viewKind,
    });
    sharedUndo.apply(upsertDiagramCommand(store, created));
    for (const [key, entry] of Object.entries(nextViewState)) {
      store.setViewElementState(created.id, key, entry);
    }
    await api.patchDiagram(workspaceId, branchId, created.id, {
      view_state: nextViewState,
      attributes: created.attributes,
    });
    // Active-switch als LAATSTE, ná de create- én patch-mutatie. Elke
    // api-mutatie stuurt een CRDT-tick (setApiMutationHook) die een self-
    // `hydrate` triggert; die rehydrate racet met deze actie en zou een
    // vóór-de-patch gezette active terugdraaien naar het bron-diagram
    // (`hydrate` valt terug op `diagrams[0]` als 't nieuwe diagram nog niet in
    // de refetch zit). Door pas hier te switchen — als het nieuwe diagram
    // server-side gecommit is — wint deze set van elke eerdere rehydrate en
    // blijft de kopie-tab geselecteerd. Zie [[project_canvas_remount_key]].
    set((s) => {
      const next = s.openDiagramIds.includes(created.id)
        ? s.openDiagramIds
        : [...s.openDiagramIds, created.id];
      writeOpenDiagramIds(workspaceId, next);
      return { activeDiagramId: created.id, openDiagramIds: next };
    });
    bumpRevision();
    return { id: created.id, hiddenCount: hidden.size };
  },

  createDiagramFromTemplate: async (workspaceId, branchId, template, nameOverride) => {
    // 1. Maak het diagram aan (gebruikt server-side default-view-kind-
    //    validatie; bestaande risk.security-overview wordt geaccepteerd).
    const diagramName = nameOverride ?? template.defaultDiagramName;
    const created = await api.createDiagram(workspaceId, branchId, {
      name: diagramName,
      notation_pack: "archimate",
      view_kind: template.viewKind,
    });
    sharedUndo.apply(upsertDiagramCommand(get().store, created));

    // 2. Voor elk template-element: creëer een concept via de API +
    //    voeg meteen een viewState-entry toe met position/size/nodeStyle.
    //    Conceptueel batch'en we 'm één-voor-één omdat createConcept
    //    geen bulk-endpoint heeft; voor template-creatie (~17 elementen)
    //    is dat acceptabel.
    for (const el of template.elements) {
      try {
        const concept = await api.createConcept(workspaceId, branchId, {
          type_id: el.typeId,
          name: el.name,
          ...(el.description ? { description: el.description } : {}),
        });
        sharedUndo.apply(upsertConceptCommand(get().store, concept));
        // Direct viewState-entry plaatsen op het nieuwe diagram via de
        // store. SchedulePersist verderop schrijft 't geheel weg naar D1.
        get().store.setViewElementState(created.id, concept.id, {
          type: "object",
          ref: concept.id,
          position: el.position,
          width: el.width,
          height: el.height,
          nodeStyle: el.nodeStyle,
        });
      } catch (err) {
        console.error("[createDiagramFromTemplate] element failed", el, err);
        // Eén-element-failure laat de rest van het template-creatie-
        // proces doorgaan; gebruiker krijgt een gedeeltelijk diagram
        // i.p.v. een totaal-mislukking.
      }
    }

    // 3. Persist viewState naar D1 (debounced — vuurt binnen ~500ms).
    schedulePersistDiagram(get(), created.id, workspaceId, branchId);

    // 4. Open + focus het nieuwe diagram.
    set((s) => {
      const next = s.openDiagramIds.includes(created.id)
        ? s.openDiagramIds
        : [...s.openDiagramIds, created.id];
      writeOpenDiagramIds(workspaceId, next);
      return { activeDiagramId: created.id, openDiagramIds: next };
    });
    return created;
  },

  renameDiagram: async (workspaceId, branchId, diagramId, name) => {
    const state = get();
    const diagram = state.store.getDiagram(diagramId);
    if (!diagram) return;
    const next = { ...diagram, name };
    state.store.upsertDiagram(next);
    try {
      // De naam die we dachten te vervangen gaat mee: heeft iemand anders 'm
      // intussen hernoemd, dan weigert de server in plaats van 'm te
      // overschrijven.
      const response = await api.patchDiagram(workspaceId, branchId, diagramId, {
        name,
        name_was: diagram.name,
      });
      if (response.branchCopied && response.id !== diagramId) {
        handleDiagramBranchCopy(diagramId, response);
      }
    } catch (err) {
      state.store.upsertDiagram(diagram);
      set({ lastError: explainError(err) });
      // 409 = iemand was je voor; het beeld klopt dan ook niet meer.
      if (err instanceof ApiError && err.status === 409) {
        void get().hydrate(workspaceId, branchId);
      }
    }
  },

  setDiagramDescription: async (workspaceId, branchId, diagramId, description) => {
    const state = get();
    const diagram = state.store.getDiagram(diagramId);
    if (!diagram) return;
    // Empty bilingual → drop the field (matches server's "null = clear")
    const isEmpty =
      !description || Object.values(description).every((v) => !v || v.trim().length === 0);
    const nextDiagram: typeof diagram = isEmpty
      ? (() => {
          const { description: _omit, ...rest } = diagram;
          return rest;
        })()
      : { ...diagram, description };
    state.store.upsertDiagram(nextDiagram);
    try {
      const response = await api.patchDiagram(workspaceId, branchId, diagramId, {
        description: isEmpty ? null : description,
      });
      if (response.branchCopied && response.id !== diagramId) {
        handleDiagramBranchCopy(diagramId, response);
      }
    } catch (err) {
      state.store.upsertDiagram(diagram);
      set({ lastError: explainError(err) });
    }
  },

  changeDiagramViewKind: async (workspaceId, branchId, diagramId, viewKind) => {
    const state = get();
    const diagram = state.store.getDiagram(diagramId);
    if (!diagram) return;
    // Guard: a type change is only allowed when EVERY concept currently
    // placed on the diagram and EVERY relation drawn between those
    // concepts remains valid under the new view-kind's restrictions.
    // Going from "less restrictive" → "more restrictive" therefore
    // succeeds only when the diagram doesn't already use anything that
    // would fall outside the new allowlist; otherwise the user gets a
    // clear lastError and must clean up first.
    const incompat = checkDiagramTypeChange(state.store, diagram, viewKind);
    if (incompat) {
      set({ lastError: incompat });
      return;
    }
    const next = { ...diagram, viewKind };
    state.store.upsertDiagram(next);
    // A previous attempt may have left a `lastError` toast on screen
    // (e.g. tried a more-restrictive type that didn't fit). Now that
    // we're switching to a type that DID validate, clear the stale
    // banner so the user can see the change went through.
    set({ lastError: null });
    try {
      const response = await api.patchDiagram(workspaceId, branchId, diagramId, {
        view_kind: viewKind,
      });
      if (response.branchCopied && response.id !== diagramId) {
        handleDiagramBranchCopy(diagramId, response);
      }
      // Roadmap-diagram → de server ensure-creëert het Gap- + Plateau-diagram
      // op de WP-main ÉN het Program-object. Volledige hydrate zodat zowel de
      // sibling-diagrammen als het nieuwe Program-concept in de store komen —
      // de Roadmap-matrix bindt aan dat Program (§6.4).
      if (viewKind === "roadmap.roadmap-diagram") {
        await get().hydrate(workspaceId, branchId);
      } else if (viewKind === "roadmap.roadmap-matrix") {
        // Roadmap-matrix → de server verhuist 't diagram naar de WP-main
        // (branch-transparant). Re-list zodat de client de verhuizing
        // reflecteert i.p.v. 't op de actieve feature-branch te tonen.
        const list = await api.listDiagrams(workspaceId, branchId);
        const st = get().store;
        // Elke upsertDiagram-call bumpt + notify't al zelf (geen transaction-
        // wrapper hier); een extra directe `revision`-zet op de Zustand-
        // spiegel is overbodig en riskant (zie `bumpRevision`-comment).
        for (const d of list) st.upsertDiagram(d);
        // Aanmaak-popup: Program + concept-type kiezen → meteen live, geen
        // Explorer-omweg/refresh (§6.4). Het diagram is net omgezet, dus altijd
        // nog niet geconfigureerd op dit pad.
        useUi.getState().openRoadmapMatrixSetup(diagramId);
      }
    } catch (err) {
      state.store.upsertDiagram(diagram);
      set({ lastError: explainError(err) });
    }
  },

  setHeatMapConfig: (workspaceId, branchId, diagramId, config) => {
    const state = get();
    const diagram = state.store.getDiagram(diagramId);
    if (!diagram) return;
    const blob: Record<string, unknown> = {};
    if (config.targetTypeId) blob.targetTypeId = config.targetTypeId;
    if (config.targetStereotypeId) blob.targetStereotypeId = config.targetStereotypeId;
    if (config.verticalAttribute) blob.verticalAttribute = config.verticalAttribute;
    if (config.horizontalAttribute) blob.horizontalAttribute = config.horizontalAttribute;
    // HEAT_MAP_CONFIG_KEY is een sentinel-key (geen concept/relation-ref).
    // We zetten type+ref naar de sentinel zelf zodat de ViewElementState-
    // shape klopt; normalizeViewState mag deze entry overslaan (key heeft
    // geen concept/relation-target).
    state.store.setViewElementState(diagramId, HEAT_MAP_CONFIG_KEY, {
      type: "object",
      ref: HEAT_MAP_CONFIG_KEY,
      style: blob,
    });
    bumpRevision();
    schedulePersistDiagram(state, diagramId, workspaceId, branchId);
  },

  setOverlayRules: (workspaceId, branchId, diagramId, ruleset) => {
    const state = get();
    const diagram = state.store.getDiagram(diagramId);
    if (!diagram) return;
    const nextViewState = writeOverlayRules(diagram.viewState, ruleset);
    const entry = nextViewState[OVERLAY_RULES_KEY];
    if (entry) state.store.setViewElementState(diagramId, OVERLAY_RULES_KEY, entry);
    bumpRevision();
    schedulePersistDiagram(state, diagramId, workspaceId, branchId);
  },

  setRoadmapMatrixConfig: (workspaceId, branchId, diagramId, config) => {
    const state = get();
    const diagram = state.store.getDiagram(diagramId);
    if (!diagram) return;
    const blob: Record<string, unknown> = {};
    if (config.programId) blob.programId = config.programId;
    if (config.conceptFilter) blob.conceptFilter = config.conceptFilter;
    if (config.rowConceptIds && config.rowConceptIds.length > 0) {
      blob.rowConceptIds = config.rowConceptIds;
    }
    if (config.rowStates && Object.keys(config.rowStates).length > 0) {
      blob.rowStates = config.rowStates;
    }
    state.store.setViewElementState(diagramId, ROADMAP_MATRIX_CONFIG_KEY, {
      type: "object",
      ref: ROADMAP_MATRIX_CONFIG_KEY,
      style: blob,
    });
    bumpRevision();
    schedulePersistDiagram(state, diagramId, workspaceId, branchId);
  },

  setDiagramBoxStyle: (workspaceId, branchId, diagramId, boxKey, patch) => {
    const state = get();
    const diagram = state.store.getDiagram(diagramId);
    if (!diagram) return;
    const nextViewState = writeBoxStyle(diagram.viewState, boxKey, patch);
    const entry = nextViewState[DIAGRAM_BOX_STYLES_KEY];
    if (entry) state.store.setViewElementState(diagramId, DIAGRAM_BOX_STYLES_KEY, entry);
    bumpRevision();
    schedulePersistDiagram(state, diagramId, workspaceId, branchId);
  },

  setDiagramAttribute: (workspaceId, branchId, diagramId, name, value) => {
    const state = get();
    const diagram = state.store.getDiagram(diagramId);
    if (!diagram) return;
    const nextAttrs = { ...(diagram.attributes ?? {}) };
    if (value === null || value === undefined || value === "") {
      delete nextAttrs[name];
    } else {
      nextAttrs[name] = value;
    }
    state.store.upsertDiagram({ ...diagram, attributes: nextAttrs });
    bumpRevision();
    schedulePersistDiagram(state, diagramId, workspaceId, branchId);
  },

  // ---- Custom view-kinds -----------------------------------------------

  createCustomViewKind: async (workspaceId, body) => {
    const created = await api.createCustomViewKind(workspaceId, body);
    set((s) => ({ customViewKinds: [...s.customViewKinds, created] }));
    getRegistry().registerViewKind(customViewKindToViewKind(created));
    bumpRevision();
    return created;
  },

  updateCustomViewKind: async (workspaceId, id, body) => {
    const updated = await api.patchCustomViewKind(workspaceId, id, body);
    set((s) => ({
      customViewKinds: s.customViewKinds.map((v) => (v.id === id ? updated : v)),
    }));
    getRegistry().registerViewKind(customViewKindToViewKind(updated));
    bumpRevision();
    return updated;
  },

  deleteCustomViewKind: async (workspaceId, id) => {
    await api.deleteCustomViewKind(workspaceId, id);
    set((s) => ({ customViewKinds: s.customViewKinds.filter((v) => v.id !== id) }));
    getRegistry().unregisterViewKind(id);
    // Diagrams that referenced the deleted view-kind were migrated to
    // `archimate.anything` server-side. Mirror that locally so the
    // canvas + diagram-tabs reflect the new state without a re-hydrate.
    const store = get().store;
    store.transaction(() => {
      for (const d of store.listDiagrams()) {
        if (d.viewKind === id) {
          store.upsertDiagram({ ...d, viewKind: "archimate.anything" });
        }
      }
    });
  },

  createFacetConcept: async (workspaceId, branchId, input) => {
    const concept = await api.createConcept(workspaceId, branchId, {
      type_id: input.typeId,
      name: input.name,
      ...(input.description ? { description: input.description } : {}),
    });
    sharedUndo.apply(upsertConceptCommand(get().store, concept));
    bumpRevision();
    return concept;
  },

  deleteDiagram: async (workspaceId, branchId, diagramId) => {
    const state = get();
    const all = state.store.listDiagrams();
    if (all.length <= 1) {
      set({ lastError: "Het laatste diagram kan niet worden verwijderd." });
      return;
    }
    const removed = state.store.getDiagram(diagramId);
    if (!removed) return;
    // Pick a fallback active diagram (first surviving one).
    const fallback = all.find((d) => d.id !== diagramId);
    state.store.removeDiagram(diagramId);
    set((s) => {
      const nextOpen = s.openDiagramIds.filter((id) => id !== diagramId);
      writeOpenDiagramIds(workspaceId, nextOpen);
      const nextActive =
        s.activeDiagramId === diagramId ? (fallback?.id ?? null) : s.activeDiagramId;
      return { openDiagramIds: nextOpen, activeDiagramId: nextActive };
    });
    const wasActive = state.activeDiagramId === diagramId;
    try {
      await api.deleteDiagram(workspaceId, branchId, diagramId);
      // Undo: herstel het diagram met DEZELFDE id via de restore-endpoint
      // (un-tombstone een geërfd diagram, of her-insert een hard-verwijderd
      // branch-eigen diagram). Zo blijft de id stabiel (geen duplicaat).
      get().pushUserUndo({
        label: "Verwijder diagram",
        undo: async () => {
          await api.restoreDiagram(workspaceId, branchId, diagramId, {
            name: removed.name,
            notation_pack: removed.notationPack,
            view_kind: removed.viewKind,
            view_state: removed.viewState,
            attributes: removed.attributes,
            ...(removed.description ? { description: removed.description } : {}),
          });
          get().store.upsertDiagram(removed);
          set((s) => {
            const nextOpen = s.openDiagramIds.includes(diagramId)
              ? s.openDiagramIds
              : [...s.openDiagramIds, diagramId];
            writeOpenDiagramIds(workspaceId, nextOpen);
            return {
              openDiagramIds: nextOpen,
              activeDiagramId: wasActive ? diagramId : s.activeDiagramId,
            };
          });
        },
        redo: async () => {
          get().store.removeDiagram(diagramId);
          set((s) => {
            const nextOpen = s.openDiagramIds.filter((d) => d !== diagramId);
            writeOpenDiagramIds(workspaceId, nextOpen);
            return {
              openDiagramIds: nextOpen,
              activeDiagramId:
                s.activeDiagramId === diagramId ? (fallback?.id ?? null) : s.activeDiagramId,
            };
          });
          await api.deleteDiagram(workspaceId, branchId, diagramId);
        },
      });
    } catch (err) {
      // Restore on failure.
      state.store.upsertDiagram(removed);
      set((s) => {
        const restored = s.openDiagramIds.includes(diagramId)
          ? s.openDiagramIds
          : [...s.openDiagramIds, diagramId];
        writeOpenDiagramIds(workspaceId, restored);
        return {
          openDiagramIds: restored,
          activeDiagramId: state.activeDiagramId,
          lastError: explainError(err),
        };
      });
    }
  },

  ensureDefaultDiagram: async (workspaceId, branchId) => {
    const existing = get().store.listDiagrams();
    if (existing[0]) {
      set({ activeDiagramId: existing[0].id });
      return existing[0];
    }
    const created = await api.createDiagram(workspaceId, branchId, {
      name: { nl: "Hoofddiagram", en: "Main diagram" },
      notation_pack: "archimate",
      // The default diagram is free-form: every type from the palette is
      // allowed. Specific viewpoint diagrams can be created via the
      // diagrams panel + view-kind picker.
      view_kind: "archimate.anything",
    });
    sharedUndo.apply(upsertDiagramCommand(get().store, created));
    set({ activeDiagramId: created.id });
    return created;
  },

  addConceptOnCanvas: async (
    workspaceId,
    branchId,
    typeId,
    name,
    position,
    attributes,
    stereotypes,
  ) => {
    const session = { ownerEmail: getOwnerEmail() };
    // Merge sensible defaults for required attributes the caller didn't
    // supply. This catches drag-drop from the regular palette (where the
    // entry has no `default_attributes`) and text-paste creates (where
    // applyParseResult only knows the typeId + label) — both would otherwise
    // hit the worker's "1 field(s) invalid" validator. C4 palette entries
    // already pass `c4_level` explicitly; their value wins via the merge.
    const merged: Record<string, unknown> = {
      ...defaultAttributesForType(typeId, stereotypes),
      ...(attributes ?? {}),
    };
    // Virtuele velden (storage_mode: "concept_description") gaan naar
    // concept.description, niet naar attributes — anders blijft de slot
    // op canvas + AttributePanel leeg terwijl de default in attributes
    // huist. Pak de NTB/TBD-default uit de merge, verplaats naar
    // top-level description, en strip de attribuut-key.
    let defaultDescription: { nl?: string; en?: string } | undefined;
    if (stereotypes && stereotypes.length > 0) {
      const reg = getRegistry();
      for (const stId of stereotypes) {
        const st = reg.getStereotype(stId);
        if (!st) continue;
        const attrs = reg.resolveStereotypeAttributes(st);
        for (const attr of attrs) {
          if (attr.storage_mode !== "concept_description") continue;
          const v = merged[attr.name];
          if (v && typeof v === "object" && !Array.isArray(v) && !defaultDescription) {
            defaultDescription = v as { nl?: string; en?: string };
          }
          delete merged[attr.name];
        }
      }
    }
    const finalAttributes = Object.keys(merged).length > 0 ? merged : undefined;
    const finalStereotypes = stereotypes && stereotypes.length > 0 ? stereotypes : undefined;
    const optimistic = createConcept({
      typeId,
      name: { ...name },
      ownerEmail: session.ownerEmail,
      ...(defaultDescription
        ? { description: { nl: defaultDescription.nl ?? "", en: defaultDescription.en ?? "" } }
        : {}),
      ...(finalAttributes ? { attributes: finalAttributes } : {}),
      ...(finalStereotypes ? { stereotypes: finalStereotypes } : {}),
    });
    sharedUndo.apply(upsertConceptCommand(get().store, optimistic));
    placeOnDiagram(get(), optimistic.id, position, workspaceId, branchId);

    try {
      const persisted = await api.createConcept(workspaceId, branchId, {
        type_id: typeId,
        name,
        ...(defaultDescription
          ? {
              description: {
                nl: defaultDescription.nl ?? "",
                en: defaultDescription.en ?? "",
              },
            }
          : {}),
        ...(finalAttributes ? { attributes: finalAttributes } : {}),
        ...(finalStereotypes ? { stereotypes: finalStereotypes } : {}),
      });
      // Replace the temp UUID with the server-issued one (cascade to view-state too).
      replaceConceptId(get(), optimistic.id, persisted, workspaceId, branchId);
      // User-undo: removing the concept on undo, recreating on redo.
      pushAddConceptUndo(workspaceId, branchId, persisted, position);
      // Duplicaat-detectie op AANMAKEN: heeft 't verse concept een naam die +
      // type + stereotypes matcht met een bestaand → samenvoeg-vraag. Named-only
      // (een naamloos concept triggert niets). Fire-and-forget.
      void get().checkConceptDuplicate(workspaceId, branchId, persisted.id);
      return persisted;
    } catch (err) {
      sharedUndo.undo();
      set({ lastError: explainError(err) });
      throw err;
    }
  },

  placeExistingConceptOnDiagram: (workspaceId, branchId, conceptId, position) => {
    const state = get();
    const diagramId = state.activeDiagramId;
    if (!diagramId) return null;
    const concept = state.store.getConcept(conceptId);
    if (!concept) return null;
    const diagram = state.store.getDiagram(diagramId);
    const canonical = vsGet(diagram?.viewState ?? {}, conceptId);
    let instanceId: string;
    let nextEntry: ViewElementState;
    if (canonical?.position === undefined) {
      // First placement on this diagram → use conceptId as the canonical
      // view-state key (no `conceptRef` needed).
      instanceId = conceptId;
      nextEntry = {
        ...(canonical ?? { type: "object", ref: conceptId }),
        type: "object",
        ref: conceptId,
        position,
      };
    } else {
      // Concept is already placed — drag-and-drop creates an additional
      // instance with its own key + `ref` pointing back to the concept.
      // ArchiMate diagrams use this to keep lines from crossing by
      // repeating the same element.
      instanceId = crypto.randomUUID();
      nextEntry = { type: "object", ref: conceptId, position };
    }
    // Bewaar de vorige entry op deze key zodat Cmd+Z de plaatsing precies
    // terugdraait (nieuwe instance → verwijderen; canonical → vorige staat
    // herstellen). Zonder dit was een gesleepte bestaande-concept-plaatsing
    // niet ongedaan te maken.
    const prevEntry = state.store.getDiagram(diagramId)?.viewState[instanceId];
    state.store.setViewElementState(diagramId, instanceId, nextEntry);
    schedulePersistDiagram(state, diagramId, workspaceId, branchId);
    get().pushUserUndo({
      label: "Object plaatsen",
      undo: () => {
        const s = get();
        if (prevEntry === undefined) s.store.removeViewElementState(diagramId, instanceId);
        else s.store.setViewElementState(diagramId, instanceId, prevEntry);
        schedulePersistDiagram(s, diagramId, workspaceId, branchId);
      },
      redo: () => {
        const s = get();
        s.store.setViewElementState(diagramId, instanceId, nextEntry);
        schedulePersistDiagram(s, diagramId, workspaceId, branchId);
      },
    });
    return instanceId;
  },

  placeExistingConceptsOnDiagram: (workspaceId, branchId, items) => {
    const state = get();
    const diagramId = state.activeDiagramId;
    if (!diagramId) return [];
    const placements: {
      instanceId: string;
      prevEntry: ViewElementState | undefined;
      nextEntry: ViewElementState;
    }[] = [];
    for (const { conceptId, position } of items) {
      const concept = state.store.getConcept(conceptId);
      if (!concept) continue;
      // Verse viewState per iteratie — vorige plaatsingen tellen mee voor de
      // canonical-vs-extra-instance-keuze.
      const canonical = vsGet(state.store.getDiagram(diagramId)?.viewState ?? {}, conceptId);
      let instanceId: string;
      let nextEntry: ViewElementState;
      if (canonical?.position === undefined) {
        instanceId = conceptId;
        nextEntry = {
          ...(canonical ?? { type: "object", ref: conceptId }),
          type: "object",
          ref: conceptId,
          position,
        };
      } else {
        instanceId = crypto.randomUUID();
        nextEntry = { type: "object", ref: conceptId, position };
      }
      const prevEntry = state.store.getDiagram(diagramId)?.viewState[instanceId];
      state.store.setViewElementState(diagramId, instanceId, nextEntry);
      placements.push({ instanceId, prevEntry, nextEntry });
    }
    if (placements.length === 0) return [];
    schedulePersistDiagram(state, diagramId, workspaceId, branchId);
    // ÉÉN gecombineerde undo-entry voor de hele bulk-drop.
    get().pushUserUndo({
      label: "Objecten plaatsen",
      undo: () => {
        const s = get();
        for (const p of placements) {
          if (p.prevEntry === undefined) s.store.removeViewElementState(diagramId, p.instanceId);
          else s.store.setViewElementState(diagramId, p.instanceId, p.prevEntry);
        }
        schedulePersistDiagram(s, diagramId, workspaceId, branchId);
      },
      redo: () => {
        const s = get();
        for (const p of placements) {
          s.store.setViewElementState(diagramId, p.instanceId, p.nextEntry);
        }
        schedulePersistDiagram(s, diagramId, workspaceId, branchId);
      },
    });
    return placements.map((p) => p.instanceId);
  },

  placeConceptInSlot: (workspaceId, branchId, conceptId, slotId, orderInSlot) => {
    const state = get();
    const diagramId = state.activeDiagramId;
    if (!diagramId) return null;
    const concept = state.store.getConcept(conceptId);
    if (!concept) return null;
    const diagram = state.store.getDiagram(diagramId);
    // findEntryKeyForEntity = silent lookup. Vindt entry via ref-first,
    // valt terug op direct-key-match voor canonical entries. Bij geen
    // bestaande entry → conceptId als key (canonical pattern).
    const viewState = diagram?.viewState ?? {};
    const existingKey = findEntryKeyForEntity(viewState, conceptId);
    const instanceId = existingKey ?? conceptId;
    const baseEntry =
      existingKey !== null
        ? { ...viewState[existingKey] }
        : { type: "object" as const, ref: conceptId };
    // Strip free-form-velden zodat de canvas-renderer de positie puur uit
    // de slot afleidt (mutually exclusive per ViewElementState-spec).
    // type/ref NA de baseEntry-spread zodat ze de baseEntry overschrijven
    // (bv. een stale ref uit replaceConceptId's id-cascade).
    const next: ViewElementState = {
      ...baseEntry,
      type: "object",
      ref: conceptId,
      slotId,
      orderInSlot,
    };
    delete next.position;
    delete next.width;
    delete next.height;
    state.store.setViewElementState(diagramId, instanceId, next);
    schedulePersistDiagram(state, diagramId, workspaceId, branchId);
    return instanceId;
  },

  placeExistingRelationOnDiagram: (workspaceId, branchId, relationId, options) => {
    const state = get();
    const diagramId = state.activeDiagramId;
    if (!diagramId) return "missing-endpoint";
    const diagram = state.store.getDiagram(diagramId);
    if (!diagram) return "missing-endpoint";
    const relation = state.store.getRelation(relationId);
    if (!relation || relation.tombstoneInBranch) return "missing-endpoint";
    // Both endpoints must already have at least one instance on this
    // diagram — otherwise React Flow has nothing to anchor to.
    const conceptOnDiagram = (conceptId: string): boolean => {
      for (const [key, ve] of Object.entries(diagram.viewState)) {
        if (ve.position === undefined) continue;
        if (ve.hidden === true) continue;
        if ((ve.ref ?? key) === conceptId) return true;
      }
      return false;
    };
    if (
      !conceptOnDiagram(relation.sourceConceptId) ||
      !conceptOnDiagram(relation.targetConceptId)
    ) {
      return "missing-endpoint";
    }
    const hasInstancePins = !!(options?.sourceInstanceId || options?.targetInstanceId);
    // Multi-instance-refactor (2026-05-26): twee paden:
    //
    // 1. **Geen instance-pins** → "canonical placement"-pad (legacy).
    //    Plaats de relatie onder z'n eigen relationId-key zodat de relatie
    //    op dit diagram zichtbaar wordt. Als de canonical entry al bestaat
    //    → "already" (no-op).
    //
    // 2. **Met instance-pins** → ALTIJD een NIEUWE viewState-instance
    //    creëren (UUID-key, type="relation", ref=relationId,
    //    sourceInstanceId/targetInstanceId gevuld). Dit is het Feature-A-
    //    pad: drag een relatie uit het paneel naar een SPECIFIEKE
    //    object-instance → maak een nieuwe relatie-instance tussen die
    //    object-instance en de near-matching andere endpoint-instance.
    //    Geen update meer van een bestaande entry — elke drop = nieuwe
    //    visualisatie.
    if (!hasInstancePins) {
      const existingVe = vsGet(diagram.viewState, relationId);
      if (existingVe !== undefined) return "already";
      const newEntry: ViewElementState = {
        type: "relation",
        ref: relationId,
      };
      state.store.setViewElementState(diagramId, relationId, newEntry);
      schedulePersistDiagram(state, diagramId, workspaceId, branchId);
      return "placed";
    }
    // Pinned: nieuwe instance.
    const instanceKey = options?.entryKey ?? crypto.randomUUID();
    const newEntry: ViewElementState = {
      type: "relation",
      ref: relationId,
      ...(options?.sourceInstanceId ? { sourceInstanceId: options.sourceInstanceId } : {}),
      ...(options?.targetInstanceId ? { targetInstanceId: options.targetInstanceId } : {}),
    };
    state.store.setViewElementState(diagramId, instanceKey, newEntry);
    schedulePersistDiagram(state, diagramId, workspaceId, branchId);
    return "placed";
  },

  nestOnDiagram: (workspaceId, branchId, childInstanceId, parentInstanceId, relativePosition) => {
    const state = get();
    const diagramId = state.activeDiagramId;
    if (!diagramId) return { hiddenCount: 0 };
    const diagram = state.store.getDiagram(diagramId);
    if (!diagram) return { hiddenCount: 0 };
    const childVe = diagram.viewState[childInstanceId];
    const parentVe = diagram.viewState[parentInstanceId];
    if (!childVe || !parentVe) return { hiddenCount: 0 };

    // Defense-in-depth tegen orphan view_state-entries. Wanneer een
    // hard-delete buiten de UI om viewState heeft achtergelaten dat
    // niet meer naar een bestaand concept verwijst, mag auto-nest
    // het ding niet als parent kiezen — dat zou React Flow een
    // `parentId` geven naar een node die niet bestaat (warning +
    // child springt naar canvas-midden). Hard-delete-pad in
    // `purgeItems` ruimt deze entries normaal op; deze check is een
    // defense-in-depth voor het pad waar dat tóch niet gelukt is.
    const childConceptId = childVe.ref ?? childInstanceId;
    const parentConceptId = parentVe.ref ?? parentInstanceId;
    if (!state.store.getConcept(childConceptId)) return { hiddenCount: 0 };
    if (!state.store.getConcept(parentConceptId)) return { hiddenCount: 0 };

    // Snapshot the child's pre-nest state so Cmd+Z can restore both the
    // containerId and the absolute (non-parent-relative) position.
    const beforeChild: typeof childVe = { ...childVe };

    state.store.setViewElementState(diagramId, childInstanceId, {
      ...childVe,
      containerId: parentInstanceId,
      position: relativePosition,
    });

    // Default-uitlijning bij het eerste nesten van een Attribute/Identifier
    // onder een Concept op een sharp.detailed-concept view: zet de tekst-
    // uitlijning van de parent op LINKS (UML-conventie voor attribuut-
    // compartimenten) — maar alleen wanneer de gebruiker nog geen
    // expliciete uitlijning koos (`labelTextAlign` undefined). Zo blijft
    // het een vrij wijzigbare default via de toolbar.
    {
      const childConcept = state.store.getConcept(childConceptId);
      const isCompartmentChild =
        childConcept?.typeId === "sharp.Attribute" || childConcept?.typeId === "sharp.Identifier";
      if (diagram.viewKind === "sharp.detailed-concept" && isCompartmentChild) {
        const parentVeNow = state.store.getDiagram(diagramId)?.viewState[parentInstanceId];
        if (parentVeNow && parentVeNow.nodeStyle?.labelTextAlign === undefined) {
          get().setNodeStyleOnCanvas(workspaceId, branchId, parentInstanceId, {
            labelTextAlign: "left",
          });
        }
        // Model-eigenaarschap vastleggen: een Attribuut/Identifier dat onder een
        // «Concept» (BusinessObject) wordt genest hoort er model-niveau bij —
        // zet parent_id zodat het eigenaarschap diagram-onafhankelijk is (niet
        // alleen visuele viewState-nesting). Re-parenten werkt via de ongelijk-
        // check; uit-nesten laat parent_id staan (eigendom blijft).
        const parentConcept = state.store.getConcept(parentConceptId);
        if (
          // Subtype-bewust: ook subtypes van BusinessObject.
          parentConcept &&
          getRegistry().effectiveTypeIdsFor(parentConcept.typeId).has("archimate.BusinessObject") &&
          childConcept?.parentId !== parentConceptId
        ) {
          void get().patchConcept(workspaceId, branchId, childConceptId, {
            parent_id: parentConceptId,
          });
        }
      }
    }

    // Hide ONLY relations whose rendered pair on this diagram matches
    // the nested instances. Other instances of the same concepts on the
    // same diagram (multi-instance) keep their relations visible.
    // (childConceptId / parentConceptId reusen we uit de orphan-check
    //  hierboven.)
    const incidentVisible = state.store
      .listRelations()
      .filter((r) => !r.tombstoneInBranch)
      .filter((r) => vsGet(diagram.viewState, r.id)?.hidden !== true)
      // Tekenlijnen blijven staan. Het verbergen bestaat omdat de visuele
      // nesting de relatie al uitdrukt: een compositie hoef je niet én als
      // lijn én als omhulling te tonen. Een `drawing.Line` drukt niets uit —
      // 'ie is een aanwijslijn of een streep, precies dat wat de modelleur
      // getekend heeft. Die weghalen omdat 'ie toevallig in het vlak eindigt
      // waar 'ie begon, wist het werk zelf (gemeld 2026-09-02).
      .filter((r) => !r.typeId.startsWith("drawing."))
      .filter((r) => relationConnectsConcepts(r, childConceptId, parentConceptId))
      .filter((r) =>
        renderedPairMatches(
          diagram,
          vsGet(diagram.viewState, r.id),
          r,
          childInstanceId,
          parentInstanceId,
        ),
      );
    for (const r of incidentVisible) {
      const ve = vsPatchEntry(vsGet(diagram.viewState, r.id), r.id, "relation");
      // Markeer als auto-nest-hide zodat toggleCollapseContainer's
      // auto-restore-pas alleen déze terug aanzet bij EXPAND, niet
      // user-hides via Delete-on-edge.
      state.store.setViewElementState(diagramId, r.id, {
        ...ve,
        hidden: true,
        hiddenByAutoNest: true,
      });
    }

    schedulePersistDiagram(state, diagramId, workspaceId, branchId);

    const hiddenIds = incidentVisible.map((r) => r.id);
    const afterChild: typeof childVe = {
      ...childVe,
      containerId: parentInstanceId,
      position: relativePosition,
    };
    get().pushUserUndo({
      label: "Inschuiven",
      undo: () => {
        const s = get();
        s.store.setViewElementState(diagramId, childInstanceId, beforeChild);
        for (const id of hiddenIds) {
          const ve = vsPatchEntry(s.store.getDiagram(diagramId)?.viewState[id], id, "relation");
          const next = { ...ve };
          delete next.hidden;
          delete next.hiddenByAutoNest;
          s.store.setViewElementState(diagramId, id, next);
        }
        schedulePersistDiagram(s, diagramId, workspaceId, branchId);
      },
      redo: () => {
        const s = get();
        s.store.setViewElementState(diagramId, childInstanceId, afterChild);
        for (const id of hiddenIds) {
          const ve = vsPatchEntry(s.store.getDiagram(diagramId)?.viewState[id], id, "relation");
          s.store.setViewElementState(diagramId, id, {
            ...ve,
            hidden: true,
            hiddenByAutoNest: true,
          });
        }
        schedulePersistDiagram(s, diagramId, workspaceId, branchId);
      },
    });

    return { hiddenCount: incidentVisible.length };
  },

  autoDetectNestingOnDiagram: (workspaceId, branchId, scope = "active") => {
    const state = get();
    // Doel-diagrammen: enkel het actief geopende of alle op deze branch.
    // De workspace-store bevat per definitie alleen diagrammen van de
    // actief gehydrateerde branch — `listDiagrams()` returnt dus
    // automatisch de juiste set voor de actieve branch. Geen extra
    // branch-filter nodig (Diagram-type heeft geen branchId-veld
    // op frontend-niveau).
    const diagramIds: string[] =
      scope === "all"
        ? state.store.listDiagrams().map((d) => d.id)
        : state.activeDiagramId
          ? [state.activeDiagramId]
          : [];

    let totalNested = 0;
    let diagramsAffected = 0;

    type Box = {
      key: string;
      absX: number;
      absY: number;
      width: number;
      height: number;
      area: number;
    };

    for (const dId of diagramIds) {
      const diagram = state.store.getDiagram(dId);
      if (!diagram) continue;
      const boxes: Box[] = [];
      const absOf = (k: string): { x: number; y: number } | null => {
        const ve = diagram.viewState[k];
        if (!ve?.position) return null;
        let x = ve.position.x;
        let y = ve.position.y;
        let cur = ve;
        while (cur.containerId) {
          const parent = diagram.viewState[cur.containerId];
          if (!parent?.position) break;
          x += parent.position.x;
          y += parent.position.y;
          cur = parent;
        }
        return { x, y };
      };
      for (const [k, ve] of Object.entries(diagram.viewState)) {
        if (!ve.position) continue;
        const abs = absOf(k);
        if (!abs) continue;
        const w = ve.width ?? 140;
        const h = ve.height ?? 60;
        boxes.push({ key: k, absX: abs.x, absY: abs.y, width: w, height: h, area: w * h });
      }
      let nestedThis = 0;
      for (const box of boxes) {
        const ve = diagram.viewState[box.key];
        if (!ve || ve.containerId) continue;
        const cx = box.absX + box.width / 2;
        const cy = box.absY + box.height / 2;
        let candidate: Box | null = null;
        for (const other of boxes) {
          if (other.key === box.key) continue;
          if (other.area <= box.area) continue;
          if (
            cx >= other.absX &&
            cx <= other.absX + other.width &&
            cy >= other.absY &&
            cy <= other.absY + other.height
          ) {
            if (!candidate || other.area < candidate.area) candidate = other;
          }
        }
        if (!candidate) continue;
        const relative = {
          x: box.absX - candidate.absX,
          y: box.absY - candidate.absY,
        };
        state.store.setViewElementState(dId, box.key, {
          ...ve,
          containerId: candidate.key,
          position: relative,
        });
        nestedThis += 1;
      }
      if (nestedThis > 0) {
        schedulePersistDiagram(state, dId, workspaceId, branchId);
        totalNested += nestedThis;
        diagramsAffected += 1;
      }
    }
    if (totalNested > 0) bumpRevision();
    return { nestedCount: totalNested, diagramsAffected };
  },

  unnestOnDiagram: (workspaceId, branchId, childInstanceId, absolutePosition) => {
    const state = get();
    const diagramId = state.activeDiagramId;
    if (!diagramId) return { previouslyHiddenRelationIds: [] };
    const diagram = state.store.getDiagram(diagramId);
    if (!diagram) return { previouslyHiddenRelationIds: [] };
    const childVe = diagram.viewState[childInstanceId];
    if (!childVe) return { previouslyHiddenRelationIds: [] };
    const parentInstanceId = childVe.containerId;

    // Snapshot pre-unnest state. The child's `position` was relative to
    // the parent; on undo we MUST restore that exact relative position
    // along with the containerId, otherwise the canvas paints the child
    // somewhere else (the relative coords are interpreted as absolute
    // when there's no parent).
    const beforeChild: typeof childVe = { ...childVe };

    state.store.setViewElementState(diagramId, childInstanceId, {
      ...childVe,
      containerId: undefined,
      position: absolutePosition,
    });

    // Find hidden relations whose rendered pair matched the unnested
    // pair (same per-instance scope as nestOnDiagram applied on hide).
    // Relations pointing at OTHER instances stay hidden, untouched.
    let hidden: string[] = [];
    if (parentInstanceId) {
      const childConceptId = childVe.ref ?? childInstanceId;
      const parentVe = diagram.viewState[parentInstanceId];
      const parentConceptId = parentVe?.ref ?? parentInstanceId;
      hidden = state.store
        .listRelations()
        .filter((r) => !r.tombstoneInBranch)
        .filter((r) => vsGet(diagram.viewState, r.id)?.hidden === true)
        .filter((r) => relationConnectsConcepts(r, childConceptId, parentConceptId))
        .filter((r) =>
          renderedPairMatches(
            diagram,
            vsGet(diagram.viewState, r.id),
            r,
            childInstanceId,
            parentInstanceId,
          ),
        )
        .map((r) => r.id);
    }

    schedulePersistDiagram(state, diagramId, workspaceId, branchId);

    const afterChild: typeof childVe = {
      ...childVe,
      containerId: undefined,
      position: absolutePosition,
    };
    get().pushUserUndo({
      label: "Uitschuiven",
      undo: () => {
        const s = get();
        s.store.setViewElementState(diagramId, childInstanceId, beforeChild);
        schedulePersistDiagram(s, diagramId, workspaceId, branchId);
      },
      redo: () => {
        const s = get();
        s.store.setViewElementState(diagramId, childInstanceId, afterChild);
        schedulePersistDiagram(s, diagramId, workspaceId, branchId);
      },
    });

    return { previouslyHiddenRelationIds: hidden };
  },

  unhideRelationOnDiagram: (workspaceId, branchId, relationId) => {
    const state = get();
    const diagramId = state.activeDiagramId;
    if (!diagramId) return;
    const diagram = state.store.getDiagram(diagramId);
    const ve = vsGet(diagram?.viewState ?? {}, relationId);
    if (ve?.hidden !== true) return;
    const next = { ...ve };
    delete next.hidden;
    delete next.hiddenByAutoNest;
    state.store.setViewElementState(diagramId, relationId, next);
    schedulePersistDiagram(state, diagramId, workspaceId, branchId);
  },

  resizeNodeOnCanvas: (workspaceId, branchId, nodeId, size) => {
    const state = get();
    const diagramId = state.activeDiagramId;
    if (!diagramId) return;
    const diagram = state.store.getDiagram(diagramId);
    if (!diagram) return;
    const ve = diagram.viewState[nodeId];
    if (!ve) return;
    if (ve.width === size.width && ve.height === size.height) return;
    const previousSize = { width: ve.width, height: ve.height };
    state.store.setViewElementState(diagramId, nodeId, {
      ...ve,
      width: size.width,
      height: size.height,
    });
    schedulePersistDiagram(state, diagramId, workspaceId, branchId);

    const applySize = (s: { width: number | undefined; height: number | undefined }) => {
      const st = get();
      const cur = vsPatchEntry(st.store.getDiagram(diagramId)?.viewState[nodeId], nodeId, "object");
      const next: ViewElementState = { ...cur };
      if (s.width === undefined) delete next.width;
      else next.width = s.width;
      if (s.height === undefined) delete next.height;
      else next.height = s.height;
      st.store.setViewElementState(diagramId, nodeId, next);
      schedulePersistDiagram(st, diagramId, workspaceId, branchId);
    };
    // Initiële autometing van een vers geplaatst kaartje (nog geen
    // width/height in viewState) is GEEN gebruikersactie — persist 'm wel,
    // maar push geen undo-entry. Anders stapelt 'ie bovenop de plaatsing-
    // undo en kost het twee keer Cmd+Z om een gesleept object weg te halen.
    const isInitialAutoMeasure =
      previousSize.width === undefined && previousSize.height === undefined;
    if (!isInitialAutoMeasure) {
      get().pushUserUndo({
        label: "Vergroot/verklein",
        undo: () => applySize(previousSize),
        redo: () => applySize(size),
      });
    }
  },

  moveConceptOnCanvas: (workspaceId, branchId, conceptId, position) => {
    const state = get();
    const diagramId = state.activeDiagramId;
    if (!diagramId) {
      return;
    }
    const existing = vsPatchEntry(
      vsGet(state.store.getDiagram(diagramId)?.viewState ?? {}, conceptId),
      conceptId,
      "object",
    );
    const previousPosition = existing.position;
    // No-op move (zelfde positie) → niets doen. Gebeurt o.a. wanneer de
    // nest-detectie ná een drop de al-gezette positie "herbevestigt"; zonder
    // deze guard stapelt dat een spurieuze "Verplaats concept"-undo bovenop
    // de plaatsing-undo → twee keer Cmd+Z nodig om een gesleept object weg te halen.
    if (
      previousPosition &&
      previousPosition.x === position.x &&
      previousPosition.y === position.y
    ) {
      return;
    }
    state.store.setViewElementState(diagramId, conceptId, { ...existing, position });
    schedulePersistDiagram(state, diagramId, workspaceId, branchId);

    // Record an undo entry only when we have a prior position to revert to.
    // The entries inline the mutation rather than recursing through this
    // action so undo/redo doesn't push another entry onto the stack.
    if (previousPosition) {
      const applyMove = (pos: { x: number; y: number }) => {
        const s = get();
        const ve = vsPatchEntry(
          vsGet(s.store.getDiagram(diagramId)?.viewState ?? {}, conceptId),
          conceptId,
          "object",
        );
        s.store.setViewElementState(diagramId, conceptId, { ...ve, position: pos });
        schedulePersistDiagram(s, diagramId, workspaceId, branchId);
      };
      get().pushUserUndo({
        label: "Verplaats concept",
        undo: () => applyMove(previousPosition),
        redo: () => applyMove(position),
        viewStateKeys: [conceptId],
      });
    }
  },

  setViewElementTableOrder: (workspaceId, branchId, elementKey, tableOrder) => {
    const state = get();
    const diagramId = state.activeDiagramId;
    if (!diagramId) return;
    // Vereist een bestaande entry — tableOrder zonder type/ref-context
    // zou een orphan-entry creëren. In de praktijk wordt deze setter
    // alleen aangeroepen vanuit GridPane op zichtbare rijen die per
    // definitie een entry hebben.
    const existing = state.store.getDiagram(diagramId)?.viewState[elementKey];
    if (!existing) return;
    state.store.setViewElementState(diagramId, elementKey, { ...existing, tableOrder });
    schedulePersistDiagram(state, diagramId, workspaceId, branchId);
  },

  setRelationHandlesOnCanvas: (workspaceId, branchId, relationId, sourceHandle, targetHandle) => {
    const state = get();
    const diagramId = state.activeDiagramId;
    if (!diagramId) {
      return;
    }
    const existing = vsPatchEntry(
      vsGet(state.store.getDiagram(diagramId)?.viewState ?? {}, relationId),
      relationId,
      "relation",
    );
    const before: ViewElementState = { ...existing };
    const next: ViewElementState = { ...existing };
    if (sourceHandle) next.sourceHandle = sourceHandle;
    else next.sourceHandle = undefined;
    if (targetHandle) next.targetHandle = targetHandle;
    else next.targetHandle = undefined;
    state.store.setViewElementState(diagramId, relationId, next);
    schedulePersistDiagram(state, diagramId, workspaceId, branchId);

    const apply = (snapshot: ViewElementState) => {
      const s = get();
      s.store.setViewElementState(diagramId, relationId, snapshot);
      schedulePersistDiagram(s, diagramId, workspaceId, branchId);
    };
    get().pushUserUndo({
      label: "Lijn-aanhechting wijzigen",
      undo: () => apply(before),
      redo: () => apply(next),
    });
  },

  setRelationAnglesOnCanvas: (workspaceId, branchId, relationId, sourceAngle, targetAngle) => {
    const state = get();
    const diagramId = state.activeDiagramId;
    if (!diagramId) return;
    // findEntryKeyForEntity = silent variant van vsGet — geen dev-popup-
    // warning, zelfde ref-first lookup. Voor UUID-keyed entries vindt 't
    // de entry via ref; voor canonical entries via direct key-match. We
    // schrijven terug onder DEZELFDE key (geen duplicate-risico).
    const viewState = state.store.getDiagram(diagramId)?.viewState ?? {};
    const entryKey = findEntryKeyForEntity(viewState, relationId) ?? relationId;
    const existing = vsPatchEntryWithRef(viewState[entryKey], relationId, "relation");
    const before: ViewElementState = { ...existing };
    const next: ViewElementState = { ...existing };
    // null = explicit clear, undefined = leave alone, number = set.
    if (sourceAngle === null) next.sourceAngle = undefined;
    else if (sourceAngle !== undefined) next.sourceAngle = sourceAngle;
    if (targetAngle === null) next.targetAngle = undefined;
    else if (targetAngle !== undefined) next.targetAngle = targetAngle;
    state.store.setViewElementState(diagramId, entryKey, next);
    schedulePersistDiagram(state, diagramId, workspaceId, branchId);

    const apply = (snapshot: ViewElementState) => {
      const s = get();
      s.store.setViewElementState(diagramId, entryKey, snapshot);
      schedulePersistDiagram(s, diagramId, workspaceId, branchId);
    };
    get().pushUserUndo({
      label: "Lijn-anker-hoek wijzigen",
      undo: () => apply(before),
      redo: () => apply(next),
    });
  },

  setListboxItemsOnCanvas: (workspaceId, branchId, instanceId, items) => {
    const state = get();
    const diagramId = state.activeDiagramId;
    if (!diagramId) return;
    const viewState = state.store.getDiagram(diagramId)?.viewState ?? {};
    const existing = vsPatchEntry(vsGet(viewState, instanceId), instanceId, "object");
    const before: ViewElementState["listboxItems"] = existing.listboxItems
      ? existing.listboxItems.map((it) => ({ ...it }))
      : undefined;
    const after: ViewElementState["listboxItems"] = items.map((it) => ({
      id: it.id,
      kind: it.kind,
    }));
    const apply = (value: ViewElementState["listboxItems"] | undefined) => {
      const ws = useWorkspaceData.getState();
      const dId = ws.activeDiagramId;
      if (!dId) return;
      const cur = vsPatchEntry(
        vsGet(ws.store.getDiagram(dId)?.viewState ?? {}, instanceId),
        instanceId,
        "object",
      );
      const { listboxItems: _drop, ...rest } = cur;
      void _drop;
      const next: typeof cur =
        value && value.length > 0 ? { ...rest, listboxItems: value } : { ...rest };
      ws.store.setViewElementState(dId, instanceId, next);
      schedulePersistDiagram(ws, dId, workspaceId, branchId);
    };
    apply(after);
    state.pushUserUndo({
      label: "Lijstvak-inhoud",
      undo: () => apply(before),
      redo: () => apply(after),
    });
  },

  setRelationLineColorOnCanvas: (workspaceId, branchId, relationId, color) => {
    const state = get();
    const diagramId = state.activeDiagramId;
    if (!diagramId) return;
    const existing = vsPatchEntry(
      vsGet(state.store.getDiagram(diagramId)?.viewState ?? {}, relationId),
      relationId,
      "relation",
    );
    const before = existing.lineColor;
    const after = color === null ? undefined : color;
    if (before === after) return;
    const apply = (value: string | undefined) => {
      const ws = useWorkspaceData.getState();
      const dId = ws.activeDiagramId;
      if (!dId) return;
      const cur = vsPatchEntry(
        vsGet(ws.store.getDiagram(dId)?.viewState ?? {}, relationId),
        relationId,
        "relation",
      );
      const next: typeof cur = { ...cur, lineColor: value };
      ws.store.setViewElementState(dId, relationId, next);
      schedulePersistDiagram(ws, dId, workspaceId, branchId);
    };
    apply(after);
    state.pushUserUndo({
      label: "Lijnkleur",
      undo: () => apply(before),
      redo: () => apply(after),
    });
  },

  setRelationLineStyleOnCanvas: (workspaceId, branchId, relationId, style) => {
    const state = get();
    const diagramId = state.activeDiagramId;
    if (!diagramId) return;
    const existing = vsPatchEntry(
      vsGet(state.store.getDiagram(diagramId)?.viewState ?? {}, relationId),
      relationId,
      "relation",
    );
    const before = existing.lineStyle;
    const after = style === null ? undefined : style;
    if (before === after) return;
    const apply = (value: "solid" | "dashed" | "dotted" | "none" | undefined) => {
      const ws = useWorkspaceData.getState();
      const dId = ws.activeDiagramId;
      if (!dId) return;
      const cur = vsPatchEntry(
        vsGet(ws.store.getDiagram(dId)?.viewState ?? {}, relationId),
        relationId,
        "relation",
      );
      const next: typeof cur = { ...cur, lineStyle: value };
      ws.store.setViewElementState(dId, relationId, next);
      schedulePersistDiagram(ws, dId, workspaceId, branchId);
    };
    apply(after);
    state.pushUserUndo({
      label: "Lijnstijl",
      undo: () => apply(before),
      redo: () => apply(after),
    });
  },

  setRelationLabelBgColorOnCanvas: (workspaceId, branchId, relationId, color) => {
    const state = get();
    const diagramId = state.activeDiagramId;
    if (!diagramId) return;
    const existing = vsPatchEntry(
      vsGet(state.store.getDiagram(diagramId)?.viewState ?? {}, relationId),
      relationId,
      "relation",
    );
    const before = existing.labelBgColor;
    const after = color === null ? undefined : color;
    if (before === after) return;
    const apply = (value: string | undefined) => {
      const ws = useWorkspaceData.getState();
      const dId = ws.activeDiagramId;
      if (!dId) return;
      const cur = vsPatchEntry(
        vsGet(ws.store.getDiagram(dId)?.viewState ?? {}, relationId),
        relationId,
        "relation",
      );
      const next: typeof cur = { ...cur, labelBgColor: value };
      ws.store.setViewElementState(dId, relationId, next);
      schedulePersistDiagram(ws, dId, workspaceId, branchId);
    };
    apply(after);
    state.pushUserUndo({
      label: "Label-achtergrondkleur",
      undo: () => apply(before),
      redo: () => apply(after),
    });
  },

  setRelationLabelStyleOnCanvas: (workspaceId, branchId, relationId, patch) => {
    const state = get();
    const diagramId = state.activeDiagramId;
    if (!diagramId) return;
    const huidig = vsPatchEntry(
      vsGet(state.store.getDiagram(diagramId)?.viewState ?? {}, relationId),
      relationId,
      "relation",
    );
    // `undefined` = niet aangeraakt, `null` = wissen. Zonder dat onderscheid
    // zou een werkbalk die één veld zet de rest van de opmaak wissen.
    const volgende: Record<string, unknown> = { ...huidig };
    let gewijzigd = false;
    for (const [sleutel, waarde] of Object.entries(patch)) {
      if (waarde === undefined) continue;
      const nieuw = waarde === null ? undefined : waarde;
      if (volgende[sleutel] === nieuw) continue;
      if (nieuw === undefined) delete volgende[sleutel];
      else volgende[sleutel] = nieuw;
      gewijzigd = true;
    }
    if (!gewijzigd) return;
    state.store.setViewElementState(diagramId, relationId, volgende as unknown as ViewElementState);
    schedulePersistDiagram(state, diagramId, workspaceId, branchId);
  },

  setRelationLabelTextColorOnCanvas: (workspaceId, branchId, relationId, color) => {
    const state = get();
    const diagramId = state.activeDiagramId;
    if (!diagramId) return;
    const existing = vsPatchEntry(
      vsGet(state.store.getDiagram(diagramId)?.viewState ?? {}, relationId),
      relationId,
      "relation",
    );
    const before = existing.labelTextColor;
    const after = color === null ? undefined : color;
    if (before === after) return;
    const apply = (value: string | undefined) => {
      const ws = useWorkspaceData.getState();
      const dId = ws.activeDiagramId;
      if (!dId) return;
      const cur = vsPatchEntry(
        vsGet(ws.store.getDiagram(dId)?.viewState ?? {}, relationId),
        relationId,
        "relation",
      );
      const next: typeof cur = { ...cur, labelTextColor: value };
      ws.store.setViewElementState(dId, relationId, next);
      schedulePersistDiagram(ws, dId, workspaceId, branchId);
    };
    apply(after);
    state.pushUserUndo({
      label: "Label-tekstkleur",
      undo: () => apply(before),
      redo: () => apply(after),
    });
  },

  setRelationLabelWrapOnCanvas: (
    workspaceId: string,
    branchId: string,
    relationId: string,
    wrap: "hyphenate" | "break-anywhere" | null,
  ) => {
    const state = get();
    const diagramId = state.activeDiagramId;
    if (!diagramId) return;
    const existing = vsPatchEntry(
      vsGet(state.store.getDiagram(diagramId)?.viewState ?? {}, relationId),
      relationId,
      "relation",
    );
    const before = existing.labelWrap;
    const after = wrap === null ? undefined : wrap;
    if (before === after) return;
    const apply = (value: "hyphenate" | "break-anywhere" | undefined) => {
      const ws = useWorkspaceData.getState();
      const dId = ws.activeDiagramId;
      if (!dId) return;
      const cur = vsPatchEntry(
        vsGet(ws.store.getDiagram(dId)?.viewState ?? {}, relationId),
        relationId,
        "relation",
      );
      const next: typeof cur = { ...cur, labelWrap: value };
      ws.store.setViewElementState(dId, relationId, next);
      schedulePersistDiagram(ws, dId, workspaceId, branchId);
    };
    apply(after);
    state.pushUserUndo({
      label: "Label-afbreken",
      undo: () => apply(before),
      redo: () => apply(after),
    });
  },

  setRelationLabelTextAlignOnCanvas: (
    workspaceId: string,
    branchId: string,
    relationId: string,
    align: "left" | "center" | "right" | null,
  ) => {
    const state = get();
    const diagramId = state.activeDiagramId;
    if (!diagramId) return;
    const existing = vsPatchEntry(
      vsGet(state.store.getDiagram(diagramId)?.viewState ?? {}, relationId),
      relationId,
      "relation",
    );
    const before = existing.labelTextAlign;
    const after = align === null ? undefined : align;
    if (before === after) return;
    const apply = (value: "left" | "center" | "right" | undefined) => {
      const ws = useWorkspaceData.getState();
      const dId = ws.activeDiagramId;
      if (!dId) return;
      const cur = vsPatchEntry(
        vsGet(ws.store.getDiagram(dId)?.viewState ?? {}, relationId),
        relationId,
        "relation",
      );
      const next: typeof cur = { ...cur, labelTextAlign: value };
      ws.store.setViewElementState(dId, relationId, next);
      schedulePersistDiagram(ws, dId, workspaceId, branchId);
    };
    apply(after);
    state.pushUserUndo({
      label: "Label-uitlijning",
      undo: () => apply(before),
      redo: () => apply(after),
    });
  },

  setRelationLabelMaxWidthOnCanvas: (workspaceId, branchId, relationId, width) => {
    const state = get();
    const diagramId = state.activeDiagramId;
    if (!diagramId) return;
    const existing = vsPatchEntry(
      vsGet(state.store.getDiagram(diagramId)?.viewState ?? {}, relationId),
      relationId,
      "relation",
    );
    const before = existing.labelMaxWidth;
    const after = width === null ? undefined : Math.max(40, Math.round(width));
    if (before === after) return;
    const apply = (value: number | undefined) => {
      const ws = useWorkspaceData.getState();
      const dId = ws.activeDiagramId;
      if (!dId) return;
      const cur = vsPatchEntry(
        vsGet(ws.store.getDiagram(dId)?.viewState ?? {}, relationId),
        relationId,
        "relation",
      );
      const next: typeof cur = { ...cur, labelMaxWidth: value };
      ws.store.setViewElementState(dId, relationId, next);
      schedulePersistDiagram(ws, dId, workspaceId, branchId);
    };
    apply(after);
    state.pushUserUndo({
      label: "Label-breedte",
      undo: () => apply(before),
      redo: () => apply(after),
    });
  },

  setRelationEndLabelInsetOnCanvas: (workspaceId, branchId, relationId, side, inset) => {
    const state = get();
    const diagramId = state.activeDiagramId;
    if (!diagramId) return;
    const existing = vsPatchEntry(
      vsGet(state.store.getDiagram(diagramId)?.viewState ?? {}, relationId),
      relationId,
      "relation",
    );
    const key = side === "source" ? "sourceLabelInset" : "targetLabelInset";
    const before = existing[key];
    // Cap onder 4 (anders kruipt 't tegen de node-rand) en boven 400
    // zodat de twee labels zelfs door elkaar heen kunnen schuiven —
    // multi-line wrap + tegenovergestelde kanten van de lijn voorkomt
    // dat ze visueel botsen.
    const after = inset === null ? undefined : Math.max(4, Math.min(400, Math.round(inset)));
    if (before === after) return;
    const apply = (value: number | undefined) => {
      const ws = useWorkspaceData.getState();
      const dId = ws.activeDiagramId;
      if (!dId) return;
      const cur = vsPatchEntry(
        vsGet(ws.store.getDiagram(dId)?.viewState ?? {}, relationId),
        relationId,
        "relation",
      );
      const next: typeof cur = { ...cur, [key]: value };
      ws.store.setViewElementState(dId, relationId, next);
      schedulePersistDiagram(ws, dId, workspaceId, branchId);
    };
    apply(after);
    state.pushUserUndo({
      label: side === "source" ? "Forward-label positie" : "Reverse-label positie",
      undo: () => apply(before),
      redo: () => apply(after),
    });
  },

  setRelationEndLabelWidthOnCanvas: (workspaceId, branchId, relationId, side, width) => {
    // Per-call wordt GEEN undo-entry gepusht — caller (drag-handler in
    // RoutedEdge) pakt before/after en pusht één entry aan drag-eind via
    // pushUserUndo. Dat voorkomt dat een hele drag de undo-stack vult
    // met 60+ entries en geeft de gebruiker een logische 'één gesture =
    // één Cmd+Z'-ervaring.
    const state = get();
    const diagramId = state.activeDiagramId;
    if (!diagramId) return;
    const existing = vsPatchEntry(
      vsGet(state.store.getDiagram(diagramId)?.viewState ?? {}, relationId),
      relationId,
      "relation",
    );
    const key = side === "source" ? "sourceLabelWidth" : "targetLabelWidth";
    const before = existing[key];
    const after = width === null ? undefined : Math.max(30, Math.min(400, Math.round(width)));
    if (before === after) return;
    const cur = vsPatchEntry(
      vsGet(state.store.getDiagram(diagramId)?.viewState ?? {}, relationId),
      relationId,
      "relation",
    );
    const next: typeof cur = { ...cur, [key]: after };
    state.store.setViewElementState(diagramId, relationId, next);
    schedulePersistDiagram(state, diagramId, workspaceId, branchId);
  },

  setRelationEndLabelOffsetOnCanvas: (workspaceId, branchId, relationId, side, offset) => {
    // Idem — geen pushUserUndo per call; caller pusht één undo-entry
    // aan drag-eind.
    const state = get();
    const diagramId = state.activeDiagramId;
    if (!diagramId) return;
    const existing = vsPatchEntry(
      vsGet(state.store.getDiagram(diagramId)?.viewState ?? {}, relationId),
      relationId,
      "relation",
    );
    const key = side === "source" ? "sourceLabelOffset" : "targetLabelOffset";
    const before = existing[key];
    const after =
      offset === null ? undefined : { x: Math.round(offset.x), y: Math.round(offset.y) };
    if (before?.x === after?.x && before?.y === after?.y) return;
    const cur = vsPatchEntry(
      vsGet(state.store.getDiagram(diagramId)?.viewState ?? {}, relationId),
      relationId,
      "relation",
    );
    const next: typeof cur = { ...cur, [key]: after };
    state.store.setViewElementState(diagramId, relationId, next);
    schedulePersistDiagram(state, diagramId, workspaceId, branchId);
  },

  setRelationWaypointsOnCanvas: (workspaceId, branchId, relationId, waypoints) => {
    const state = get();
    const diagramId = state.activeDiagramId;
    if (!diagramId) return;
    const existing = vsPatchEntry(
      vsGet(state.store.getDiagram(diagramId)?.viewState ?? {}, relationId),
      relationId,
      "relation",
    );
    const before: typeof existing = { ...existing };
    const next: typeof existing = { ...existing };
    if (waypoints && waypoints.length > 0) {
      // Defensive copy so a later mutation by the caller doesn't bleed
      // into the store.
      next.waypoints = waypoints.map((p) => ({ x: p.x, y: p.y }));
      // First bend implicitly switches the routing to "manual" so the
      // dropdown reflects what the user is seeing.
      next.routing = "manual";
    } else {
      next.waypoints = undefined;
      // Clearing all waypoints on a manual line falls back to the
      // workspace default routing — drop the override.
      if (existing.routing === "manual") next.routing = undefined;
    }
    state.store.setViewElementState(diagramId, relationId, next);
    schedulePersistDiagram(state, diagramId, workspaceId, branchId);

    const apply = (snapshot: typeof existing) => {
      const s = get();
      s.store.setViewElementState(diagramId, relationId, snapshot);
      schedulePersistDiagram(s, diagramId, workspaceId, branchId);
    };
    get().pushUserUndo({
      label: "Lijn-knikpunten wijzigen",
      undo: () => apply(before),
      redo: () => apply(next),
    });
  },

  setRelationGeometryOnCanvas: (
    workspaceId,
    branchId,
    relationId,
    waypoints,
    sourceAngle,
    targetAngle,
  ) => {
    const state = get();
    const diagramId = state.activeDiagramId;
    if (!diagramId) return;
    const viewState = state.store.getDiagram(diagramId)?.viewState ?? {};
    const entryKey = findEntryKeyForEntity(viewState, relationId) ?? relationId;
    const existing = vsPatchEntryWithRef(viewState[entryKey], relationId, "relation");
    const before: ViewElementState = { ...existing };
    const next: ViewElementState = { ...existing };
    // Waypoints: null/[] = clear, [...] = set.
    if (waypoints && waypoints.length > 0) {
      next.waypoints = waypoints.map((p) => ({ x: p.x, y: p.y }));
      next.routing = "manual";
    } else {
      next.waypoints = undefined;
      if (existing.routing === "manual") next.routing = undefined;
    }
    // Angles: null = clear, undefined = leave alone, number = set.
    if (sourceAngle === null) next.sourceAngle = undefined;
    else if (sourceAngle !== undefined) next.sourceAngle = sourceAngle;
    if (targetAngle === null) next.targetAngle = undefined;
    else if (targetAngle !== undefined) next.targetAngle = targetAngle;
    state.store.setViewElementState(diagramId, entryKey, next);
    schedulePersistDiagram(state, diagramId, workspaceId, branchId);

    const apply = (snapshot: ViewElementState) => {
      const s = get();
      s.store.setViewElementState(diagramId, entryKey, snapshot);
      schedulePersistDiagram(s, diagramId, workspaceId, branchId);
    };
    get().pushUserUndo({
      label: "Lijn rechtmaken",
      undo: () => apply(before),
      redo: () => apply(next),
    });
  },

  setSwimlaneLaneHeightOnCanvas: (workspaceId, branchId, bewonerId, hoogte) => {
    const state = get();
    const diagramId = state.activeDiagramId;
    if (!diagramId) return;
    const bestaand = vsPatchEntry(
      vsGet(state.store.getDiagram(diagramId)?.viewState ?? {}, bewonerId),
      bewonerId,
      "object",
    );
    if ((bestaand.swimlaneBaanHoogte ?? null) === hoogte) return;
    const voor: ViewElementState = { ...bestaand };
    const na: ViewElementState = { ...bestaand, swimlaneBaanHoogte: hoogte ?? undefined };
    const apply = (entry: ViewElementState) => {
      const s2 = get();
      s2.store.setViewElementState(diagramId, bewonerId, entry);
      schedulePersistDiagram(s2, diagramId, workspaceId, branchId);
    };
    apply(na);
    get().pushUserUndo({
      label: "Baanhoogte",
      undo: () => apply(voor),
      redo: () => apply(na),
    });
  },

  setSwimlaneLaneOnCanvas: (workspaceId, branchId, sleutel, baanId) => {
    const state = get();
    const diagramId = state.activeDiagramId;
    if (!diagramId) return;
    const viewState = state.store.getDiagram(diagramId)?.viewState ?? {};
    const bestaand = vsPatchEntry(vsGet(viewState, sleutel), sleutel, "object");
    if ((bestaand.swimlaneBaanId ?? null) === baanId) return;
    const na: ViewElementState = { ...bestaand, swimlaneBaanId: baanId ?? undefined };
    state.store.setViewElementState(diagramId, sleutel, na);
    schedulePersistDiagram(get(), diagramId, workspaceId, branchId);
  },

  setSwimlaneRowOrderOnCanvas: (workspaceId, branchId, volgorde) => {
    const state = get();
    const diagramId = state.activeDiagramId;
    if (!diagramId) return;
    const viewState = state.store.getDiagram(diagramId)?.viewState ?? {};
    const voor = new Map<string, ViewElementState>();
    const na = new Map<string, ViewElementState>();
    volgorde.forEach((conceptId, index) => {
      const bestaand = vsPatchEntry(vsGet(viewState, conceptId), conceptId, "object");
      if ((bestaand.swimlaneRij ?? null) === index) return;
      voor.set(conceptId, { ...bestaand });
      na.set(conceptId, { ...bestaand, swimlaneRij: index });
    });
    if (na.size === 0) return;
    const apply = (stand: Map<string, ViewElementState>) => {
      const s2 = get();
      for (const [conceptId, entry] of stand) {
        s2.store.setViewElementState(diagramId, conceptId, entry);
      }
      schedulePersistDiagram(s2, diagramId, workspaceId, branchId);
    };
    apply(na);
    get().pushUserUndo({
      label: "Baan verplaatsen",
      undo: () => apply(voor),
      redo: () => apply(na),
    });
  },

  setSwimlaneKnikOnCanvas: (workspaceId, branchId, relationId, knik) => {
    const state = get();
    const diagramId = state.activeDiagramId;
    if (!diagramId) return;
    const existing = vsPatchEntry(
      vsGet(state.store.getDiagram(diagramId)?.viewState ?? {}, relationId),
      relationId,
      "relation",
    );
    const before: typeof existing = { ...existing };
    const next: typeof existing = { ...existing, swimlaneKnik: knik ?? undefined };
    state.store.setViewElementState(diagramId, relationId, next);
    schedulePersistDiagram(state, diagramId, workspaceId, branchId);
    const apply = (snapshot: typeof existing) => {
      const s2 = get();
      s2.store.setViewElementState(diagramId, relationId, snapshot);
      schedulePersistDiagram(s2, diagramId, workspaceId, branchId);
    };
    get().pushUserUndo({
      label: knik ? "Stroom buigen" : "Stroom rechttrekken",
      undo: () => apply(before),
      redo: () => apply(next),
    });
  },

  setRelationRoutingOnCanvas: (workspaceId, branchId, relationId, routing) => {
    const state = get();
    const diagramId = state.activeDiagramId;
    if (!diagramId) return;
    const existing = vsPatchEntry(
      vsGet(state.store.getDiagram(diagramId)?.viewState ?? {}, relationId),
      relationId,
      "relation",
    );
    const before: typeof existing = { ...existing };
    const next: typeof existing = { ...existing };
    next.routing = routing ?? undefined;
    // Bewust kiezen van een built-in router wist de handmatige knikpunten
    // zodat de nieuwe route ook echt zichtbaar wordt. "manual" laat de
    // bestaande knikpunten staan — die zijn dan juist de bron van de route.
    if (routing !== "manual") {
      next.waypoints = undefined;
    }
    // Een nieuwe routing verandert de natuurlijke midpunt-positie van
    // het label; de oude offset slaat nergens meer op. Reset 'm zodat
    // het label schoon op de nieuwe lijn aanlandt.
    next.labelOffset = undefined;
    state.store.setViewElementState(diagramId, relationId, next);
    schedulePersistDiagram(state, diagramId, workspaceId, branchId);

    const apply = (snapshot: typeof existing) => {
      const s = get();
      s.store.setViewElementState(diagramId, relationId, snapshot);
      schedulePersistDiagram(s, diagramId, workspaceId, branchId);
    };
    get().pushUserUndo({
      label: "Lijn-routing wijzigen",
      undo: () => apply(before),
      redo: () => apply(next),
    });
  },

  setRelationLabelOffsetOnCanvas: (workspaceId, branchId, relationId, offset) => {
    const state = get();
    const diagramId = state.activeDiagramId;
    if (!diagramId) return;
    const existing = vsPatchEntry(
      vsGet(state.store.getDiagram(diagramId)?.viewState ?? {}, relationId),
      relationId,
      "relation",
    );
    const next: typeof existing = { ...existing };
    if (offset && (offset.x !== 0 || offset.y !== 0)) {
      next.labelOffset = { x: offset.x, y: offset.y };
    } else {
      next.labelOffset = undefined;
    }
    state.store.setViewElementState(diagramId, relationId, next);
    schedulePersistDiagram(state, diagramId, workspaceId, branchId);
  },

  setConceptLabelOffsetOnCanvas: (workspaceId, branchId, instanceId, offset) => {
    const state = get();
    const diagramId = state.activeDiagramId;
    if (!diagramId) return;
    // instanceId = entry-key; bail wanneer entry niet bestaat (label-
    // offset op een niet-geplaatste node is een no-op).
    const existing = state.store.getDiagram(diagramId)?.viewState[instanceId];
    if (!existing) return;
    const next: ViewElementState = { ...existing };
    if (offset && (offset.x !== 0 || offset.y !== 0)) {
      next.labelOffset = { x: offset.x, y: offset.y };
    } else {
      next.labelOffset = undefined;
    }
    state.store.setViewElementState(diagramId, instanceId, next);
    schedulePersistDiagram(state, diagramId, workspaceId, branchId);
  },

  setNodeStyleOnCanvas: (workspaceId, branchId, instanceId, patch) => {
    const state = get();
    const diagramId = state.activeDiagramId;
    if (!diagramId) return;
    // instanceId = entry-key; bail wanneer entry niet bestaat (node-
    // style op een niet-geplaatste node is een no-op).
    const existing = state.store.getDiagram(diagramId)?.viewState[instanceId];
    if (!existing) return;
    const prevStyle = existing.nodeStyle ?? {};
    const nextStyle = { ...prevStyle, ...patch };
    // Drop keys explicitly set to undefined so the object stays clean.
    for (const key of Object.keys(nextStyle) as Array<keyof typeof nextStyle>) {
      if (nextStyle[key] === undefined) delete nextStyle[key];
    }
    const next: ViewElementState = {
      ...existing,
      nodeStyle: Object.keys(nextStyle).length > 0 ? nextStyle : undefined,
    };
    state.store.setViewElementState(diagramId, instanceId, next);
    schedulePersistDiagram(state, diagramId, workspaceId, branchId);
    state.pushUserUndo({
      label: "Opmaak node",
      undo: () => {
        const s = get();
        const ve = s.store.getDiagram(diagramId)?.viewState[instanceId];
        if (!ve) return;
        s.store.setViewElementState(diagramId, instanceId, {
          ...ve,
          nodeStyle: Object.keys(prevStyle).length > 0 ? prevStyle : undefined,
        });
        schedulePersistDiagram(s, diagramId, workspaceId, branchId);
      },
      redo: () => {
        const s = get();
        const ve = s.store.getDiagram(diagramId)?.viewState[instanceId];
        if (!ve) return;
        s.store.setViewElementState(diagramId, instanceId, {
          ...ve,
          nodeStyle: Object.keys(nextStyle).length > 0 ? nextStyle : undefined,
        });
        schedulePersistDiagram(s, diagramId, workspaceId, branchId);
      },
    });
  },

  changeZOrderOnCanvas: (workspaceId, branchId, instanceIds, action) => {
    const state = get();
    const diagramId = state.activeDiagramId;
    if (!diagramId) return;
    const diagram = state.store.getDiagram(diagramId);
    if (!diagram) return;
    if (instanceIds.length === 0) return;

    // Bereken de huidige min/max zOrder over álle concept-instances op
    // het diagram. Voor "toFront"/"toBack" hebben we deze nodig om de
    // selectie boven/onder alles te leggen. Voor "forward"/"backward"
    // niet strikt nodig maar guard tegen runaway-waarden.
    let minZ = 0;
    let maxZ = 0;
    for (const [, ve] of Object.entries(diagram.viewState)) {
      if (ve.position === undefined) continue;
      const z = ve.zOrder ?? 0;
      if (z < minZ) minZ = z;
      if (z > maxZ) maxZ = z;
    }

    // Snapshot pre-state per id voor undo
    const before = new Map<string, number | undefined>();
    for (const id of instanceIds) {
      before.set(id, diagram.viewState[id]?.zOrder);
    }

    const apply = (target: "forward" | "backward" | "toFront" | "toBack") => {
      const ws = useWorkspaceData.getState();
      const d = ws.store.getDiagram(diagramId);
      if (!d) return;
      // Re-bereken min/max bij apply-tijd zodat redo na andere mutaties
      // de juiste extreme grenzen pakt.
      let curMin = 0;
      let curMax = 0;
      for (const [, ve] of Object.entries(d.viewState)) {
        if (ve.position === undefined) continue;
        const z = ve.zOrder ?? 0;
        if (z < curMin) curMin = z;
        if (z > curMax) curMax = z;
      }
      const front = curMax + 1;
      const back = curMin - 1;
      for (const id of instanceIds) {
        const cur = d.viewState[id];
        if (!cur) continue;
        const curZ = cur.zOrder ?? 0;
        let next: number;
        switch (target) {
          case "forward":
            next = curZ + 1;
            break;
          case "backward":
            next = curZ - 1;
            break;
          case "toFront":
            next = front;
            break;
          case "toBack":
            next = back;
            break;
        }
        ws.store.setViewElementState(diagramId, id, { ...cur, zOrder: next });
      }
      schedulePersistDiagram(ws, diagramId, workspaceId, branchId);
    };

    apply(action);

    state.pushUserUndo({
      label:
        action === "toFront"
          ? "Helemaal naar voren"
          : action === "toBack"
            ? "Helemaal naar achteren"
            : action === "forward"
              ? "Naar voren"
              : "Naar achteren",
      undo: () => {
        const ws = useWorkspaceData.getState();
        const d = ws.store.getDiagram(diagramId);
        if (!d) return;
        for (const [id, prevZ] of before) {
          const cur = d.viewState[id];
          if (!cur) continue;
          const next: ViewElementState = { ...cur };
          if (prevZ === undefined) {
            delete next.zOrder;
          } else {
            next.zOrder = prevZ;
          }
          ws.store.setViewElementState(diagramId, id, next);
        }
        schedulePersistDiagram(ws, diagramId, workspaceId, branchId);
      },
      redo: () => apply(action),
    });
  },

  connectConcepts: async (
    workspaceId,
    branchId,
    sourceId,
    targetId,
    relationTypeId,
    stereotypes,
    extraAttributes,
  ) => {
    // Required stereotype-attrs vooraf vullen met NTB/TBD/enum-default
    // zodat de worker-validatie niet rejecteert. Symmetrisch met
    // addConceptOnCanvas.
    let defaultAttrs: Record<string, unknown> = {};
    if (stereotypes && stereotypes.length > 0) {
      const reg = getRegistry();
      for (const stId of stereotypes) {
        const st = reg.getStereotype(stId);
        if (!st?.attributes?.length) continue;
        defaultAttrs = fillMissingRequiredAttributes(st.attributes, defaultAttrs);
      }
    }
    // Extra-attrs overschrijven NTB/TBD-defaults bij conflict (caller
    // wist 't beter — bv. `directed: true` op een Association).
    if (extraAttributes) {
      defaultAttrs = { ...defaultAttrs, ...extraAttributes };
    }
    const finalStereotypes = stereotypes && stereotypes.length > 0 ? stereotypes : undefined;
    const finalAttributes = Object.keys(defaultAttrs).length > 0 ? defaultAttrs : undefined;
    const optimistic = createRelation({
      typeId: relationTypeId,
      sourceConceptId: sourceId,
      targetConceptId: targetId,
      ownerEmail: getOwnerEmail(),
      ...(finalStereotypes ? { stereotypes: finalStereotypes } : {}),
      ...(finalAttributes ? { attributes: finalAttributes } : {}),
    });
    sharedUndo.apply(upsertRelationCommand(get().store, optimistic));
    // Per-diagram-scoping: leg een leeg viewState-entry voor deze
    // relatie op het actieve diagram. Dit is wat de filter in Canvas /
    // TextPane gebruikt om "hoort op dit diagram" te onderscheiden van
    // "hoort op een ander diagram (niet hier tonen)". Zonder deze entry
    // zou de relatie ook op elk ander diagram verschijnen waar bron- en
    // doel-concept toevallig allebei staan.
    {
      const state = get();
      const diagramId = state.activeDiagramId;
      if (diagramId) {
        state.store.setViewElementState(diagramId, optimistic.id, {
          type: "relation",
          ref: optimistic.id,
        });
        schedulePersistDiagram(state, diagramId, workspaceId, branchId);
      }
    }
    try {
      const persisted = await api.createRelation(workspaceId, branchId, {
        type_id: relationTypeId,
        source_concept_id: sourceId,
        target_concept_id: targetId,
        ...(finalStereotypes ? { stereotypes: finalStereotypes } : {}),
        ...(finalAttributes ? { attributes: finalAttributes } : {}),
      });
      replaceRelationId(get(), optimistic.id, persisted);
      pushAddRelationUndo(workspaceId, branchId, persisted);
      return persisted;
    } catch (err) {
      sharedUndo.undo();
      set({ lastError: explainError(err) });
      throw err;
    }
  },

  patchConcept: async (workspaceId, branchId, conceptId, patch) => {
    const state = get();
    const existing = state.store.getConcept(conceptId);
    if (!existing) {
      return;
    }
    // Capture the baseline at the start of an edit session so we can build
    // ONE undo entry that covers the entire session, not one per keystroke.
    if (!conceptPatchBaselines.has(conceptId)) {
      conceptPatchBaselines.set(conceptId, existing);
    }
    // Optimistic local update — merge the latest delta on top of the
    // current value so each keystroke is reflected immediately.
    let next = applyConceptPatch(existing, patch);
    // Bij type-wisseling: vul ontbrekende required velden van het
    // nieuwe type met pack-defaults zodat de PATCH niet faalt op de
    // worker-validatie ("1 field(s) invalid for type ..."). Dezelfde
    // helper draait server-side ook — beide kanten houden de attributes
    // consistent. Bug F in M4-acceptance.md.
    let typeChangeFill: Record<string, unknown> | null = null;
    if (next.typeId !== existing.typeId) {
      const elementType = getRegistry().getElementType(next.typeId);
      if (elementType?.attributes?.length) {
        const filled = fillMissingRequiredAttributes(elementType.attributes, next.attributes);
        if (filled !== next.attributes) {
          next = { ...next, attributes: filled };
          // Bewaar de fill-delta zodat de PATCH-body de defaults
          // expliciet meestuurt — anders zou de server alleen het
          // type-id-deel zien en moet 'ie zelf invullen (dat doet 'ie
          // wel, maar expliciet meesturen voorkomt rare race-condities
          // tussen client- en server-state).
          typeChangeFill = filled;
        }
      }
    }

    // Stereotype-toggle: vul ontbrekende required attrs van de actieve
    // stereotypes met pack-defaults zodat de PATCH server-side
    // validatie haalt. Anders rejecteert de worker (bv. severity is
    // required op een stereotype met required enum-attrs) en draait
    // de optimistic update terug — checkbox flikkert dan + valt terug
    // naar uit.
    if (patch.stereotypes !== undefined) {
      const reg = getRegistry();
      const stereotypeAttrs = next.stereotypes.flatMap((stId) => {
        const st = reg.getStereotype(stId);
        return st ? reg.resolveStereotypeAttributes(st) : [];
      });
      if (stereotypeAttrs.length > 0) {
        const filled = fillMissingRequiredAttributes(stereotypeAttrs, next.attributes);
        if (filled !== next.attributes) {
          next = { ...next, attributes: filled };
          // Stuur de fill-delta mee in de PATCH-body — zelfde
          // motivatie als typeChangeFill.
          typeChangeFill = { ...(typeChangeFill ?? {}), ...filled };
        }
      }
    }
    state.store.upsertConcept(next);

    // Accumulate field-level changes so the eventual API call sends every
    // field touched within this edit session, not just the latest one.
    const patchWithFill =
      typeChangeFill !== null ? { ...patch, attributes: typeChangeFill } : patch;
    const merged = mergeConceptPatch(conceptPatchAccumulators.get(conceptId) ?? {}, patchWithFill);
    conceptPatchAccumulators.set(conceptId, merged);

    // Debounce the API call so rapid typing batches into one PATCH.
    const existingTimer = pendingConceptPatches.get(conceptId);
    if (existingTimer) {
      clearTimeout(existingTimer);
    }
    const timer = setTimeout(() => {
      pendingConceptPatches.delete(conceptId);
      const finalPatch = conceptPatchAccumulators.get(conceptId) ?? patch;
      conceptPatchAccumulators.delete(conceptId);
      const baseline = conceptPatchBaselines.get(conceptId);
      conceptPatchBaselines.delete(conceptId);
      const after = useWorkspaceData.getState().store.getConcept(conceptId);

      // Push a single user-undo entry covering this edit session, but only
      // if the content actually changed (skip no-op flushes from echoes).
      if (baseline && after && !sameConceptContent(baseline, after)) {
        useWorkspaceData
          .getState()
          .pushUserUndo(buildConceptUndoEntry(workspaceId, branchId, conceptId, baseline, after));
      }

      // De versie die deze sessie in handen heeft gaat mee: de locking-laag
      // weigert de schrijfactie als er sinds het ophalen iets veranderd is.
      // Zie `docs/architecture/gelijktijdigheid.md` §4.
      const held = useWorkspaceData.getState().store.getConcept(conceptId);
      const withVersion =
        held?.rowVersion === undefined
          ? finalPatch
          : { ...(finalPatch as Record<string, unknown>), row_version: held.rowVersion };

      void api
        .patchConcept(workspaceId, branchId, conceptId, withVersion)
        .then((response) => {
          // De versie die de server nu heeft. Zonder deze stap schrijft de
          // volgende bewerking met de versie van de laatste ophaalactie mee en
          // weigert de laag 'm — voor de gebruiker: je tekst verdwijnt terwijl
          // je typt.
          const nieuweVersie = (response as { rowVersion?: number } | undefined)?.rowVersion;
          if (typeof nieuweVersie === "number") {
            useWorkspaceData.getState().store.noteRowVersion("concept", conceptId, nieuweVersie);
          }
          // Branch-copy-on-write: server kan een nieuwe concept-id
          // hebben gemaakt. Verleg in-memory store + UI-selection +
          // pas viewState in alle gekopieerde diagrams aan.
          if (
            response &&
            typeof response === "object" &&
            "branchCopied" in response &&
            (response as { branchCopied?: boolean }).branchCopied &&
            "id" in response
          ) {
            const copy = response as Concept & {
              branchCopied?: boolean;
              diagramRenames?: Array<{ oldDiagramId: string; newDiagramId: string }>;
            };
            if (copy.id !== conceptId) {
              handleConceptBranchCopy(conceptId, copy, copy.diagramRenames ?? []);
            }
          }
        })
        .catch((err) => {
          // 409 = je werkte op een verouderd exemplaar. Dan klopt het beeld
          // zelf niet meer en halen we 't opnieuw op; dát zet de waarde recht,
          // een aparte terugdraai-actie is overbodig.
          if (err instanceof ApiError && err.status === 409) {
            // Eerst het beeld rechtzetten, dán pas de melding — andersom wist
            // het ophalen 'm meteen weer weg en zag de gebruiker z'n wijziging
            // verdwijnen zonder te horen waarom.
            useWorkspaceData.getState().store.upsertConcept(baseline ?? existing);
            void useWorkspaceData
              .getState()
              .hydrate(workspaceId, branchId)
              .finally(() => useWorkspaceData.setState({ lastError: explainError(err) }));
            return;
          }
          // Elke andere fout — de server ligt eruit, het netwerk hapert, een
          // 500 — betekent dat er níets is opgeslagen. Dan is wat de modelleur
          // typte nog steeds het enige dat er is, en terugdraaien wist precies
          // dat. Symptoom van de oude terugdraai: "mijn tekst verdwijnt
          // tijdens het typen" zodra de worker niet antwoordt (gemeld
          // 2026-09-02, met een dev-worker die eruit lag). We laten de waarde
          // staan en zeggen dat 'ie niet is opgeslagen; de volgende bewerking
          // stuurt 'm opnieuw mee.
          useWorkspaceData.setState({ lastError: explainError(err) });
        });
    }, PERSIST_DEBOUNCE_MS);
    pendingConceptPatches.set(conceptId, timer);
  },

  patchRelation: async (workspaceId, branchId, relationId, patch) => {
    const state = get();
    const existing = state.store.getRelation(relationId);
    if (!existing) return;

    if (!relationPatchBaselines.has(relationId)) {
      relationPatchBaselines.set(relationId, existing);
    }
    let next = applyRelationPatch(existing, patch);
    // Bij type-wisseling van een relatie: vul ontbrekende required
    // velden van het nieuwe type met pack-defaults. Anders faalt de
    // worker-validatie met "1 field(s) invalid for relation type ..."
    // op velden die de gebruiker nooit heeft aangeraakt (bijv. de
    // `directed: boolean` op Association). Bug F in M4-acceptance.md.
    let typeChangeFill: Record<string, unknown> | null = null;
    let typeChangeStripStereos: string[] | null = null;
    if (next.typeId !== existing.typeId) {
      const reg = getRegistry();
      // Strip stereotypes die niet op het nieuwe type mogen.
      // Voorbeeld: bmc.Realization heeft applies_to=[Association]. Als
      // de modelleur de drager-relatie omzet naar Influence, kan
      // bmc.Realization daar niet meer op landen — server-validatie
      // zou afwijzen en optimistic update terugdraaien (= relatie
      // springt terug). Daarom hier proactief weglaten zodat de PATCH
      // een schone relatie aflevert.
      const currentStereos = next.stereotypes ?? [];
      // Volledige huidige array meegeven: conditionele `applies_to`-entries
      // (`requires_stereotype`) matchen alleen wanneer het co-vereiste
      // stereotype óók aanwezig is. Weglaten zou hier stilletjes te veel
      // strippen.
      const validStereos = currentStereos.filter((stId) =>
        reg.canApplyStereotype(stId, next.typeId, currentStereos),
      );
      if (validStereos.length !== currentStereos.length) {
        next = { ...next, stereotypes: validStereos };
        typeChangeStripStereos = validStereos;
      }
      const relType = reg.getRelationType(next.typeId);
      if (relType?.attributes?.length) {
        const filled = fillMissingRequiredAttributes(relType.attributes, next.attributes);
        if (filled !== next.attributes) {
          next = { ...next, attributes: filled };
          typeChangeFill = filled;
        }
      }
    }
    // Stereotype-toggle (analog to patchConcept): vul ontbrekende
    // required attrs van actieve relatie-stereotypes met pack-defaults
    // zodat de PATCH server-side validatie haalt. Anders rejecteert
    // de worker (bv. forward_name + cardinaliteit op Sharp's
    // «ConceptAssociation») en draait de optimistic update terug —
    // checkbox flikkert dan + valt terug naar uit.
    if (patch.stereotypes !== undefined) {
      const reg = getRegistry();
      const stereotypeAttrs = next.stereotypes.flatMap((stId) => {
        const st = reg.getStereotype(stId);
        return st ? reg.resolveStereotypeAttributes(st) : [];
      });
      if (stereotypeAttrs.length > 0) {
        const filled = fillMissingRequiredAttributes(stereotypeAttrs, next.attributes);
        if (filled !== next.attributes) {
          next = { ...next, attributes: filled };
          typeChangeFill = { ...(typeChangeFill ?? {}), ...filled };
        }
      }
    }
    state.store.upsertRelation(next);

    // Bouw de uitgaande PATCH zodat 'ie de afgeleide wijzigingen
    // (auto-fill van required attrs, auto-strip van incompatibele
    // stereotypes) ook meeneemt — server-side validatie moet anders
    // niet doorlopen worden door wat 't optimistic-pad al heeft
    // gefixed.
    let patchWithFill = patch;
    if (typeChangeFill !== null) {
      patchWithFill = { ...patchWithFill, attributes: typeChangeFill };
    }
    if (typeChangeStripStereos !== null) {
      patchWithFill = { ...patchWithFill, stereotypes: typeChangeStripStereos };
    }
    const merged = mergeRelationPatch(
      relationPatchAccumulators.get(relationId) ?? {},
      patchWithFill,
    );
    relationPatchAccumulators.set(relationId, merged);

    const existingTimer = pendingRelationPatches.get(relationId);
    if (existingTimer) clearTimeout(existingTimer);
    const timer = setTimeout(() => {
      pendingRelationPatches.delete(relationId);
      const finalPatch = relationPatchAccumulators.get(relationId) ?? patch;
      relationPatchAccumulators.delete(relationId);
      const baseline = relationPatchBaselines.get(relationId);
      relationPatchBaselines.delete(relationId);
      const after = useWorkspaceData.getState().store.getRelation(relationId);

      if (baseline && after && !sameRelationContent(baseline, after)) {
        useWorkspaceData
          .getState()
          .pushUserUndo(buildRelationUndoEntry(workspaceId, branchId, relationId, baseline, after));
      }

      // Dezelfde poort als bij concepten. De serverkant stond er sinds
      // `06ec26b`, alleen stuurde de client de versie nooit mee — en zonder
      // versie slaat de worker de controle stilzwijgend over. Zie
      // `docs/architecture/gelijktijdigheid.md` §4.
      const held = useWorkspaceData.getState().store.getRelation(relationId);
      const withVersion =
        held?.rowVersion === undefined
          ? finalPatch
          : { ...(finalPatch as Record<string, unknown>), row_version: held.rowVersion };

      void api
        .patchRelation(workspaceId, branchId, relationId, withVersion)
        .then((response) => {
          // Zelfde reden als bij het concept-pad: zonder de verse versie
          // weigert de laag de volgende schrijfactie op deze relatie.
          const nieuweVersie = (response as { rowVersion?: number } | undefined)?.rowVersion;
          if (typeof nieuweVersie === "number") {
            useWorkspaceData.getState().store.noteRowVersion("relation", relationId, nieuweVersie);
          }
          // Branch-copy-on-write: server kan een nieuwe relation-id
          // hebben gemaakt. Verleg de in-memory store + UI-selection
          // zodat het AttributePanel naar de kopie mee verhuist en
          // verdere mutaties op de kopie landen. `diagramRenames` lijst
          // diagrams die meegekopieerd zijn — pas de viewState-keys aan
          // in de in-memory store en switch activeDiagramId indien nodig.
          if (
            response &&
            typeof response === "object" &&
            "branchCopied" in response &&
            (response as { branchCopied?: boolean }).branchCopied &&
            "id" in response
          ) {
            const copy = response as Relation & {
              branchCopied?: boolean;
              diagramRenames?: Array<{ oldDiagramId: string; newDiagramId: string }>;
            };
            if (copy.id !== relationId) {
              handleRelationBranchCopy(relationId, copy, copy.diagramRenames ?? []);
            }
          }
          // Duplicaat-detectie na patch (besluit 2026-06-18): is de IDENTITEIT
          // van de relatie veranderd (type/naam/richting/directed) en is 'ie nu
          // identiek aan een bestaande → vraag samenvoegen. De net-gewijzigde
          // gaat op in de gekozen bestaande.
          const finalId =
            response &&
            typeof response === "object" &&
            "branchCopied" in response &&
            (response as { branchCopied?: boolean }).branchCopied &&
            "id" in response
              ? (response as Relation).id
              : relationId;
          const wsNow = useWorkspaceData.getState();
          const patched = wsNow.store.getRelation(finalId);
          if (baseline && patched && !isSameRelationIdentity(baseline, patched)) {
            // Label-only (rename) NIET hier: de naam wordt per toetsaanslag
            // gepatcht, dus dit zou de vraag midden in het typen tonen ("aaa"
            // terwijl je "aaaa" wilt). De naam-edit triggert de check op commit
            // (blur/Enter). Type/richting/directed = discrete acties → direct.
            const directedSame =
              (baseline.attributes?.directed === true) === (patched.attributes?.directed === true);
            const labelOnly =
              baseline.typeId === patched.typeId &&
              baseline.sourceConceptId === patched.sourceConceptId &&
              baseline.targetConceptId === patched.targetConceptId &&
              directedSame;
            if (!labelOnly) {
              void useWorkspaceData
                .getState()
                .checkRelationDuplicate(workspaceId, branchId, finalId);
            }
          }
        })
        .catch((err) => {
          // Zelfde afweging als bij het concept-pad: bij een 409 klopt het
          // beeld niet meer en zetten we 'm terug; bij elke andere fout is er
          // niets opgeslagen en is wat de modelleur invulde het enige dat er
          // is. Dat weggooien maakt de fout erger dan 'ie is.
          if (err instanceof ApiError && err.status === 409) {
            useWorkspaceData.getState().store.upsertRelation(baseline ?? existing);
            void useWorkspaceData
              .getState()
              .hydrate(workspaceId, branchId)
              .finally(() => useWorkspaceData.setState({ lastError: explainError(err) }));
            return;
          }
          useWorkspaceData.setState({ lastError: explainError(err) });
        });
    }, PERSIST_DEBOUNCE_MS);
    pendingRelationPatches.set(relationId, timer);
  },

  removeConcept: async (workspaceId, branchId, conceptId, actionId) => {
    const state = get();
    // Capture the concept, its view_state, AND all incident relations
    // (with their view_state) BEFORE the local cascade runs — both the
    // local store and the server delete-cascade clear them, so we need
    // the snapshots to be able to restore everything on undo.
    const beforeConcept = state.store.getConcept(conceptId);
    const diagramId = state.activeDiagramId;
    const diagram = diagramId ? state.store.getDiagram(diagramId) : null;
    const beforeView = vsGet(diagram?.viewState ?? {}, conceptId);
    const incidentRelations = state.store
      .listRelations()
      .filter((r) => r.sourceConceptId === conceptId || r.targetConceptId === conceptId)
      .map((r) => ({
        relation: r,
        viewState: vsGet(diagram?.viewState ?? {}, r.id),
      }));

    sharedUndo.apply(removeConceptCommand(state.store, conceptId));
    const recordUndo = () => {
      if (beforeConcept) {
        pushRemoveConceptUndo(
          workspaceId,
          branchId,
          beforeConcept,
          beforeView,
          incidentRelations,
          diagramId,
        );
      }
    };
    try {
      await deleteConceptWithImpactRetry(workspaceId, branchId, conceptId, actionId);
      recordUndo();
    } catch (err) {
      sharedUndo.undo();
      set({ lastError: explainError(err) });
    }
  },

  mergeConcepts: async (workspaceId, branchId, winnerId, loserIds) => {
    if (loserIds.length === 0) return;
    const state = get();
    try {
      const updatedWinner = await api.mergeConcepts(workspaceId, branchId, winnerId, loserIds);
      // Lokale store opruimen: losers verwijderen, hun viewState-keys
      // re-pointen, relaties' endpoints aanpassen, winner's attributes
      // updaten met de server-respons.
      state.store.transaction(() => {
        state.store.upsertConcept(updatedWinner);
        for (const id of loserIds) {
          state.store.removeConcept(id);
        }
        // Relaties: in-memory updaten zodat de UI direct klopt zonder
        // refresh. Server heeft 'm al gepatcht.
        for (const r of state.store.listRelations()) {
          if (loserIds.includes(r.sourceConceptId) || loserIds.includes(r.targetConceptId)) {
            state.store.upsertRelation({
              ...r,
              sourceConceptId: loserIds.includes(r.sourceConceptId) ? winnerId : r.sourceConceptId,
              targetConceptId: loserIds.includes(r.targetConceptId) ? winnerId : r.targetConceptId,
            });
          }
        }
        // ViewState: per diagram zoek loser-keys + conceptRefs en herwijz.
        for (const d of state.store.listDiagrams()) {
          let changed = false;
          const next: Record<string, ViewElementState> = {};
          for (const [k, ve] of Object.entries(d.viewState)) {
            const newKey = loserIds.includes(k) ? winnerId : k;
            const newRef = ve.ref && loserIds.includes(ve.ref) ? winnerId : ve.ref;
            if (newKey !== k || newRef !== ve.ref) changed = true;
            // Dedupe: als winner al een entry heeft op deze key, laatste-wins.
            next[newKey] = { ...ve, ...(newRef ? { ref: newRef } : {}) };
          }
          if (changed) {
            state.store.upsertDiagram({ ...d, viewState: next });
          }
        }
      });
    } catch (err) {
      set({ lastError: explainError(err) });
    }
  },

  mergeRelations: async (workspaceId, branchId, winnerId, loserIds) => {
    if (loserIds.length === 0) return;
    const state = get();
    const winner = state.store.getRelation(winnerId);
    if (!winner) return;
    try {
      await api.mergeRelations(workspaceId, branchId, winnerId, loserIds);
      // Lokale store spiegelt de server-merge (re-point + materialiseer).
      const loserSet = new Set(loserIds);
      const winnerSrc = winner.sourceConceptId;
      const winnerTgt = winner.targetConceptId;
      // Overal-zichtbaar-detectie: heeft een relatie ÉRGENS een entry?
      const hasEntry = new Set<string>();
      for (const d of state.store.listDiagrams()) {
        for (const [k, ve] of Object.entries(d.viewState)) {
          if (ve.position !== undefined || ve.type === "object") continue;
          hasEntry.add(ve.ref ?? k);
        }
      }
      const winnerEverywhereVisible = !hasEntry.has(winnerId);
      const anyLoserEverywhereVisible = loserIds.some((id) => !hasEntry.has(id));
      state.store.transaction(() => {
        for (const id of loserIds) state.store.removeRelation(id);
        for (const d of state.store.listDiagrams()) {
          let changed = false;
          let winnerHasEntryHere = false;
          let srcPresent = false;
          let tgtPresent = false;
          const next: Record<string, ViewElementState> = {};
          for (const [k, ve] of Object.entries(d.viewState)) {
            const isObject = ve.position !== undefined || ve.type === "object";
            const refId = ve.ref ?? k;
            if (isObject) {
              if (refId === winnerSrc) srcPresent = true;
              if (refId === winnerTgt) tgtPresent = true;
            } else if (refId === winnerId) {
              winnerHasEntryHere = true;
            }
            const keyIsLoser = loserSet.has(k);
            const refIsLoser = !!ve.ref && loserSet.has(ve.ref);
            if (!keyIsLoser && !refIsLoser) {
              next[k] = ve;
              continue;
            }
            changed = true;
            const newKey = keyIsLoser ? winnerId : k;
            const newVe: ViewElementState = { ...ve, ...(refIsLoser ? { ref: winnerId } : {}) };
            if (newKey in next) continue; // collision → winner behoudt z'n entry
            next[newKey] = newVe;
            winnerHasEntryHere = true;
          }
          if (
            anyLoserEverywhereVisible &&
            !winnerEverywhereVisible &&
            srcPresent &&
            tgtPresent &&
            !winnerHasEntryHere
          ) {
            next[winnerId] = { type: "relation", ref: winnerId };
            changed = true;
          }
          if (changed) state.store.upsertDiagram({ ...d, viewState: next });
        }
      });
      // De store.transaction hierboven bumpt + sync't de revision al zelf
      // (zie `bumpRevision`-comment); een extra directe zet was overbodig.
    } catch (err) {
      set({ lastError: explainError(err) });
    }
  },

  checkRelationDuplicate: async (workspaceId, branchId, relationId) => {
    const state = get();
    const r = state.store.getRelation(relationId);
    if (!r) return;
    const dups = findDuplicateRelations(r, state.store.listRelations(), relationId);
    if (dups.length === 0) return;
    const choice = await useUi.getState().promptMergeRelations(
      relationId,
      dups.map((d) => d.id),
    );
    if (choice && choice !== "keep") {
      // De net-gewijzigde relatie (relationId) gaat op in de gekozen bestaande.
      await get().mergeRelations(workspaceId, branchId, choice, [relationId]);
    }
  },

  checkConceptDuplicate: async (workspaceId, branchId, conceptId) => {
    const state = get();
    const c = state.store.getConcept(conceptId);
    if (!c || !conceptHasName(c)) return; // named-only
    // Een object dat nog "Step" of "Merge point" heet is niet benoemd maar
    // getypeerd; twee daarvan zijn geen klonen. Sharp zet eerst lege dozen
    // neer om de stroom vast te leggen (H8 fig. 8.4) — dan hoort er geen
    // samenvoeg-vraag tussen te komen.
    if (heeftStandaardnaam(c)) return;
    const dups = findDuplicateConcepts(c, state.store.listConcepts(), conceptId);
    if (dups.length === 0) return;
    const choice = await useUi.getState().promptMergeConcepts(
      conceptId,
      dups.map((d) => d.id),
    );
    if (choice && choice !== "keep") {
      // Het net-aangemaakte/hernoemde concept gaat op in de gekozen bestaande.
      await get().mergeConcepts(workspaceId, branchId, choice, [conceptId]);
    }
  },

  applyViewElementOnCanvas: (workspaceId, branchId, diagramId, instanceId, viewState) => {
    const state = get();
    state.store.setViewElementState(diagramId, instanceId, viewState);
    schedulePersistDiagram(state, diagramId, workspaceId, branchId);
  },

  removeViewElementOnCanvas: (workspaceId, branchId, diagramId, instanceId) => {
    const state = get();
    state.store.removeViewElementState(diagramId, instanceId);
    schedulePersistDiagram(state, diagramId, workspaceId, branchId);
  },

  hideConceptOnDiagram: (workspaceId, branchId, nodeId) => {
    // "Hide" = drop the viewState entry for this diagram. `nodeId` is
    // a viewState entry key — either the canonical concept id (first
    // placement) or a fresh UUID for een additional instance via
    // `ref`/`conceptRef`. The underlying concept blijft in de workspace
    // store en blijft zichtbaar op andere instances/diagrammen.
    //
    // Cascade-invariant (2026-05-26 multi-instance-refactor): een
    // relatie-instance bestaat alleen bij gratie van z'n twee endpoint-
    // object-instances. Wanneer een endpoint-instance van dit diagram
    // verdwijnt, verliest elke daarvan afhankelijke relatie-instance
    // z'n bestaansrecht en moet OOK uit viewState verdwijnen.
    // Re-anchoren naar een andere instance van hetzelfde concept is
    // verboden — dat zou de relatie visueel "verspringen" en is geen
    // user-bedoeling.
    //
    // Afhankelijkheidsdefinitie (relatie-instance R hangt aan
    // object-instance X met key = nodeId):
    //   • R.sourceInstanceId === nodeId, OF
    //   • R.targetInstanceId === nodeId, OF
    //   • R heeft GEEN sourceInstanceId-pin én nodeId === R-source-
    //     concept.id (canonieke anchor — entry-key matcht conceptId),
    //   • idem voor target zonder pin.
    const state = get();
    const diagramId = state.activeDiagramId;
    if (!diagramId) return;
    const diagram = state.store.getDiagram(diagramId);
    const beforeView = diagram?.viewState[nodeId];
    if (!beforeView) return;

    // Verzamel afhankelijke relatie-instance-entries vóór we verwijderen,
    // zodat de undo ze terug kan zetten.
    const dependentRelEntries: Array<{ key: string; entry: ViewElementState }> = [];
    for (const [key, ve] of Object.entries(diagram?.viewState ?? {})) {
      if (ve.position !== undefined) continue; // object-entry
      if (ve.type === "object") continue;
      const relId = ve.ref ?? key;
      const rel = state.store.getRelation(relId);
      if (!rel) continue;
      // Pinned check
      const pinnedBySource = ve.sourceInstanceId === nodeId;
      const pinnedByTarget = ve.targetInstanceId === nodeId;
      // Pin-less canonical-anchor check: alleen wanneer nodeId daadwerkelijk
      // de canonieke entry-key is (nodeId === concept-id) telt 't als
      // implicit anchor. Multi-instance entry-keys (UUID) raken pin-less
      // relations niet.
      const isCanonicalForSource = !ve.sourceInstanceId && nodeId === rel.sourceConceptId;
      const isCanonicalForTarget = !ve.targetInstanceId && nodeId === rel.targetConceptId;
      if (pinnedBySource || pinnedByTarget || isCanonicalForSource || isCanonicalForTarget) {
        dependentRelEntries.push({ key, entry: ve });
      }
    }

    // Verwijder eerst de afhankelijke relatie-instances, dan de
    // object-instance zelf. Volgorde maakt voor de UI niet uit (één
    // re-render aan het einde van de transaction), maar leesbaar zo.
    for (const { key } of dependentRelEntries) {
      state.store.removeViewElementState(diagramId, key);
    }
    removeFromActiveDiagram(state, nodeId, workspaceId, branchId);
    // Tombstone óók de afhankelijke relatie-instances (die via
    // removeViewElementState gingen, niet via removeFromActiveDiagram) zodat
    // de reconcile-merge ze niet uit de parent terughaalt.
    tombstoneViewStateKeys(
      state,
      diagramId,
      dependentRelEntries.map((e) => e.key),
    );

    // Alle keys die deze hide als bewust-verwijderd markeert: het concept zelf
    // (via removeFromActiveDiagram) + de afhankelijke relatie-instances.
    const hiddenKeys = [nodeId, ...dependentRelEntries.map((e) => e.key)];
    const applyRedo = () => {
      const ws = useWorkspaceData.getState();
      for (const { key } of dependentRelEntries) {
        ws.store.removeViewElementState(diagramId, key);
      }
      removeFromActiveDiagram(ws, nodeId, workspaceId, branchId);
      tombstoneViewStateKeys(
        ws,
        diagramId,
        dependentRelEntries.map((e) => e.key),
      );
    };
    const applyUndo = () => {
      const ws = useWorkspaceData.getState();
      ws.store.setViewElementState(diagramId, nodeId, beforeView);
      for (const { key, entry } of dependentRelEntries) {
        ws.store.setViewElementState(diagramId, key, entry);
      }
      // De bewust-verwijderd-markeringen opheffen, anders werkt de
      // branch-reconcile-merge het herstel tegen (zelfde bug als
      // hideRelationOnDiagram).
      untombstoneViewStateKeys(ws, diagramId, hiddenKeys);
      schedulePersistDiagram(ws, diagramId, workspaceId, branchId);
    };
    useWorkspaceData.getState().pushUserUndo({
      label:
        dependentRelEntries.length > 0
          ? `Verwijder concept + ${dependentRelEntries.length} afhankelijke relatie(s) van diagram`
          : "Verwijder concept van diagram",
      undo: applyUndo,
      redo: applyRedo,
    });
  },

  hideRelationOnDiagram: (workspaceId, branchId, idOrEntryKey) => {
    // Multi-instance-refactor (2026-05-26): Delete op een edge moet
    // ÉÉN SPECIFIEKE INSTANCE verwijderen, niet alle instances van de
    // domain-relatie. Parameter is entry-key (zoals Canvas DEL-handler
    // edge.id meegeeft) OF relation-id (voor legacy callers); we
    // ondersteunen beide via findEntryKeyForEntity-fallback.
    //
    // Semantiek: viewState-entry wordt VERWIJDERD (niet hidden:true) —
    // consistent met hideConceptOnDiagram. Undo zet 'm terug.
    const state = get();
    const diagramId = state.activeDiagramId;
    if (!diagramId) return;
    const diagram = state.store.getDiagram(diagramId);
    if (!diagram) return;
    // Resolve entry-key: direct-key match (Canvas geeft edge.id =
    // entry-key) OR ref-based lookup (legacy callers passen relation.id).
    let entryKey: string | null = null;
    if (diagram.viewState[idOrEntryKey] !== undefined) {
      entryKey = idOrEntryKey;
    } else {
      entryKey = findEntryKeyForEntity(diagram.viewState, idOrEntryKey);
    }
    if (entryKey === null) {
      // Overal-zichtbare (legacy, géén marker) relatie: er is geen entry om te
      // verwijderen, dus DEL deed voorheen niks. We verbergen 'm op DÍT diagram
      // door een `hidden:true`-marker te zetten (de edge-builder slaat die over).
      // Bijeffect (besluit 2026-06-18): de relatie wordt daarmee per-diagram-
      // scoped — 'ie verdwijnt ook van andere diagrammen waar 'ie via de
      // overal-zichtbaar-fallback stond. Voor legacy/auto-relaties acceptabel.
      // edge.id == relation-id voor zulke relaties.
      const relId = idOrEntryKey;
      if (!state.store.getRelation(relId)) return;
      const hiddenEntry: ViewElementState = { type: "relation", ref: relId, hidden: true };
      state.store.setViewElementState(diagramId, relId, hiddenEntry);
      schedulePersistDiagram(state, diagramId, workspaceId, branchId);
      useWorkspaceData.getState().pushUserUndo({
        label: "Verberg relatie op diagram",
        undo: () => {
          const ws = useWorkspaceData.getState();
          ws.store.removeViewElementState(diagramId, relId);
          schedulePersistDiagram(ws, diagramId, workspaceId, branchId);
        },
        redo: () => {
          const ws = useWorkspaceData.getState();
          ws.store.setViewElementState(diagramId, relId, hiddenEntry);
          schedulePersistDiagram(ws, diagramId, workspaceId, branchId);
        },
      });
      return;
    }
    const beforeView = diagram.viewState[entryKey];
    if (beforeView === undefined) return;
    state.store.removeViewElementState(diagramId, entryKey);
    tombstoneViewStateKeys(state, diagramId, [entryKey]);
    schedulePersistDiagram(state, diagramId, workspaceId, branchId);

    const apply = (snapshot: ViewElementState | undefined) => {
      const ws = useWorkspaceData.getState();
      if (snapshot) {
        // Undo: entry terug + de bewust-verwijderd-markering opheffen, anders
        // werkt de branch-reconcile-merge het herstel tegen (zie
        // untombstoneViewStateKeys).
        ws.store.setViewElementState(diagramId, entryKey, snapshot);
        untombstoneViewStateKeys(ws, diagramId, [entryKey]);
      } else {
        // Redo: entry weg + opnieuw als bewust-verwijderd markeren.
        ws.store.removeViewElementState(diagramId, entryKey);
        tombstoneViewStateKeys(ws, diagramId, [entryKey]);
      }
      schedulePersistDiagram(ws, diagramId, workspaceId, branchId);
    };
    useWorkspaceData.getState().pushUserUndo({
      label: "Verwijder relatie-instance van diagram",
      undo: () => apply(beforeView),
      redo: () => apply(undefined),
    });
  },

  toggleCollapseContainer: (workspaceId, branchId, containerInstanceId) => {
    const state = get();
    const diagramId = state.activeDiagramId;
    if (!diagramId) return;
    const diagram = state.store.getDiagram(diagramId);
    if (!diagram) return;
    const viewState = diagram.viewState;

    // Geen actie wanneer er geen descendants zijn (chevron is dan
    // sowieso niet getoond, maar verdedig diepte tegen rare states).
    const descendants = collectInstanceDescendants(viewState, containerInstanceId);
    if (descendants.length === 0) return;

    // Z-index-aanpak (vervangt het eerdere `hidden:true`-zetten op
    // descendants): we togglen één flag op de container zelf. Bij collapse
    // krijgt 'ie zIndex hoog → schildert bovenop kinderen + binnen-segmenten
    // van edges. Bij expand: zIndex weer laag → kinderen zichtbaar.
    // Voordeel: edges blijven semantisch verbonden met descendants, geen
    // re-anchor-rekenwerk; browser regelt visuele clipping puur via paint
    // order. Lijnen "eindigen" op de container-rand op exact het punt
    // waar ze logisch door de rand zouden gaan.
    const containerVe = viewState[containerInstanceId];
    if (!containerVe) return;
    const beforeVe: ViewElementState = { ...containerVe };
    const isCurrentlyCollapsed = containerVe.collapsedAsContainer === true;
    const nextCollapsed = !isCurrentlyCollapsed;

    const apply = (collapsed: boolean) => {
      const ws = useWorkspaceData.getState();
      const d = ws.store.getDiagram(diagramId);
      if (!d) return;
      const cur = d.viewState[containerInstanceId];
      if (!cur) return;
      const next: ViewElementState = { ...cur };
      if (collapsed) {
        next.collapsedAsContainer = true;
      } else {
        delete next.collapsedAsContainer;
      }
      ws.store.setViewElementState(diagramId, containerInstanceId, next);
      schedulePersistDiagram(ws, diagramId, workspaceId, branchId);
    };

    apply(nextCollapsed);

    state.pushUserUndo({
      label: nextCollapsed ? "Container inklappen" : "Container uitklappen",
      undo: () => {
        const ws = useWorkspaceData.getState();
        ws.store.setViewElementState(diagramId, containerInstanceId, beforeVe);
        schedulePersistDiagram(ws, diagramId, workspaceId, branchId);
      },
      redo: () => apply(nextCollapsed),
    });
  },

  removeRelation: async (workspaceId, branchId, relationId, actionId) => {
    const state = get();
    const beforeRelation = state.store.getRelation(relationId);
    const diagramId = state.activeDiagramId;
    const diagram = diagramId ? state.store.getDiagram(diagramId) : null;
    const beforeView = vsGet(diagram?.viewState ?? {}, relationId);

    sharedUndo.apply(removeRelationCommand(state.store, relationId));
    try {
      await api.deleteRelation(workspaceId, branchId, relationId, actionId);
      if (beforeRelation) {
        pushRemoveRelationUndo(workspaceId, branchId, beforeRelation, beforeView, diagramId);
      }
    } catch (err) {
      sharedUndo.undo();
      set({ lastError: explainError(err) });
    }
  },

  removeBatch: async (workspaceId, branchId, conceptIds, relationIds) => {
    const state = get();
    const diagramId = state.activeDiagramId;
    const diagram = diagramId ? state.store.getDiagram(diagramId) : null;
    const actionId = crypto.randomUUID();
    const conceptIdSet = new Set(conceptIds);

    // ── Snapshot fase ─────────────────────────────────────────────
    // Concepten + viewState. Sla op vóórdat 't lokale cascade-pad
    // 't materiaal opvreet.
    const conceptSnapshots = conceptIds
      .map((id) => {
        const c = state.store.getConcept(id);
        if (!c) return null;
        // viewState is instance-UUID-keyed (#40): resolve de placement via
        // vsGet i.p.v. viewState[conceptId] (dat is undefined). Anders krijgt
        // de undo geen plaatsing terug en verschijnt het object niet op canvas.
        return { concept: c, viewState: vsGet(diagram?.viewState ?? {}, id) };
      })
      .filter(
        (s): s is { concept: Concept; viewState: ViewElementState | undefined } => s !== null,
      );

    // Alle te-verwijderen relaties = expliciet meegegeven ids +
    // incident-relations van te-verwijderen concepten. Dedupe via Set.
    const allRelationIdSet = new Set<string>(relationIds);
    for (const id of conceptIds) {
      for (const r of state.store.listRelations()) {
        if (r.sourceConceptId === id || r.targetConceptId === id) {
          allRelationIdSet.add(r.id);
        }
      }
    }
    const relationSnapshots = [...allRelationIdSet]
      .map((id) => {
        const r = state.store.getRelation(id);
        if (!r) return null;
        return { relation: r, viewState: vsGet(diagram?.viewState ?? {}, id) };
      })
      .filter(
        (s): s is { relation: Relation; viewState: ViewElementState | undefined } => s !== null,
      );

    // ── Delete fase ───────────────────────────────────────────────
    // Lokale store: cascade-delete via removeConceptCommand pakt incident-
    // relations vanzelf. Relaties die NIET incident zijn (expliciet
    // meegegeven, source/target buiten de batch) doen we apart.
    state.store.transaction(() => {
      for (const id of conceptIds) {
        sharedUndo.apply(removeConceptCommand(state.store, id));
      }
      // Relations die NIET door de cascade weggegaan zijn (= geen endpoint
      // in conceptIdSet) wissen we expliciet.
      for (const rid of relationIds) {
        const r = state.store.getRelation(rid);
        if (!r) continue; // al weg via cascade
        if (conceptIdSet.has(r.sourceConceptId) || conceptIdSet.has(r.targetConceptId)) continue;
        sharedUndo.apply(removeRelationCommand(state.store, rid));
      }
    });

    // Server-side deletes. Volgorde: explicit-only relations eerst
    // (anders zou een cascade-delete vanuit een concept ze al doen,
    // en de losse api.deleteRelation hieronder 404-en), dan concepten.
    try {
      const conceptIdSetLocal = conceptIdSet;
      for (const rid of relationIds) {
        const snap = relationSnapshots.find((s) => s.relation.id === rid);
        if (!snap) continue;
        const r = snap.relation;
        if (conceptIdSetLocal.has(r.sourceConceptId) || conceptIdSetLocal.has(r.targetConceptId)) {
          continue; // cascade pakt 'm op
        }
        try {
          await api.deleteRelation(workspaceId, branchId, rid, actionId);
        } catch (err) {
          // eslint-disable-next-line no-console
          console.error(`api.deleteRelation(${rid}) failed`, err);
        }
      }
      for (const id of conceptIds) {
        try {
          await deleteConceptWithImpactRetry(workspaceId, branchId, id, actionId);
        } catch (err) {
          // eslint-disable-next-line no-console
          console.error(`api.deleteConcept(${id}) failed`, err);
        }
      }
    } catch (err) {
      set({ lastError: explainError(err) });
    }

    // ── Combined undo ─────────────────────────────────────────────
    if (conceptSnapshots.length > 0 || relationSnapshots.length > 0) {
      pushRemoveBatchUndo(workspaceId, branchId, conceptSnapshots, relationSnapshots, diagramId);
    }
  },

  pushUserUndo: (entry) => {
    // Tijdens een open batch verzamelen we i.p.v. direct te pushen.
    if (userUndoBatch !== null) {
      userUndoBatch.push(entry);
      return;
    }
    set((s) => {
      const stack = [...s.userUndoStack, entry];
      if (stack.length > USER_UNDO_LIMIT) stack.shift();
      return { userUndoStack: stack, userRedoStack: [] };
    });
  },

  beginUserUndoBatch: () => {
    // Defensief: een nog-open batch (bv. drag afgebroken zonder end) eerst
    // afsluiten zodat entries niet eindeloos blijven verzamelen.
    if (userUndoBatch !== null && userUndoBatch.length > 0) {
      get().endUserUndoBatch("Batch");
    }
    userUndoBatch = [];
  },

  endUserUndoBatch: (label) => {
    const batch = userUndoBatch;
    userUndoBatch = null;
    if (!batch || batch.length === 0) return;
    // Eén entry → geen wrapper nodig; meerdere → composite (undo in omgekeerde
    // volgorde, redo in originele volgorde).
    const entry: UserUndoEntry =
      batch.length === 1 && batch[0]
        ? batch[0]
        : {
            label,
            undo: async () => {
              for (let i = batch.length - 1; i >= 0; i--) await batch[i]?.undo();
            },
            redo: async () => {
              for (const e of batch) await e.redo();
            },
            children: batch,
            viewStateKeys: batch.flatMap((e) => e.viewStateKeys ?? []),
          };
    get().pushUserUndo(entry);
  },

  mergeUserUndoSince: (marker, label) => {
    const stack = get().userUndoStack;
    const idx = stack.lastIndexOf(marker);
    if (idx < 0 || idx === stack.length - 1) return;
    const batch = stack.slice(idx);
    const entry: UserUndoEntry = {
      label,
      undo: async () => {
        for (let i = batch.length - 1; i >= 0; i--) await batch[i]?.undo();
      },
      redo: async () => {
        for (const e of batch) await e.redo();
      },
      children: batch,
      viewStateKeys: batch.flatMap((e) => e.viewStateKeys ?? []),
    };
    set({ userUndoStack: [...stack.slice(0, idx), entry] });
  },

  userUndo: async () => {
    const stack = [...get().userUndoStack];
    const entry = stack.pop();
    if (entry) {
      set((s) => ({
        userUndoStack: stack,
        userRedoStack: [...s.userRedoStack, entry],
      }));
      try {
        await entry.undo();
      } catch (err) {
        useWorkspaceData.setState({ lastError: explainError(err) });
      }
      // Forceer edge-geometrie-hermeting (endpoint-lag na store-gedreven revert).
      set((s) => ({ canvasGeometryNonce: s.canvasGeometryNonce + 1 }));
      return;
    }
    // Geen in-memory entry — typisch na een page-reload, die de undo-historie
    // (in-geheugen) wist. Een delete is echter persistent (tombstone), dus
    // vallen we terug op het herstellen van de meest recente weggegooide batch
    // uit de Prullenbak. Zo werkt CMD+Z ook ná een reload, en chaint herhaald
    // CMD+Z vanzelf door (elke restore haalt die batch uit de Prullenbak).
    await restoreLatestTrashedBatch();
  },

  userRedo: async () => {
    const stack = [...get().userRedoStack];
    const entry = stack.pop();
    if (!entry) return;
    set((s) => ({
      userRedoStack: stack,
      userUndoStack: [...s.userUndoStack, entry],
    }));
    try {
      await entry.redo();
    } catch (err) {
      useWorkspaceData.setState({ lastError: explainError(err) });
    }
    set((s) => ({ canvasGeometryNonce: s.canvasGeometryNonce + 1 }));
  },

  clearError: () => set({ lastError: null }),
}));

// ---- Helpers --------------------------------------------------------------

/** Eigenaar van een nieuw object (optimistisch; de worker zet de echte). */
function getOwnerEmail(): string {
  return useSession.getState().me?.email ?? "";
}

/**
 * Compute defaults for a concept type's REQUIRED attributes. The registry
 * marks fields like `c4_level` (ApplicationComponent) and
 * `data_classification` (DataObject) as required-enum, which means a
 * create without an explicit value would fail server-side with
 * "1 field(s) invalid for type …". For each required attribute we pick:
 *   - enum: de TBD/NTB-placeholder (nooit stil de eerste optie);
 *   - bool: false; int: 0; string: "TBD".
 * Optional fields (`type` ending in `?`) are left untouched — they don't
 * need a placeholder.
 */
function defaultAttributesForType(
  typeId: string,
  stereotypes?: readonly string[],
): Record<string, unknown> {
  const reg = getRegistry();
  const elementType = reg.getElementType(typeId);
  // Hergebruik de gedeelde helper uit @ae/domain — zorgt dat client en
  // worker exact dezelfde defaults toepassen. Lege values-map als basis
  // omdat dit pad voor verse drag-from-palette is (geen bestaande data).
  let out: Record<string, unknown> = elementType?.attributes?.length
    ? fillMissingRequiredAttributes(elementType.attributes, {})
    : {};
  // Stereotype-attributen meenemen — anders blijven required velden op
  // bv. «Concept Constraint» (rule_statement / rule_type) leeg en
  // ziet de gebruiker meteen rode "Verplicht veld is leeg"-warnings
  // bij drag-from-palette. NTB/TBD-sentinel-pair voor bilingual_string,
  // TBD-placeholder voor enum, etc. — exact dezelfde defaults als
  // base-type-attrs.
  if (stereotypes && stereotypes.length > 0) {
    for (const stId of stereotypes) {
      const st = reg.getStereotype(stId);
      if (!st?.attributes?.length) continue;
      out = fillMissingRequiredAttributes(st.attributes, out);
    }
  }
  return out;
}

function placeOnDiagram(
  state: WorkspaceStoreState,
  conceptId: string,
  position: { x: number; y: number },
  workspaceId: string,
  branchId: string,
): void {
  const diagramId = state.activeDiagramId;
  if (!diagramId) {
    return;
  }
  state.store.setViewElementState(diagramId, conceptId, {
    type: "object",
    ref: conceptId,
    position,
  });
  schedulePersistDiagram(state, diagramId, workspaceId, branchId);
}

function schedulePersistDiagram(
  state: WorkspaceStoreState,
  diagramId: string,
  workspaceId: string,
  branchId: string,
): void {
  // Alleen kijken: geen viewState naar de server. Ook niet de stille
  // migraties die een canvas bij het openen doet (swimlane, roadmap-matrix).
  if (useSession.getState().readOnly) return;
  const existing = pendingDiagramPatches.get(diagramId);
  if (existing?.timer) {
    clearTimeout(existing.timer);
  }
  // De stand van vóór onze eigen wijziging. Alleen bij de eerste planning
  // vastleggen; een herplanning binnen dezelfde debounce hoort dezelfde
  // basis te houden. Hieruit leiden we bij het flushen af wat wij hebben
  // wéggehaald — de server voegt namelijk samen en kan een verwijdering
  // niet meer afleiden uit "staat er niet in".
  const baseline = existing?.baseline ?? {
    // De stand van de server, niet die van de store: de store is op dit moment
    // al bijgewerkt door de actie die ons aanriep.
    ...(serverViewStates.get(diagramId) ?? state.store.getDiagram(diagramId)?.viewState ?? {}),
  };
  const timer = setTimeout(() => {
    // BELANGRIJK: de in-flight-marker NIET wissen vóór de netwerk-call. De
    // patch zelf triggert een CRDT-tick → self-`hydrate` → `store.replace`;
    // valt die rehydrate tijdens de request, dan zou 'ie de lokale (net
    // gesleepte) viewState overschrijven met de nog-oude server-versie → het
    // object springt terug. Door de marker (timer:null = "aan het flushen,
    // niet meer gepland") vast te houden tot de RESPONSE binnen is, behoudt
    // de hydrate-guard de lokale viewState gedurende de hele round-trip.
    pendingDiagramPatches.set(diagramId, { diagramId, timer: null, baseline });
    const diagram = state.store.getDiagram(diagramId);
    if (!diagram) {
      pendingDiagramPatches.delete(diagramId);
      return;
    }
    // Alleen sturen wat afwijkt van wat de server heeft. Stuur je alles mee,
    // dan telt élke entry die lokaal verouderd is als een wijziging — en krijg
    // je een weigering over vakjes waar je niet eens aan zat. Scheelt bovendien
    // een hoop verkeer: een versleping stuurde tot 2026-08-19 het hele diagram
    // op.
    const { gewijzigd, verwijderd: removed } = bepaalWatErHeenMoet(
      baseline as Record<string, ViewElementState>,
      diagram.viewState as Record<string, ViewElementState>,
      sameViewEntry,
    );
    void api
      .patchDiagram(workspaceId, branchId, diagramId, {
        view_state: gewijzigd,
        ...(diagram.rowVersion !== undefined ? { row_version: diagram.rowVersion } : {}),
        ...(removed.length > 0 ? { view_state_removed: removed } : {}),
        attributes: diagram.attributes,
      })
      .then((response) => {
        // Copy-on-write: server maakte een branch-eigen kopie met een
        // nieuwe id. Verleg activeDiagramId + in-memory store-record naar
        // de kopie zodat verdere clicks/mutaties op de juiste record
        // landen, en de oude main-record onaangeroerd blijft.
        if (response.branchCopied && response.id !== diagramId) {
          handleDiagramBranchCopy(diagramId, response);
        }
        const geweigerd = response.viewStateRejected ?? [];
        if (geweigerd.length > 0) {
          handleRejectedViewStateKeys(
            response.id,
            geweigerd,
            response.viewState ?? {},
            baseline as Record<string, ViewElementState>,
            (sleutels) => {
              // Opnieuw aanbieden. De lokale entry draagt de wens van de
              // modelleur én inmiddels de verse teller; door 'm uit het
              // uitgangspunt te laten telt 'ie bij de volgende ronde als
              // wijziging en gaat 'ie dus mee.
              const zonder = { ...baseline };
              for (const sleutel of sleutels) delete zonder[sleutel];
              pendingDiagramPatches.set(response.id, {
                diagramId: response.id,
                timer: null,
                baseline: zonder,
              });
              schedulePersistDiagram(
                useWorkspaceData.getState(),
                response.id,
                workspaceId,
                branchId,
              );
            },
          );
        }
        // De stand ná deze schrijfactie vasthouden — de rij-teller én de teller
        // per voorkomen. Zonder dit schrijft de volgende patch met verouderde
        // versies: de snelle route in `mergeViewState` vervalt, élke entry
        // wordt per sleutel vergeleken, en de client krijgt z'n eigen
        // wijzigingen als weigering terug. Symptoom: alles wat je ná de eerste
        // opslag tekent is na een herlaad verdwenen.
        // Wat de server nu heeft — het uitgangspunt voor de volgende patch.
        serverViewStates.set(response.id, {
          ...((response.viewState ?? {}) as Record<string, ViewElementState>),
        });
        const winkel = useWorkspaceData.getState().store;
        const versVersie = (response as { rowVersion?: number }).rowVersion;
        if (typeof versVersie === "number") {
          winkel.noteRowVersion("diagram", response.id, versVersie);
        }
        const afgewezen = new Set(geweigerd);
        const versies = new Map<string, number>();
        for (const [sleutel, entry] of Object.entries(response.viewState ?? {})) {
          if (afgewezen.has(sleutel)) continue;
          // Geslaagd: de herkansing-teller mag weer op nul, anders is een
          // voorkomen dat ooit één keer botste de rest van de sessie
          // uitgesloten van een nieuwe poging.
          geweigerdeHerkansingen.delete(`${response.id}:${sleutel}`);
          const v = (entry as { version?: number } | null)?.version;
          if (typeof v === "number") versies.set(sleutel, v);
        }
        winkel.noteViewStateVersions(response.id, versies);
      })
      .catch((err) => {
        useWorkspaceData.setState({ lastError: explainError(err) });
      })
      .finally(() => {
        // Alleen wissen als er ondertussen geen NIEUWE patch is ingepland
        // (dan staat er een verse timer en moet die z'n eigen leven leiden).
        const cur = pendingDiagramPatches.get(diagramId);
        if (cur && cur.timer === null) pendingDiagramPatches.delete(diagramId);
      });
  }, PERSIST_DEBOUNCE_MS);
  pendingDiagramPatches.set(diagramId, { diagramId, timer, baseline });
}

/**
 * Verwerk de branch-copy van een relation: vervang het record in de
 * in-memory store, verleg de UI-selection (AttributePanel volgt mee),
 * en pas viewState-keys aan in de meegekopieerde diagrams. Renamed
 * diagrams kunnen ook de active diagram zijn — switch activeDiagramId
 * naar de kopie indien nodig.
 */
/**
 * Verwerk de branch-copy van een concept: vervang het record in de
 * in-memory store, verleg de UI-selection (AttributePanel volgt mee),
 * en pas viewState-keys aan in de meegekopieerde diagrams. Renamed
 * diagrams kunnen ook de active diagram zijn — switch activeDiagramId
 * naar de kopie indien nodig.
 */
function handleConceptBranchCopy(
  oldId: string,
  copy: Concept,
  diagramRenames: Array<{ oldDiagramId: string; newDiagramId: string }>,
): void {
  const renamedOldIds = new Set(diagramRenames.map((r) => r.oldDiagramId));
  useWorkspaceData.setState((s) => {
    s.store.transaction(() => {
      s.store.removeConcept(oldId);
      s.store.upsertConcept(copy);
      // Frontend-tegenhanger van cascadeRelationEndpointRemap: relaties in de
      // store die naar het oude concept-id wezen krijgen hun endpoint
      // herwezen naar de kopie. Zonder dit verwijst de relatie naar het
      // zojuist-verwijderde oude concept → React Flow dropt de edge tot de
      // volgende hydrate (symptoom: net-toegevoegde relatie verdwijnt na
      // rename, komt terug na branch-switch).
      for (const r of s.store.listRelations()) {
        const srcMatch = r.sourceConceptId === oldId;
        const tgtMatch = r.targetConceptId === oldId;
        if (srcMatch || tgtMatch) {
          s.store.upsertRelation({
            ...r,
            ...(srcMatch ? { sourceConceptId: copy.id } : {}),
            ...(tgtMatch ? { targetConceptId: copy.id } : {}),
          });
        }
      }
      for (const ren of diagramRenames) {
        const oldDiagram = s.store.getDiagram(ren.oldDiagramId);
        if (oldDiagram) {
          const next = { ...oldDiagram, id: ren.newDiagramId };
          const vs = { ...next.viewState };
          // ViewState-entries kunnen ofwel direct op concept-id wijzen
          // (canonical placement, key === conceptId) of via conceptRef
          // (extra instances). Beide patronen verleggen we naar de
          // nieuwe id zodat de canvas-render het kopie-record toont.
          if (oldId in vs) {
            vs[copy.id] = vs[oldId]!;
            delete vs[oldId];
          }
          // Multi-instance: remap zowel `ref` (nieuw) als `conceptRef`
          // (legacy) wanneer ze naar de oude concept-id wijzen.
          for (const [key, val] of Object.entries(vs)) {
            if (!val) continue;
            const v = val as { ref?: string; conceptRef?: string };
            const refMatch = v.ref === oldId;
            const conceptRefMatch = v.conceptRef === oldId;
            if (refMatch || conceptRefMatch) {
              vs[key] = {
                ...val,
                ...(refMatch ? { ref: copy.id } : {}),
                ...(conceptRefMatch ? { conceptRef: copy.id } : {}),
              };
            }
          }
          next.viewState = vs;
          s.store.upsertDiagram(next);
          s.store.removeDiagram(ren.oldDiagramId);
        }
      }
      // Scenario B: diagrammen die al op de active branch staan worden
      // server-side NIET gekopieerd (geen diagramRename) maar hun viewState-
      // ref is wél in de DB geremapt. Werk de in-memory store-kopie bij zodat
      // de node niet verdwijnt tot de volgende hydrate. Sla de zojuist
      // hernoemde/verwijderde diagrammen over (al afgehandeld hierboven).
      for (const d of s.store.listDiagrams()) {
        if (renamedOldIds.has(d.id) || d.id === copy.id) continue;
        let changed = false;
        const vs = { ...d.viewState };
        for (const [key, val] of Object.entries(vs)) {
          if (!val) continue;
          const v = val as { ref?: string; conceptRef?: string };
          const refMatch = v.ref === oldId;
          const conceptRefMatch = v.conceptRef === oldId;
          if (refMatch || conceptRefMatch) {
            vs[key] = {
              ...val,
              ...(refMatch ? { ref: copy.id } : {}),
              ...(conceptRefMatch ? { conceptRef: copy.id } : {}),
            };
            changed = true;
          }
        }
        if (changed) s.store.upsertDiagram({ ...d, viewState: vs });
      }
    });
    let nextActive = s.activeDiagramId;
    let nextOpen = s.openDiagramIds;
    for (const ren of diagramRenames) {
      if (nextActive === ren.oldDiagramId) nextActive = ren.newDiagramId;
      nextOpen = nextOpen.map((id) => (id === ren.oldDiagramId ? ren.newDiagramId : id));
    }
    // Geen `revision: s.revision + 1` hier — de `s.store.transaction(...)`
    // hierboven bumpt + sync't de revision al zelf (zie `bumpRevision`-
    // comment). Een stale directe zet op dit punt kan de correcte sync
    // overschrijven met een verouderde waarde.
    return {
      activeDiagramId: nextActive,
      openDiagramIds: nextOpen,
    };
  });
  if (useUi.getState().selectedConceptId === oldId) {
    useUi.getState().selectConcept(copy.id);
  }
}

function handleRelationBranchCopy(
  oldId: string,
  copy: Relation,
  diagramRenames: Array<{ oldDiagramId: string; newDiagramId: string }>,
): void {
  useWorkspaceData.setState((s) => {
    s.store.transaction(() => {
      // Vervang relation in store
      s.store.removeRelation(oldId);
      s.store.upsertRelation(copy);
      // ViewState-key rename in elke meegekopieerde diagram
      for (const ren of diagramRenames) {
        const oldDiagram = s.store.getDiagram(ren.oldDiagramId);
        if (oldDiagram) {
          // Maak een nieuwe diagram-record met de nieuwe id en de
          // viewState-key omgezet van oldRelationId naar copy.id
          const next = { ...oldDiagram, id: ren.newDiagramId };
          const vs = { ...next.viewState };
          if (oldId in vs) {
            vs[copy.id] = vs[oldId]!;
            delete vs[oldId];
          }
          next.viewState = vs;
          s.store.upsertDiagram(next);
          s.store.removeDiagram(ren.oldDiagramId);
        }
      }
    });
    // ActiveDiagram remappen als 'ie tussen de gekopieerde diagrams zit
    let nextActive = s.activeDiagramId;
    let nextOpen = s.openDiagramIds;
    for (const ren of diagramRenames) {
      if (nextActive === ren.oldDiagramId) nextActive = ren.newDiagramId;
      nextOpen = nextOpen.map((id) => (id === ren.oldDiagramId ? ren.newDiagramId : id));
    }
    // Geen `revision: s.revision + 1` hier — de `s.store.transaction(...)`
    // hierboven bumpt + sync't de revision al zelf (zie `bumpRevision`-
    // comment). Een stale directe zet op dit punt kan de correcte sync
    // overschrijven met een verouderde waarde.
    return {
      activeDiagramId: nextActive,
      openDiagramIds: nextOpen,
    };
  });
  // UI-selection volgen — AttributePanel rendert de kopie
  useUi.getState().selectRelation(copy.id);
}

/**
 * Verwerk de respons van een PATCH die een branch-copy heeft gemaakt
 * (server-side copy-on-write). Verleg de active-diagram, voeg het
 * nieuwe record toe aan de in-memory store, verwijder het oude record
 * (dat blijft fysiek op main maar hoort op deze branch niet meer
 * zichtbaar te zijn — de Explorer-overlay-SELECT verbergt 'm via origin_id).
 */
function handleDiagramBranchCopy(
  oldId: string,
  copy: {
    id: string;
    name: BilingualText;
    description?: BilingualText;
    viewKind?: string;
    viewState?: ViewState;
    notationPack?: string;
  },
): void {
  useWorkspaceData.setState((s) => {
    const old = s.store.getDiagram(oldId);
    s.store.transaction(() => {
      // Insert nieuwe record met de viewState die we al LOKAAL hadden. Dit
      // is de bron van waarheid: `handleDiagramBranchCopy` draait vanuit de
      // patch-`.then`, dus de lokale store bevat exact wat we net naar de
      // server stuurden — inclusief een zojuist gesleepte node-positie. De
      // `copy.viewState` uit de server-response kan de pre-patch-staat van de
      // net-aangemaakte branch-kopie zijn (zonder die drag); die voorrang
      // geven liet het object terugspringen bij de EERSTE mutatie op een
      // feature-branch (copy-on-write), waarna latere drags — nu branch-owned,
      // dus geen copy-on-write meer — wél bleven staan. `old` bestaat hier
      // altijd (we hadden 'm net); `copy.viewState` is enkel een vangnet.
      s.store.upsertDiagram({
        id: copy.id,
        name: copy.name,
        ...(copy.description ? { description: copy.description } : {}),
        notationPack: copy.notationPack ?? old?.notationPack ?? "archimate",
        viewKind: copy.viewKind ?? old?.viewKind ?? "archimate.anything",
        viewState: old?.viewState ?? copy.viewState ?? {},
        attributes: old?.attributes ?? {},
        stereotypes: old?.stereotypes ?? [],
      });
      // De oude record (op main) hoort op deze branch niet meer zichtbaar.
      // Verwijder uit in-memory store; bij volgende refresh komt 'ie via
      // de overlay-NOT-IN-clause ook niet meer terug.
      s.store.removeDiagram(oldId);
    });
    // Verleg active-diagram naar de kopie zodat clicks landen op het
    // juiste record. Verleg ook de open-diagram-tab.
    const nextOpen = s.openDiagramIds.map((id) => (id === oldId ? copy.id : id));
    // Geen `revision: s.revision + 1` hier — de `s.store.transaction(...)`
    // hierboven bumpt + sync't de revision al zelf (zie `bumpRevision`-
    // comment). Dit was de exacte plek van de "vereenvoudigde-kopie-object-
    // springt-terug"-bug: een stale `s.revision`-snapshot kon de correcte
    // sync overschrijven zodra de twee tellers toevallig samenvielen.
    return {
      activeDiagramId: copy.id,
      openDiagramIds: nextOpen,
    };
  });
  // UI-selection volgen — AttributePanel rendert de kopie wanneer
  // gebruiker 'm via Explorer geselecteerd had.
  if (useUi.getState().selectedDiagramId === oldId) {
    useUi.getState().selectDiagram(copy.id);
  }
}

function replaceConceptId(
  state: WorkspaceStoreState,
  oldId: string,
  persisted: Concept,
  workspaceId: string,
  branchId: string,
): void {
  state.store.transaction(() => {
    state.store.removeConcept(oldId);
    state.store.upsertConcept(persisted);
    // Move the view-state entry from oldId to the persisted id. Sinds
    // Fase 2 (2026-05-26) is `ref` required en wijst naar de
    // concept-UUID — we moeten 'm mee-updaten naar de persisted id,
    // anders blijft 'ie naar de inmiddels-verwijderde optimistic-id
    // wijzen en faalt elke downstream `getConcept(ve.ref)` (o.a. BMC
    // slot-render in CanvasGridLayout).
    const diagramId = state.activeDiagramId;
    if (diagramId) {
      const diagram = state.store.getDiagram(diagramId);
      const old = diagram?.viewState[oldId];
      if (diagram && old) {
        const migrated: ViewElementState = { ...old, ref: persisted.id };
        const next = {
          ...diagram,
          viewState: { ...diagram.viewState, [persisted.id]: migrated },
        };
        delete next.viewState[oldId];
        state.store.upsertDiagram(next);
      }
    }
  });
  if (state.activeDiagramId) {
    schedulePersistDiagram(state, state.activeDiagramId, workspaceId, branchId);
  }
}

function replaceRelationId(state: WorkspaceStoreState, oldId: string, persisted: Relation): void {
  state.store.transaction(() => {
    state.store.removeRelation(oldId);
    state.store.upsertRelation(persisted);
    // Migrate the per-diagram visibility marker (set by `connectConcepts`
    // for per-diagram-scoping) and any waypoints / lineColor / etc. from
    // the optimistic id to the server-issued one. We walk every diagram
    // because the entry could in principle exist on more than one (drag-
    // from-explorer adds entries on whichever canvas it's dropped onto).
    for (const diagram of state.store.listDiagrams()) {
      const old = diagram.viewState[oldId];
      if (!old) continue;
      // ref mee-updaten — zelfde reden als bij replaceConceptId.
      const migrated: ViewElementState = { ...old, ref: persisted.id };
      const nextViewState = { ...diagram.viewState, [persisted.id]: migrated };
      delete nextViewState[oldId];
      state.store.upsertDiagram({ ...diagram, viewState: nextViewState });
    }
  });
}

function applyConceptPatch(existing: Concept, patch: ConceptPatch): Concept {
  return {
    ...existing,
    ...(patch.name ? { name: { ...existing.name, ...patch.name } } : {}),
    ...(patch.description
      ? { description: { ...existing.description, ...patch.description } }
      : {}),
    ...(patch.attributes ? { attributes: { ...existing.attributes, ...patch.attributes } } : {}),
    ...(patch.tags ? { tags: patch.tags } : {}),
    ...(patch.type_id ? { typeId: patch.type_id } : {}),
    ...(patch.parent_id !== undefined ? { parentId: patch.parent_id } : {}),
    ...(patch.stereotypes ? { stereotypes: patch.stereotypes } : {}),
    ...(patch.alt_labels ? { altLabels: { ...existing.altLabels, ...patch.alt_labels } } : {}),
    version: existing.version + 1,
  };
}

function mergeConceptPatch(a: ConceptPatch, b: ConceptPatch): ConceptPatch {
  const out: ConceptPatch = { ...a, ...b };
  if (a.name || b.name) out.name = { ...(a.name ?? {}), ...(b.name ?? {}) };
  if (a.description || b.description)
    out.description = { ...(a.description ?? {}), ...(b.description ?? {}) };
  if (a.attributes || b.attributes)
    out.attributes = { ...(a.attributes ?? {}), ...(b.attributes ?? {}) };
  // Alt-labels mergen per resolver-sleutel (net als attributes) zodat een
  // sharp-edit en een c4-edit in dezelfde debounce-batch beide overleven.
  if (a.alt_labels || b.alt_labels)
    out.alt_labels = { ...(a.alt_labels ?? {}), ...(b.alt_labels ?? {}) };
  // Stereotypes is een hele-array-replace (geen merge per element); de
  // laatste write wint zoals bij tags.
  if (b.stereotypes !== undefined) out.stereotypes = b.stereotypes;
  else if (a.stereotypes !== undefined) out.stereotypes = a.stereotypes;
  return out;
}

function applyRelationPatch(existing: Relation, patch: RelationPatch): Relation {
  return {
    ...existing,
    ...(patch.type_id ? { typeId: patch.type_id } : {}),
    ...(patch.source_concept_id ? { sourceConceptId: patch.source_concept_id } : {}),
    ...(patch.target_concept_id ? { targetConceptId: patch.target_concept_id } : {}),
    ...(patch.label ? { label: { ...existing.label, ...patch.label } } : {}),
    ...(patch.attributes ? { attributes: { ...existing.attributes, ...patch.attributes } } : {}),
    ...(patch.stereotypes ? { stereotypes: patch.stereotypes } : {}),
    ...(patch.alt_labels ? { altLabels: { ...existing.altLabels, ...patch.alt_labels } } : {}),
    version: existing.version + 1,
  };
}

function mergeRelationPatch(a: RelationPatch, b: RelationPatch): RelationPatch {
  const out: RelationPatch = { ...a, ...b };
  if (a.label || b.label) out.label = { ...(a.label ?? {}), ...(b.label ?? {}) };
  if (a.attributes || b.attributes)
    out.attributes = { ...(a.attributes ?? {}), ...(b.attributes ?? {}) };
  if (a.alt_labels || b.alt_labels)
    out.alt_labels = { ...(a.alt_labels ?? {}), ...(b.alt_labels ?? {}) };
  // Stereotypes is een hele-array-replace — laatste write wint.
  if (b.stereotypes !== undefined) out.stereotypes = b.stereotypes;
  else if (a.stereotypes !== undefined) out.stereotypes = a.stereotypes;
  return out;
}

function sameConceptContent(a: Concept, b: Concept): boolean {
  return (
    JSON.stringify(a.name) === JSON.stringify(b.name) &&
    JSON.stringify(a.description) === JSON.stringify(b.description) &&
    JSON.stringify(a.attributes) === JSON.stringify(b.attributes) &&
    JSON.stringify(a.tags) === JSON.stringify(b.tags) &&
    JSON.stringify(a.altLabels) === JSON.stringify(b.altLabels) &&
    a.typeId === b.typeId &&
    a.parentId === b.parentId
  );
}

function sameRelationContent(a: Relation, b: Relation): boolean {
  return (
    JSON.stringify(a.label) === JSON.stringify(b.label) &&
    JSON.stringify(a.attributes) === JSON.stringify(b.attributes) &&
    JSON.stringify(a.altLabels) === JSON.stringify(b.altLabels) &&
    a.typeId === b.typeId &&
    a.sourceConceptId === b.sourceConceptId &&
    a.targetConceptId === b.targetConceptId
  );
}

function buildConceptUndoEntry(
  workspaceId: string,
  branchId: string,
  conceptId: string,
  baseline: Concept,
  after: Concept,
): UserUndoEntry {
  const apply = (target: Concept) => {
    // Cancel any in-flight debounce so the undo wins.
    const t = pendingConceptPatches.get(conceptId);
    if (t) {
      clearTimeout(t);
      pendingConceptPatches.delete(conceptId);
    }
    conceptPatchAccumulators.delete(conceptId);
    conceptPatchBaselines.delete(conceptId);
    useWorkspaceData.getState().store.upsertConcept(target);
    void api
      .patchConcept(workspaceId, branchId, conceptId, {
        name: target.name,
        description: target.description,
        attributes: target.attributes,
        tags: target.tags,
        type_id: target.typeId,
        parent_id: target.parentId,
      })
      .catch((err) => {
        useWorkspaceData.setState({ lastError: explainError(err) });
      });
  };
  return {
    label: "Bewerk concept",
    undo: () => apply(baseline),
    redo: () => apply(after),
  };
}

function buildRelationUndoEntry(
  workspaceId: string,
  branchId: string,
  relationId: string,
  baseline: Relation,
  after: Relation,
): UserUndoEntry {
  const apply = (target: Relation) => {
    const t = pendingRelationPatches.get(relationId);
    if (t) {
      clearTimeout(t);
      pendingRelationPatches.delete(relationId);
    }
    relationPatchAccumulators.delete(relationId);
    relationPatchBaselines.delete(relationId);
    useWorkspaceData.getState().store.upsertRelation(target);
    void api
      .patchRelation(workspaceId, branchId, relationId, {
        type_id: target.typeId,
        source_concept_id: target.sourceConceptId,
        target_concept_id: target.targetConceptId,
        label: target.label,
        attributes: target.attributes,
      })
      .catch((err) => {
        useWorkspaceData.setState({ lastError: explainError(err) });
      });
  };
  return {
    label: "Bewerk relatie",
    undo: () => apply(baseline),
    redo: () => apply(after),
  };
}

/**
 * Pushes a user-undo entry for "add concept" so Cmd+Z removes the concept
 * and Cmd+Shift+Z brings it back. The entry's internal `currentId` is
 * remapped each time the concept is recreated, since the server assigns a
 * fresh UUID on every POST.
 */
function pushAddConceptUndo(
  workspaceId: string,
  branchId: string,
  initialConcept: Concept,
  position: { x: number; y: number },
): void {
  let currentId = initialConcept.id;
  let snapshot: Concept = initialConcept;

  useWorkspaceData.getState().pushUserUndo({
    label: "Voeg concept toe",
    undo: async () => {
      const ws = useWorkspaceData.getState();
      const cur = ws.store.getConcept(currentId);
      if (cur) snapshot = cur;
      ws.store.removeConcept(currentId);
      removeFromActiveDiagram(ws, currentId, workspaceId, branchId);
      try {
        await deleteConceptWithImpactRetry(workspaceId, branchId, currentId);
      } catch (err) {
        useWorkspaceData.setState({ lastError: explainError(err) });
      }
    },
    redo: async () => {
      const ws = useWorkspaceData.getState();
      try {
        const created = await api.createConcept(workspaceId, branchId, {
          type_id: snapshot.typeId,
          name: snapshot.name,
          description: snapshot.description,
          attributes: snapshot.attributes,
          tags: snapshot.tags,
          parent_id: snapshot.parentId,
          extends_type_id: snapshot.extendsTypeId,
          owner_email: snapshot.ownerEmail,
        });
        currentId = created.id;
        snapshot = created;
        ws.store.upsertConcept(created);
        const diagramId = ws.activeDiagramId;
        if (diagramId) {
          ws.store.setViewElementState(diagramId, created.id, {
            type: "object",
            ref: created.id,
            position,
          });
          schedulePersistDiagram(ws, diagramId, workspaceId, branchId);
        }
      } catch (err) {
        useWorkspaceData.setState({ lastError: explainError(err) });
      }
    },
  });
}

function pushAddRelationUndo(
  workspaceId: string,
  branchId: string,
  initialRelation: Relation,
): void {
  let currentId = initialRelation.id;
  let snapshot: Relation = initialRelation;

  useWorkspaceData.getState().pushUserUndo({
    label: "Voeg relatie toe",
    undo: async () => {
      const ws = useWorkspaceData.getState();
      const cur = ws.store.getRelation(currentId);
      if (cur) snapshot = cur;
      ws.store.removeRelation(currentId);
      removeFromActiveDiagram(ws, currentId, workspaceId, branchId);
      try {
        await api.deleteRelation(workspaceId, branchId, currentId);
      } catch (err) {
        useWorkspaceData.setState({ lastError: explainError(err) });
      }
    },
    redo: async () => {
      const ws = useWorkspaceData.getState();
      try {
        const created = await api.createRelation(workspaceId, branchId, {
          type_id: snapshot.typeId,
          source_concept_id: snapshot.sourceConceptId,
          target_concept_id: snapshot.targetConceptId,
          label: snapshot.label,
          attributes: snapshot.attributes,
          owner_email: snapshot.ownerEmail,
        });
        currentId = created.id;
        snapshot = created;
        ws.store.upsertRelation(created);
      } catch (err) {
        useWorkspaceData.setState({ lastError: explainError(err) });
      }
    },
  });
}

interface IncidentRelationSnapshot {
  relation: Relation;
  viewState: ViewElementState | undefined;
}

/**
 * Push een gecombineerde undo-entry voor een multi-delete (zie
 * `removeBatch`). Restore-logica:
 *  1. Alle concepten opnieuw aanmaken (api.createConcept levert nieuwe
 *     UUIDs op). Map old-id → new-id opbouwend tijdens.
 *  2. Alle relaties opnieuw aanmaken met endpoints vertaald via die map
 *     (een endpoint dat een te-restoreren concept was → nieuwe id; een
 *     endpoint dat al bestaat blijft ongewijzigd).
 *  3. ViewState herstellen onder de nieuwe ids.
 *
 * Cross-undo coordinatie die per-concept-undo NIET kan: in één
 * restore-actie zien we het complete plaatje, dus weten welke endpoint-
 * ids vertaald moeten worden.
 */
function pushRemoveBatchUndo(
  workspaceId: string,
  branchId: string,
  initialConcepts: Array<{ concept: Concept; viewState: ViewElementState | undefined }>,
  initialRelations: Array<{ relation: Relation; viewState: ViewElementState | undefined }>,
  diagramId: string | null,
): void {
  // Snapshots muteren tijdens undo/redo (nieuwe ids), dus per-entry
  // tracking via lokale closures.
  const concepts = initialConcepts.map((c) => ({
    snapshot: c.concept,
    currentId: c.concept.id,
    viewState: c.viewState,
  }));
  const relations = initialRelations.map((r) => ({
    snapshot: r.relation,
    currentId: r.relation.id,
    viewState: r.viewState,
  }));

  useWorkspaceData.getState().pushUserUndo({
    label:
      concepts.length > 0 && relations.length > 0
        ? `Verwijder ${concepts.length} concept${concepts.length === 1 ? "" : "en"} + ${relations.length} relatie${relations.length === 1 ? "" : "s"}`
        : concepts.length > 0
          ? `Verwijder ${concepts.length} concept${concepts.length === 1 ? "" : "en"}`
          : `Verwijder ${relations.length} relatie${relations.length === 1 ? "" : "s"}`,
    undo: async () => {
      const ws = useWorkspaceData.getState();
      // Un-tombstone de ORIGINELEN via het trash-restore-pad: zelfde ids, géén
      // re-create. Dit voorkomt duplicaten en tombstone-rommel bij herhaalde
      // delete/undo-cycli. restoreItems auto-herstelt incident-relaties en
      // skipt dangling endpoints server-side.
      try {
        await api.restoreTrash(workspaceId, branchId, {
          concept_ids: concepts.map((c) => c.currentId),
          relation_ids: relations.map((r) => r.currentId),
        });
      } catch (err) {
        useWorkspaceData.setState({ lastError: explainError(err) });
        return;
      }
      // Lokale store + diagram-placement terugzetten. De ids zijn ongewijzigd,
      // dus de viewState-ref klopt al; we zetten 'm expliciet voor de
      // zekerheid (legacy direct-key-fallback).
      ws.store.transaction(() => {
        for (const c of concepts) {
          ws.store.upsertConcept(c.snapshot);
          if (diagramId && c.viewState) {
            ws.store.setViewElementState(diagramId, c.currentId, {
              ...c.viewState,
              ref: c.currentId,
            });
          }
        }
        for (const r of relations) {
          // Mirror de server-skip: alleen herstellen als beide endpoints leven.
          if (
            !ws.store.getConcept(r.snapshot.sourceConceptId) ||
            !ws.store.getConcept(r.snapshot.targetConceptId)
          ) {
            continue;
          }
          ws.store.upsertRelation(r.snapshot);
          if (diagramId && r.viewState) {
            ws.store.setViewElementState(diagramId, r.currentId, {
              ...r.viewState,
              ref: r.currentId,
            });
          }
        }
      });
      if (diagramId) {
        schedulePersistDiagram(useWorkspaceData.getState(), diagramId, workspaceId, branchId);
        // Spring naar het diagram waar het herstelde object op staat (A) zodat
        // de gebruiker het resultaat ziet, ook als 'ie op een ander diagram was.
        useWorkspaceData.getState().openDiagram(workspaceId, diagramId);
      }
    },
    redo: async () => {
      const actionId = crypto.randomUUID();
      const ws = useWorkspaceData.getState();
      // Reverse-volgorde: relaties eerst om FK-issues lokaal te voorkomen,
      // dan concepten. Server cascade is hetzelfde verhaal.
      for (const r of relations) {
        try {
          await api.deleteRelation(workspaceId, branchId, r.currentId, actionId);
          ws.store.removeRelation(r.currentId);
        } catch (err) {
          // eslint-disable-next-line no-console
          console.error("removeBatch.redo relation-delete failed", err);
        }
      }
      for (const c of concepts) {
        try {
          await deleteConceptWithImpactRetry(workspaceId, branchId, c.currentId, actionId);
          ws.store.removeConcept(c.currentId);
        } catch (err) {
          // eslint-disable-next-line no-console
          console.error("removeBatch.redo concept-delete failed", err);
        }
      }
    },
  });
}

/**
 * Verwijder een concept met automatische impact-bevestiging. De server eist
 * `confirm_impact` zodra het concept in >1 diagram voorkomt (409 met
 * impact_token = `delete-<id>`). De gebruiker heeft de delete al in de UI
 * bevestigd, dus we retryen met dat token. Gooit door bij elke andere fout.
 */
async function deleteConceptWithImpactRetry(
  workspaceId: string,
  branchId: string,
  conceptId: string,
  actionId?: string,
): Promise<void> {
  try {
    await api.deleteConcept(workspaceId, branchId, conceptId, undefined, actionId);
  } catch (err) {
    const token =
      err instanceof ApiError && err.status === 409
        ? (err.body as { impact_token?: string } | null)?.impact_token
        : undefined;
    if (!token) throw err;
    await api.deleteConcept(workspaceId, branchId, conceptId, token, actionId);
  }
}

/**
 * Fallback voor `userUndo` wanneer de in-memory undo-stack leeg is (typisch na
 * een page-reload): herstel de meest recente weggegooide batch uit de
 * Prullenbak. Een delete is server-side persistent (tombstone met
 * `delete_action_id`), dus de "laatste delete" is reconstrueerbaar. Na restore
 * re-hydraten we de branch zodat het herstelde materiaal terug in de store +
 * op het canvas komt. Herhaald CMD+Z chaint vanzelf door — elke restore haalt
 * die batch uit de Prullenbak, waarna de volgende de op-één-na-laatste pakt.
 */
async function restoreLatestTrashedBatch(): Promise<void> {
  const { workspaceId, branchId } = useSession.getState();
  if (!workspaceId || !branchId) return;
  try {
    const { items } = await api.listTrash(workspaceId, branchId);
    if (items.length === 0) return;
    const latest = [...items].sort((a, b) => b.tombstoned_at.localeCompare(a.tombstoned_at))[0];
    if (!latest) return;
    // Recency-guard: CMD+Z-na-reload herstelt alleen RECENTE deletes. Zonder
    // dit zou een toevallige CMD+Z (terwijl er niets recents is weggegooid) een
    // oude, bewuste delete uit de Prullenbak resurrecten. Voor oudere herstel-
    // acties is de Prullenbak-UI bedoeld. `tombstoned_at` is UTC (datetime('now')).
    const tombstonedMs = new Date(`${latest.tombstoned_at.replace(" ", "T")}Z`).getTime();
    if (Number.isFinite(tombstonedMs) && Date.now() - tombstonedMs > 60 * 60 * 1000) {
      return;
    }
    // Eén delete-actie = de gedeelde `delete_action_id`; zonder id is 't een
    // losse delete (alleen dit item).
    const batch = latest.delete_action_id
      ? items.filter((i) => i.delete_action_id === latest.delete_action_id)
      : [latest];
    const conceptIds = batch.filter((i) => i.kind === "concept").map((i) => i.id);
    const relationIds = batch.filter((i) => i.kind === "relation").map((i) => i.id);
    // Diagram waar het herstelde materiaal op stond → daar naartoe springen
    // zodat de gebruiker het resultaat ziet (gedrag A).
    const targetDiagramId =
      batch.flatMap((i) => i.placed_on_diagrams ?? []).map((d) => d.id)[0] ?? null;
    await api.restoreTrash(workspaceId, branchId, {
      ...(conceptIds.length > 0 ? { concept_ids: conceptIds } : {}),
      ...(relationIds.length > 0 ? { relation_ids: relationIds } : {}),
    });
    await useWorkspaceData.getState().hydrate(workspaceId, branchId);
    if (targetDiagramId && useWorkspaceData.getState().store.getDiagram(targetDiagramId)) {
      useWorkspaceData.getState().openDiagram(workspaceId, targetDiagramId);
    }
  } catch (err) {
    useWorkspaceData.setState({ lastError: explainError(err) });
  }
}

function pushRemoveConceptUndo(
  workspaceId: string,
  branchId: string,
  initialConcept: Concept,
  initialView: ViewElementState | undefined,
  initialIncidents: IncidentRelationSnapshot[],
  diagramId: string | null,
): void {
  // The concept's id (and the incident relations' ids) get re-minted on
  // every undo because the server assigns fresh UUIDs on POST. We track
  // the "current" ids so a follow-up redo reaches the right rows on the
  // server.
  const currentConceptId = initialConcept.id;
  let conceptSnapshot: Concept = initialConcept;
  const viewSnapshot = initialView;
  // For each incident relation, remember its current id (mutates on
  // resurrection), the snapshot of the relation as it was at delete time,
  // and the original (pre-delete) source/target so we can rewrite endpoints
  // when one of them was the concept being resurrected.
  const incidents = initialIncidents.map((s) => ({
    currentRelationId: s.relation.id,
    snapshot: s.relation,
    viewState: s.viewState,
  }));

  useWorkspaceData.getState().pushUserUndo({
    label: "Verwijder concept",
    undo: async () => {
      const ws = useWorkspaceData.getState();
      // Un-tombstone het origineel + incident-relaties via het trash-restore-
      // pad: zelfde ids, geen re-create → geen duplicaten bij herhaalde
      // delete/undo. restoreItems auto-herstelt incident-relaties + skipt
      // dangling endpoints server-side.
      try {
        await api.restoreTrash(workspaceId, branchId, {
          concept_ids: [currentConceptId],
          relation_ids: incidents.map((inc) => inc.currentRelationId),
        });
      } catch (err) {
        useWorkspaceData.setState({ lastError: explainError(err) });
        return;
      }
      ws.store.transaction(() => {
        ws.store.upsertConcept(conceptSnapshot);
        if (diagramId && viewSnapshot) {
          ws.store.setViewElementState(diagramId, currentConceptId, {
            ...viewSnapshot,
            ref: currentConceptId,
          });
        }
        for (const inc of incidents) {
          if (
            !ws.store.getConcept(inc.snapshot.sourceConceptId) ||
            !ws.store.getConcept(inc.snapshot.targetConceptId)
          ) {
            continue;
          }
          ws.store.upsertRelation(inc.snapshot);
          if (diagramId && inc.viewState) {
            ws.store.setViewElementState(diagramId, inc.currentRelationId, {
              ...inc.viewState,
              ref: inc.currentRelationId,
            });
          }
        }
      });
      if (diagramId) {
        schedulePersistDiagram(useWorkspaceData.getState(), diagramId, workspaceId, branchId);
        // Spring naar het diagram waar het herstelde object op staat (A) zodat
        // de gebruiker het resultaat ziet, ook als 'ie op een ander diagram was.
        useWorkspaceData.getState().openDiagram(workspaceId, diagramId);
      }
    },
    redo: async () => {
      // Capture the latest state of the concept + incident relations so a
      // subsequent undo restores any post-resurrection edits.
      const ws = useWorkspaceData.getState();
      const cur = ws.store.getConcept(currentConceptId);
      if (cur) conceptSnapshot = cur;
      for (const inc of incidents) {
        const curRel = ws.store.getRelation(inc.currentRelationId);
        if (curRel) inc.snapshot = curRel;
      }
      // The cascade in the server handles the relations; the local store
      // also cascades (see WorkspaceStore.removeConcept), so we don't need
      // to delete relations ourselves.
      ws.store.removeConcept(currentConceptId);
      removeFromActiveDiagram(ws, currentConceptId, workspaceId, branchId);
      try {
        await deleteConceptWithImpactRetry(workspaceId, branchId, currentConceptId);
      } catch (err) {
        useWorkspaceData.setState({ lastError: explainError(err) });
      }
    },
  });
}

function pushRemoveRelationUndo(
  workspaceId: string,
  branchId: string,
  initialRelation: Relation,
  initialView: ViewElementState | undefined,
  diagramId: string | null,
): void {
  const currentId = initialRelation.id;
  let snapshot: Relation = initialRelation;
  const viewSnapshot = initialView;

  useWorkspaceData.getState().pushUserUndo({
    label: "Verwijder relatie",
    undo: async () => {
      const ws = useWorkspaceData.getState();
      // Un-tombstone het origineel (zelfde id) i.p.v. re-creëren.
      try {
        await api.restoreTrash(workspaceId, branchId, { relation_ids: [currentId] });
      } catch (err) {
        useWorkspaceData.setState({ lastError: explainError(err) });
        return;
      }
      ws.store.upsertRelation(snapshot);
      if (diagramId && viewSnapshot) {
        ws.store.setViewElementState(diagramId, currentId, { ...viewSnapshot, ref: currentId });
        schedulePersistDiagram(ws, diagramId, workspaceId, branchId);
      }
      if (diagramId) useWorkspaceData.getState().openDiagram(workspaceId, diagramId);
    },
    redo: async () => {
      const ws = useWorkspaceData.getState();
      const cur = ws.store.getRelation(currentId);
      if (cur) snapshot = cur;
      ws.store.removeRelation(currentId);
      removeFromActiveDiagram(ws, currentId, workspaceId, branchId);
      try {
        await api.deleteRelation(workspaceId, branchId, currentId);
      } catch (err) {
        useWorkspaceData.setState({ lastError: explainError(err) });
      }
    },
  });
}

/**
 * Drop the given id from the active diagram's view_state and persist. No-op
 * when there's no active diagram or the entry is already gone.
 */
/**
 * Markeer viewState-keys als bewust-van-diagram-verwijderd (Fase-1 branch-
 * diagram-nasynchronisatie, ontwerp 2026-06-07). De keys belanden in
 * `diagram.attributes.viewStateTombstones` zodat de server-side reconcile-
 * merge ze niet opnieuw uit de parent overneemt. Branch-autoriteit: een
 * verwijdering blijft een verwijdering, ook als de parent het object later
 * verplaatst. Idempotent; persistente opslag lift mee op de eerstvolgende
 * `schedulePersistDiagram` (die de volledige attributes-blob meestuurt).
 *
 * We over-tombstonen bewust ook branch-eigen keys (die de parent niet kent):
 * dat is onschadelijk — de merge raakt zo'n key nooit aan — en bespaart een
 * dure "zit deze key in de parent?"-check die de client niet kan doen.
 */
function tombstoneViewStateKeys(
  state: WorkspaceStoreState,
  diagramId: string,
  keys: string[],
): void {
  if (keys.length === 0) return;
  const diagram = state.store.getDiagram(diagramId);
  if (!diagram) return;
  const existing = Array.isArray(diagram.attributes?.viewStateTombstones)
    ? (diagram.attributes.viewStateTombstones as unknown[]).filter(
        (x): x is string => typeof x === "string",
      )
    : [];
  const merged = Array.from(new Set([...existing, ...keys]));
  if (merged.length === existing.length) return; // niets nieuws toegevoegd
  state.store.upsertDiagram({
    ...diagram,
    attributes: { ...(diagram.attributes ?? {}), viewStateTombstones: merged },
  });
}

/**
 * Inverse van `tombstoneViewStateKeys` — haalt keys WEER uit
 * `diagram.attributes.viewStateTombstones`. Nodig bij undo van een
 * per-diagram-hide: de hide markeert de entry-key als bewust-verwijderd zodat
 * de branch-reconcile-merge 'm niet opnieuw uit de parent overneemt; bij undo
 * moet die markering verdwijnen, anders werkt de reconcile op een feature-branch
 * het herstel tegen (op main draait de reconcile niet, dus dáár viel 't niet op).
 * Idempotent.
 */
function untombstoneViewStateKeys(
  state: WorkspaceStoreState,
  diagramId: string,
  keys: string[],
): void {
  if (keys.length === 0) return;
  const diagram = state.store.getDiagram(diagramId);
  if (!diagram) return;
  const existing = Array.isArray(diagram.attributes?.viewStateTombstones)
    ? (diagram.attributes.viewStateTombstones as unknown[]).filter(
        (x): x is string => typeof x === "string",
      )
    : [];
  const remove = new Set(keys);
  const next = existing.filter((k) => !remove.has(k));
  if (next.length === existing.length) return; // niets verwijderd
  state.store.upsertDiagram({
    ...diagram,
    attributes: { ...(diagram.attributes ?? {}), viewStateTombstones: next },
  });
}

function removeFromActiveDiagram(
  state: WorkspaceStoreState,
  id: string,
  workspaceId: string,
  branchId: string,
): void {
  const diagramId = state.activeDiagramId;
  if (!diagramId) return;
  const diagram = state.store.getDiagram(diagramId);
  if (!diagram?.viewState[id]) return;
  const next = { ...diagram, viewState: { ...diagram.viewState } };
  delete next.viewState[id];
  state.store.upsertDiagram(next);
  tombstoneViewStateKeys(state, diagramId, [id]);
  schedulePersistDiagram(state, diagramId, workspaceId, branchId);
}

/**
 * Translate the server-side DTO into the runtime registry's ViewKind
 * shape. The server stores names as separate columns and the included
 * lists as JSON arrays; we pick the bilingual fallback automatically
 * so the runtime always has at least one language present.
 */
function customViewKindToViewKind(c: CustomViewKindDTO): import("@ae/domain").ViewKind {
  const name: { nl?: string; en?: string } = {};
  if (c.name.nl) name.nl = c.name.nl;
  if (c.name.en) name.en = c.name.en;
  // BilingualText needs at least one entry; fall back if both missing.
  const finalName: Record<string, string> = Object.keys(name).length > 0 ? name : { en: c.id };
  const view: import("@ae/domain").ViewKind = {
    id: c.id,
    name: finalName,
    fase1_active: true,
    custom: true,
  };
  if (c.description) {
    const description: Record<string, string> = {};
    if (c.description.nl) description.nl = c.description.nl;
    if (c.description.en) description.en = c.description.en;
    if (Object.keys(description).length > 0) view.description = description;
  }
  if (c.includedTypes.length > 0) view.included_types = c.includedTypes;
  if (c.includedRelationTypes && c.includedRelationTypes.length > 0) {
    view.included_relation_types = c.includedRelationTypes;
  }
  return view;
}

/**
 * Walk de viewState-boom en verzamel ALLE descendants van een
 * container-instance: directe kinderen, hun kinderen, enz. Gebruikt
 * door `toggleCollapseContainer` om recursief hidden-flags te zetten.
 *
 * Werkt op puur view-state — er zit geen materialized "children"-list
 * in onze schema, dus elk niveau kost een lineaire scan over de
 * viewState-Object. Voor de schaal in Fase 1 (~50 elementen per
 * diagram) is dat ruim binnen budget.
 */
function collectInstanceDescendants(
  viewState: Record<string, ViewElementState>,
  parentId: string,
): string[] {
  const out: string[] = [];
  const stack: string[] = [parentId];
  const seen = new Set<string>([parentId]);
  while (stack.length > 0) {
    const current = stack.pop()!;
    for (const [id, ve] of Object.entries(viewState)) {
      if (ve.containerId === current && !seen.has(id)) {
        seen.add(id);
        out.push(id);
        stack.push(id);
      }
    }
  }
  return out;
}

/**
 * Force a workspace-store revision bump so React subscribers re-render.
 * Routes through `sharedStore.forceRevisionBump()` — the domain store's OWN
 * counter + subscriber-sync — rather than writing `revision` on the Zustand
 * mirror directly. Two independent writers of that one field can drift and
 * collide (both landing on the same number makes the subscriber's `!==`
 * guard skip the sync), which is exactly what caused the "vereenvoudigde-
 * kopie-object-springt-terug"-bug (2026-07-11). Never reintroduce a direct
 * `set(s => ({ revision: s.revision + 1 }))` — always go through the store.
 */
function bumpRevision(): void {
  sharedStore.forceRevisionBump();
}

/**
 * Verify that switching `diagram` to `nextViewKindId` keeps every concept
 * and relation currently on the diagram inside the new view-kind's
 * allowlists. Returns `null` when the change is safe, or a localized
 * error message when something on the canvas would no longer fit.
 *
 * The check runs against the runtime registry — a missing view-kind
 * (e.g. an unknown id) is treated as "no restriction" so the change
 * still succeeds.
 */
function checkDiagramTypeChange(
  store: WorkspaceStore,
  diagram: Diagram,
  nextViewKindId: string,
): string | null {
  const view = getRegistry().getViewKind(nextViewKindId);
  if (!view) return null;
  const allowedTypes = (view.included_types?.length ?? 0) > 0 ? new Set(view.included_types) : null;
  const allowedRelations =
    (view.included_relation_types?.length ?? 0) > 0 ? new Set(view.included_relation_types) : null;

  if (allowedTypes) {
    // Walk the diagram's view_state. Each entry maps to either a concept
    // (canonical key) or an additional instance via `conceptRef`; either
    // way the underlying concept's typeId must fit. Drawing-types
    // (`drawing.*`) zijn niet-semantisch en mogen op elke view staan —
    // dus altijd compatible bij een type-wissel.
    for (const [key, ve] of Object.entries(diagram.viewState)) {
      if (ve.position === undefined) continue; // not a concept entry
      if (ve.hidden === true) continue;
      const conceptId = ve.ref ?? key;
      const concept = store.getConcept(conceptId);
      if (!concept || concept.tombstoneInBranch) continue;
      if (concept.typeId.startsWith("drawing.")) continue;
      if (!allowedTypes.has(concept.typeId)) {
        return `Type kan niet gewijzigd worden: er staat een concept van type '${concept.typeId}' op het diagram dat in '${nextViewKindId}' niet is toegestaan.`;
      }
    }
  }

  if (allowedRelations) {
    // Relations are workspace-scoped, but only relevant if both endpoints
    // are placed on this diagram (otherwise the canvas filters it out).
    // Drawing-relaties zijn ook hier matrix-vrij — type-wissel mag niet
    // blokkeren op een vrije lijn / pijl.
    const hasInstance = (id: string): boolean => {
      for (const [key, ve] of Object.entries(diagram.viewState)) {
        if (ve.position === undefined || ve.hidden === true) continue;
        if (key === id || ve.ref === id) return true;
      }
      return false;
    };
    for (const r of store.listRelations()) {
      if (r.tombstoneInBranch) continue;
      if (vsGet(diagram.viewState, r.id)?.hidden === true) continue;
      if (!hasInstance(r.sourceConceptId) || !hasInstance(r.targetConceptId)) continue;
      if (r.typeId.startsWith("drawing.")) continue;
      if (!allowedRelations.has(r.typeId)) {
        return `Type kan niet gewijzigd worden: er loopt een relatie van type '${r.typeId}' op het diagram die in '${nextViewKindId}' niet is toegestaan.`;
      }
    }
  }
  return null;
}

/** True when the relation has the given two concept ids as endpoints (in either direction). */
function relationConnectsConcepts(r: Relation, conceptA: string, conceptB: string): boolean {
  return (
    (r.sourceConceptId === conceptA && r.targetConceptId === conceptB) ||
    (r.sourceConceptId === conceptB && r.targetConceptId === conceptA)
  );
}

/**
 * Eerste (in viewState-insertievolgorde) geplaatste instance van
 * `conceptId` op dit diagram. Dezelfde resolutie als de canvas-render
 * gebruikt voor een relatie-endpoint zonder expliciete instance-pin (zie
 * `firstInstancePerConcept` + `resolveEndpointAnchor` in Canvas.tsx's
 * derived-nodes). Nodig omdat een concept soms UITSLUITEND als non-
 * canonical multi-instance op een diagram staat (instanceId !== conceptId
 * — bv. via drag-from-explorer op een reeds-elders-geplaatst concept).
 * Zonder deze resolutie zou de canonical conceptId als "rendered anchor"
 * worden aangenomen terwijl die helemaal niet als eigen viewState-entry op
 * dit diagram voorkomt, waardoor een al-bestaande relatie nooit als
 * "matcht deze instance" herkend wordt. Valt terug op `conceptId` zelf
 * wanneer geen enkele instance gevonden wordt (defensief; callers hebben
 * de concept-aanwezigheid al gecheckt).
 */
function defaultInstanceForConcept(diagram: Diagram, conceptId: string): string {
  for (const [key, ve] of Object.entries(diagram.viewState)) {
    if (ve.position === undefined) continue;
    if ((ve.ref ?? key) === conceptId) return key;
  }
  return conceptId;
}

/**
 * True when the relation is rendered between exactly the given two
 * canvas instances (in either direction). The "rendered pair" defaults
 * to `defaultInstanceForConcept` (first placed instance) unless the
 * view-state explicitly names a `sourceInstanceId` / `targetInstanceId`.
 */
function renderedPairMatches(
  diagram: Diagram,
  ve: ViewElementState | undefined,
  r: Relation,
  instanceA: string,
  instanceB: string,
): boolean {
  const renderedSource =
    ve?.sourceInstanceId ?? defaultInstanceForConcept(diagram, r.sourceConceptId);
  const renderedTarget =
    ve?.targetInstanceId ?? defaultInstanceForConcept(diagram, r.targetConceptId);
  return (
    (renderedSource === instanceA && renderedTarget === instanceB) ||
    (renderedSource === instanceB && renderedTarget === instanceA)
  );
}

/**
 * Per-workspace persistence of which diagrams have a tab open in the
 * editor. Stored in localStorage so reloading the page doesn't reset
 * the user's tab arrangement.
 */
// Branch waarvoor de open-tab-set geldt. Hydrate zet 'm bij elke
// branch/WP-switch zodat de open-tabs PER BRANCH worden onthouden i.p.v.
// per workspace. Zonder dit waren bij een switch álle diagrammen van de
// nieuwe branch "nieuw" → werden ze allemaal geopend (gebruiker wil zelf
// controle). Module-var i.p.v. session-import om circulaire deps te mijden.
let openTabsBranchKey: string | null = null;
// Huidige workspace — door hydrate gezet, zodat de active-diagram-subscription
// (die geen workspaceId-arg heeft) weet onder welke key te persisteren.
let currentWorkspaceId: string | null = null;

function openDiagramsKey(workspaceId: string): string {
  return openTabsBranchKey
    ? `ae.openDiagrams.${workspaceId}.${openTabsBranchKey}`
    : `ae.openDiagrams.${workspaceId}`;
}

function readOpenDiagramIds(workspaceId: string): string[] | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(openDiagramsKey(workspaceId));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return null;
    return parsed.filter((v): v is string => typeof v === "string");
  } catch {
    return null;
  }
}

function writeOpenDiagramIds(workspaceId: string, ids: string[]): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(openDiagramsKey(workspaceId), JSON.stringify(ids));
  } catch {
    // Storage may be unavailable; tab state is best-effort.
  }
}

// Per-branch "known diagram ids": elke diagram-id die deze branch ooit in een
// hydrate heeft gezien. Wordt gebruikt om écht-nieuwe diagrammen (net via
// import/snapshot/realtime binnengekomen → auto-open) te onderscheiden van
// diagrammen die de gebruiker bewust heeft gesloten (mogen NIET vanzelf
// terugkomen bij een branch-revisit). Zonder dit floodde elke branch-switch
// het canvas met alle ooit-gesloten tabs.
function knownDiagramsKey(workspaceId: string): string {
  return openTabsBranchKey
    ? `ae.knownDiagrams.${workspaceId}.${openTabsBranchKey}`
    : `ae.knownDiagrams.${workspaceId}`;
}

function readKnownDiagramIds(workspaceId: string): string[] | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(knownDiagramsKey(workspaceId));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return null;
    return parsed.filter((v): v is string => typeof v === "string");
  } catch {
    return null;
  }
}

function writeKnownDiagramIds(workspaceId: string, ids: string[]): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(knownDiagramsKey(workspaceId), JSON.stringify(ids));
  } catch {
    // Storage may be unavailable; tab state is best-effort.
  }
}

// Per-branch "actieve diagram": het diagram waar de gebruiker stond. Hersteld
// bij hydrate zodat een hard refresh je op hetzelfde diagram laat i.p.v. terug
// te vallen op het eerste. De subscription hieronder schrijft 'm bij elke
// activeDiagramId-wijziging weg.
function activeDiagramKey(workspaceId: string): string {
  return openTabsBranchKey
    ? `ae.activeDiagram.${workspaceId}.${openTabsBranchKey}`
    : `ae.activeDiagram.${workspaceId}`;
}

function readActiveDiagramId(workspaceId: string): string | null {
  if (typeof window === "undefined") return null;
  try {
    return window.localStorage.getItem(activeDiagramKey(workspaceId));
  } catch {
    return null;
  }
}

function writeActiveDiagramId(workspaceId: string, id: string | null): void {
  if (typeof window === "undefined") return;
  try {
    if (id) window.localStorage.setItem(activeDiagramKey(workspaceId), id);
    else window.localStorage.removeItem(activeDiagramKey(workspaceId));
  } catch {
    // best-effort
  }
}

// Persisteer de actieve diagram per branch zodat een hard refresh je op
// hetzelfde diagram laat. `currentWorkspaceId` + `openTabsBranchKey` worden
// door hydrate gezet; vóór de eerste hydrate is er niets om te persisteren.
useWorkspaceData.subscribe((s, prev) => {
  if (s.activeDiagramId !== prev.activeDiagramId && currentWorkspaceId) {
    writeActiveDiagramId(currentWorkspaceId, s.activeDiagramId);
  }
});

function explainError(err: unknown): string {
  if (err instanceof ApiError) {
    return err.body?.detail ?? err.body?.title ?? `HTTP ${err.status}`;
  }
  if (err instanceof Error) {
    return err.message;
  }
  return String(err);
}

/**
 * Voorkomens die de server niet schreef omdat iemand anders ze intussen
 * wijzigde.
 *
 * Wat wél landde blijft staan — één botsing hoort niet het hele gebaar terug
 * te draaien. Wat we doen is: het geweigerde vakje terugzetten op wat er
 * werkelijk staat, het uit de undo-stap halen zodat Cmd+Z 'm niet opnieuw
 * probeert (en dezelfde melding oplevert), en de gebruiker vertellen wát er
 * niet lukte en dat 'ie het opnieuw kan proberen.
 *
 * Zie `docs/architecture/gelijktijdigheid.md`.
 */
/**
 * Hoeveel keer een geweigerd voorkomen al opnieuw is aangeboden. Voorkomt dat
 * twee sessies elkaar eindeloos blijven overschrijven.
 */
const geweigerdeHerkansingen = new Map<string, number>();

/**
 * Een geweigerd voorkomen afhandelen — **zonder het werk van de modelleur weg
 * te gooien.**
 *
 * De oude afhandeling overschreef de lokale entry met die van de server en
 * haalde de stap van de ongedaan-stapel. Daarmee was de versleping, de maat of
 * de nesting die je net had aangebracht definitief weg: er stond niets meer
 * om terug te zetten. Dat is de verkeerde afweging. Een weigering zegt iets
 * over de **boekhouding** (jouw teller loopt achter), niet over de inhoud.
 *
 * Vandaar twee gevallen:
 *
 * - **Alleen de teller liep achter.** De server heeft inhoudelijk nog steeds
 *   wat wij als uitgangspunt hadden — niemand heeft er echt aan gezeten. Dan
 *   bieden we het gewoon opnieuw aan, met de verse teller. Eén keer; blijft
 *   het dan nog hangen, dan is er wél iets anders aan de hand.
 * - **Een collega heeft het voorkomen echt gewijzigd.** Dan schrijven we niet
 *   overheen, maar we gooien jouw versie ook niet weg: die blijft op het
 *   scherm staan en je krijgt te horen wat er speelt, zodat jíj kiest.
 *
 * In geen van beide gevallen verdwijnt er werk.
 */
function handleRejectedViewStateKeys(
  diagramId: string,
  keys: string[],
  serverViewState: Record<string, unknown>,
  baseline: Record<string, ViewElementState>,
  opnieuwAanbieden: (sleutels: string[]) => void,
): void {
  const state = useWorkspaceData.getState();
  const diagram = state.store.getDiagram(diagramId);
  if (!diagram) return;

  const server = serverViewState as Record<string, ViewElementState | undefined>;
  const { herkansen, botsend: echtBotsend } = classificeerWeigering(
    keys,
    server,
    baseline,
    (sleutel) => geweigerdeHerkansingen.get(`${diagramId}:${sleutel}`) ?? 0,
    sameViewEntry,
  );
  for (const key of herkansen) {
    const teller = `${diagramId}:${key}`;
    geweigerdeHerkansingen.set(teller, (geweigerdeHerkansingen.get(teller) ?? 0) + 1);
  }
  // De verse teller overnemen; de rest van de entry blijft van de gebruiker.
  for (const key of keys) {
    const versie = server[key]?.version;
    if (versie !== undefined) {
      state.store.noteViewStateVersions(diagramId, new Map([[key, versie]]));
    }
  }

  if (herkansen.length > 0) opnieuwAanbieden(herkansen);

  if (echtBotsend.length === 0) return;

  // Namen in plaats van sleutels: een sleutel zegt de modelleur niets. Eerst
  // de naam in de taal van de UI, anders de andere.
  const [eerste, tweede] =
    i18n.language === "en" ? (["en", "nl"] as const) : (["nl", "en"] as const);
  const namen = echtBotsend.map((key) => {
    const ve = diagram.viewState[key];
    const ref = ve?.ref ?? key;
    const concept = state.store.getConcept(ref);
    if (concept)
      return (
        concept.name[eerste] || concept.name[tweede] || i18n.t("canvas.viewStateConflict.unnamed")
      );
    const relation = state.store.getRelation(ref);
    if (relation)
      return (
        relation.label[eerste] ||
        relation.label[tweede] ||
        i18n.t("canvas.viewStateConflict.relation")
      );
    return i18n.t("canvas.viewStateConflict.object");
  });
  useWorkspaceData.setState({
    lastError: i18n.t("canvas.viewStateConflict.notSaved", {
      count: namen.length,
      names: namen.join(", "),
    }),
  });
}

/**
 * Zijn twee viewState-entries inhoudelijk gelijk? De teller telt niet mee: die
 * hoort bij de stand op de server, niet bij wat de gebruiker deed.
 *
 * Vergelijking via JSON — de entries zijn klein en plat, en zo mist 'ie geen
 * veld zodra iemand er eentje toevoegt.
 */
function sameViewEntry(a: ViewElementState, b: ViewElementState): boolean {
  const zonderVersie = (entry: ViewElementState) =>
    JSON.stringify(
      Object.entries(entry as unknown as Record<string, unknown>)
        .filter(([veld]) => veld !== "version")
        .sort(([links], [rechts]) => (links < rechts ? -1 : links > rechts ? 1 : 0)),
    );
  return zonderVersie(a) === zonderVersie(b);
}
